import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import * as lfe from '../lib/longformEpisodes';
import { useAuth } from '../context/AuthContext';
import LongformSeriesPanel from '../components/LongformSeriesPanel';
import { formatLabel, formatBadgeClass, formatTitle } from '../utils/formatBadge';
import { useResizableColumns, useResizableTableColumns } from '../hooks/useResizableColumns';
import { useScriptClipboard } from '../hooks/useScriptClipboard';
import { exportSceneCSV } from '../lib/scriptExport';
import { diffToOps, applyOps } from '../utils/scriptOps';
import ClioChat from '../components/ClioChat';
import CharacterRegistryModal from '../components/CharacterRegistryModal';
import { useSpokenWordCount } from '../hooks/useSpokenWordCount';
import {
  SPOKEN_COL, formatRuntime, formatWordCount,
  WPM_STORAGE_KEY, WPM_DEFAULT, WPM_MIN, WPM_MAX, clampWpm,
} from '../utils/wordCount';

// ── Constants ────────────────────────────────────────────────────

// 'Filming' is NOT a qual_status — it is the in-production shelf (the script's
// video card has reached Filming or later, so the script has left the writing
// queue and the default list hides it). It sits in the chip row in pipeline
// order, between Approved and Posted, and is the only way back to those
// scripts from this page. The server owns the shelf (?qualStatus=Filming).
const QUAL_STATUSES = ['I/P', 'Needs Review', 'Needs Revision', 'Approved', 'Filming', 'Posted', 'Backburner'];

const QUAL_CHIP_CLASS = {
  'I/P':            'chip-ip',
  'Needs Review':   'chip-nr',
  'Needs Revision': 'chip-revision',
  'Approved':       'chip-approved',
  'Filming':        'chip-filming',
  'Posted':         'chip-posted',
  'Backburner':     'chip-backburner',
};

const TRANSITIONS = {
  // Roles mirror the server exactly (TRANSITIONS in routes/scripts.js): Needs
  // Review is a WRITER-side move — Writer / Power User / Admin. A Qual Reviewer
  // used to be offered the button here and got a 403 from the server every time.
  // From 'Approved' this is the pull-back (Owner 2026-09-08): allowed only while
  // the script is still just a script, which the server re-checks and 409s.
  'Needs Review':   { from: ['I/P', 'Needs Revision', 'Approved'],                roles: ['Writer', 'Admin', 'Power User'] },
  'Approved':       { from: ['Needs Review'],                                      roles: ['Qual Reviewer', 'Admin', 'Power User'], noSelf: true },
  'Needs Revision': { from: ['Needs Review', 'Approved'],                          roles: ['Qual Reviewer', 'Admin', 'Power User'], noSelf: true },
  'I/P':            { from: ['Needs Revision', 'Backburner'],                      roles: ['Writer', 'Qual Reviewer', 'Admin', 'Power User'] },
  'Backburner':     { from: ['I/P', 'Needs Review', 'Needs Revision', 'Approved'], roles: ['Writer', 'Qual Reviewer', 'Admin', 'Power User'] },
};

const FORMAT_COLS = {
  // Longform (D-089): Skit-shaped rows — the series' dialogue/breakdown, so the
  // character / casting / Scene Scrub machinery works by construction.
  Longform: [
    { key: 'sceneNumber', label: 'Scene #',           cls: 'notes',     rows: 1, width: '8%'  },
    { key: 'character',   label: 'Character',         cls: 'character', rows: 1, width: '16%' },
    { key: 'line',        label: 'Line',              cls: 'line',      rows: 2, width: '44%' },
    { key: 'scene',       label: 'Scene Description', cls: 'notes',     rows: 2, width: '16%' },
  ],
  Skit: [
    { key: 'character', label: 'Character',         cls: 'character', rows: 1, width: '16%' },
    { key: 'line',      label: 'Line',              cls: 'line',      rows: 2, width: '32%' },
    { key: 'notes',     label: 'Directional Notes', cls: 'notes',     rows: 2, width: '16%' },
    { key: 'scene',     label: 'Scene Description', cls: 'notes',     rows: 2, width: '16%' },
    { key: 'props',     label: 'Props',             cls: 'notes',     rows: 1, width: '16%' },
  ],
  TH: [
    { key: 'narration', label: 'Narration', cls: 'line',  rows: 3, width: '36%' },
    { key: 'aRoll',     label: 'A Roll',    cls: 'notes', rows: 2, width: '12%' },
    { key: 'bRoll',     label: 'B Roll',    cls: 'notes', rows: 2, width: '12%' },
    { key: 'vfx',       label: 'VFX',       cls: 'notes', rows: 2, width: '12%' },
    { key: 'sfx',       label: 'SFX',       cls: 'notes', rows: 2, width: '12%' },
    { key: 'props',     label: 'Props',     cls: 'notes', rows: 1, width: '12%' },
  ],
  AI: [
    { key: 'narration', label: 'Narration', cls: 'line',  rows: 3 },
    { key: 'visuals',   label: 'Visuals',   cls: 'notes', rows: 3 },
  ],
  Mockumentary: [
    { key: 'character', label: 'Character',          cls: 'character', rows: 1, width: '13%' },
    { key: 'line',      label: 'Line',               cls: 'line',      rows: 3, width: '22%' },
    { key: 'notes',     label: 'Directional Notes',  cls: 'notes',     rows: 2, width: '13%' },
    { key: 'aroll',     label: 'A-Roll',             cls: 'notes',     rows: 2, width: '10%' },
    { key: 'broll',     label: 'B-Roll',             cls: 'notes',     rows: 2, width: '10%' },
    { key: 'vfx',       label: 'FX',                 cls: 'notes',     rows: 2, width: '11%' },
    { key: 'props',     label: 'Props',              cls: 'notes',     rows: 1, width: '11%' },
    { key: 'location',  label: 'Location',           cls: 'notes',     rows: 1, width: '10%' },
  ],
  // Phase 1 of the Cooking workflow. Practical on-camera reference document,
  // not a creative script. The Recipe column accepts plain text OR a URL
  // (auto-rendered as a clickable link via URL_TEXT_COLS below).
  Recipe: [
    { key: 'recipe',   label: 'Recipe',   cls: 'line',  rows: 2, width: '55%' },
    { key: 'gestures', label: 'Gestures', cls: 'notes', rows: 2, width: '45%' },
  ],
};

// ── Production Notes column ─────────────────────────────────────────────────
// A column the shoot crew fills in once a script is approved (what actually
// happened vs. what was scripted). It is NOT part of FORMAT_COLS on purpose:
// FORMAT_COLS feeds positional importers/paste (parseScenesPositional,
// mapClipRowsToDestination, CSV/TSV/HTML import) where injecting a column would
// shift every mapping. Instead it's spliced into the *display/export* column
// list only, via displayCols(). Inserted immediately after each format's main
// narration/dialogue column. AI is intentionally absent (no physical shoot).
const PROD_NOTES_COL = { key: 'prodNotes', label: 'Production Notes', cls: 'notes', rows: 2 };
const PROD_NOTES_INSERT_IDX = { TH: 1, Skit: 2, Mockumentary: 2, Recipe: 1, Longform: 3 };
function displayCols(format, includeProdNotes) {
  // DEF-104: an unknown / legacy format ('None', null) must never render ZERO
  // columns — that hid a fully-written 60-row script behind a blank grid. Fall
  // back to the Skit layout so the rows stay visible and editable.
  const base = FORMAT_COLS[format] || FORMAT_COLS.Skit;
  const at = PROD_NOTES_INSERT_IDX[format];
  if (!includeProdNotes || at == null) return base;
  const out = base.slice();
  out.splice(at, 0, PROD_NOTES_COL);
  return out;
}

const STATUS_FLOW_STEPS = ['I/P', 'Needs Review', 'Approved', 'Posted'];

const PILL_CLASS = {
  'I/P':            'pill-ip',
  'Needs Review':   'pill-nr',
  'Needs Revision': 'pill-revision',
  'Approved':       'pill-approved',
  'Posted':         'pill-posted',
  'Backburner':     'pill-backburner',
  'Filming':        'pill-filming',
};

// FIX-1: a row whose production card is in production (the API's `stage`, from
// lib/scriptStage) shows Filming, the shelf it sits on, instead of its qual
// status. An Incomplete card is stage Filming. Without `stage` (older API) the
// row shows qualStatus as before.
const IN_PRODUCTION_STAGES = ['Filming', 'Filmed', 'Editing', 'Qual', 'Approved', 'Posted'];
const listStatus = s =>
  (s.qualStatus !== 'Posted' && IN_PRODUCTION_STAGES.includes(s.stage)) ? 'Filming' : s.qualStatus;

const ROW_COLORS = [
  { value: 'rgba(255,213,66,0.3)',   label: 'Yellow' },
  { value: 'rgba(80,200,120,0.3)',   label: 'Green' },
  { value: 'rgba(99,149,255,0.3)',   label: 'Blue' },
  { value: 'rgba(255,100,100,0.3)',  label: 'Red' },
  { value: 'rgba(200,100,255,0.3)',  label: 'Purple' },
  { value: 'rgba(255,160,50,0.3)',   label: 'Orange' },
  { value: 'rgba(50,220,220,0.3)',   label: 'Cyan' },
  { value: 'rgba(180,180,180,0.25)', label: 'Gray' },
  { value: null,                     label: 'Clear' },
];

const SCRIPT_HEADERS = ['Script Name', 'Project', 'Writer', 'Status', 'Date', 'Approved'];

// The read-only Characters column. Special-cased everywhere a header is treated
// as a sort key or a plain label: it is the only column whose header holds
// controls (a search box + the whole-column expand toggle) and the only one that
// cannot be sorted, so it is matched by name rather than by position.
const CHARACTERS_COL = 'Characters';

// Column widths. Characters is last in both variants — it goes immediately right
// of Qual Notes, which is itself only present for roles that can see it.
// New storage keys rather than extending the old ones: useResizableColumns
// discards a stored array whose length no longer matches the defaults, so reusing
// `colWidths_scripts*` would silently wipe every user's existing column widths.
const SCRIPT_COL_DEFAULTS    = [320, 160, 160, 130, 90, 90, 230];
const SCRIPT_COL_DEFAULTS_QN = [320, 160, 160, 130, 90, 90, 200, 230];

// ── Characters column: the display + match rules, in one place ────────────────
// Both the tags and the header search's suggestions read through these, so a
// value the column refuses to show can never be offered as something to search
// for (and vice versa).

// Does this value contain at least one letter? The body's character column is a
// free-text cell, and real data holds plenty that is not a name: `...`, `???`,
// `&`, `,`, `3` (DEF-081). Nothing without a letter in it can be a character, so
// the column suppresses it. Anything WITH a letter shows exactly as typed —
// `asdf` and `Camera Pan` included. Deciding which letter-bearing strings are
// "really" names is a writer-convention call, not this column's job.
// \p{L} rather than [A-Za-z]: an accented or non-Latin name is still a name.
const hasLetter = s => /\p{L}/u.test(s || '');

const displayCharacters = names => (Array.isArray(names) ? names.filter(hasLetter) : []);

// WORD-PREFIX match, case-insensitive — moved here verbatim from the retired
// toolbar Character dropdown so the header search behaves the way that control
// did. The query must start the NAME or start one of its WORDS: "brit" finds
// `Brittany` and `Chad & Brittany`, "rit" finds neither. Splitting on anything
// non-alphanumeric makes `/`, `&`, `#` and `-` boundaries, which matters because
// character names are routinely multi-word (`Hype Man #1`, `Ashley/Debbie`) and
// someone typing "debbie" plainly means the second one (DEF-074, D-017).
// Whole-string matches rank above word matches.
//
// The server's ?characterPrefix= mirrors this condition for condition
// (characterPrefixCondition in scripts.js). They have to agree: this function
// decides what the search OFFERS, and that one decides what it RETURNS.
function matchCharacterNames(query, options) {
  const q = (query || '').trim().toLowerCase();
  if (!q) return [];
  const starts = [], word = [];
  for (const n of options) {
    const l = n.toLowerCase();
    if (l.startsWith(q)) { starts.push(n); continue; }
    if (l.split(/[^a-z0-9]+/).some(w => w && w.startsWith(q))) word.push(n);
  }
  return [...starts, ...word];
}

// Is this cell value already in the client's registry? Case-insensitive under
// the shared trim rule (all-whitespace trim, matching the server's charExpr).
function registryHasName(registryNames, value) {
  const v = String(value || '').replace(/^\s+|\s+$/g, '').toLowerCase();
  if (!v) return true; // an empty cell is not "not in list"
  return registryNames.some(n => String(n).replace(/^\s+|\s+$/g, '').toLowerCase() === v);
}

// The repeat-previous-speaker source: the character TWO rows above — the other
// side of the exchange (Alt+T / Enter-on-empty, Owner decision 1; Ctrl+T is
// browser-reserved and deliberately unused).
function speakerTwoAbove(draftBodyRef, idx) {
  return String(draftBodyRef.current?.scenes?.[idx - 2]?.character || '').trim();
}

// Columns that support the explicit 🔗 link button
const LINK_COLS = {
  TH:           ['vfx', 'props'],
  Skit:         ['notes', 'scene', 'props'],
  Longform:     ['scene'],
  Mockumentary: ['notes', 'vfx', 'props', 'location'],
  Recipe:       ['recipe'],
};

// Columns where raw pasted URLs in cell text are auto-rendered as clickable links
const URL_TEXT_COLS = {
  TH:           ['vfx'],
  Skit:         ['scene', 'props'],
  Longform:     ['scene'],
  Mockumentary: ['notes', 'vfx', 'props', 'location'],
  Recipe:       ['recipe'],
};

function extractCellUrl(text) {
  const m = (text || '').match(/https?:\/\/[^\s]+|www\.[^\s]+/);
  if (!m) return null;
  const url = m[0].replace(/[.,;:!?)]+$/, '');
  return url.startsWith('www.') ? 'https://' + url : url;
}

// P0: stable per-row identity for per-cell merge. Every scene carries a unique `id`.
function uid() {
  try { return crypto.randomUUID(); }
  catch { return 'r' + Date.now().toString(36) + Math.random().toString(36).slice(2, 10); }
}

// Mints ids for scenes missing one and re-mints duplicates (e.g. a copied row) so ids
// stay unique within the body. The server re-normalizes on save and is authoritative;
// this keeps the in-memory draft consistent so copies don't collide before the next save.
function normalizeSceneIds(scriptBody) {
  if (!scriptBody || !Array.isArray(scriptBody.scenes)) return scriptBody;
  const seen = new Set();
  const scenes = scriptBody.scenes.map(s => {
    let id = s && typeof s.id === 'string' && s.id ? s.id : null;
    if (!id || seen.has(id)) id = uid();
    seen.add(id);
    return { ...s, id };
  });
  return { ...scriptBody, scenes };
}

function emptyRow(format) {
  const cols = FORMAT_COLS[format] || [];
  return { id: uid(), ...Object.fromEntries(cols.map(c => [c.key, ''])) };
}

function getAvailableTransitions(script, user) {
  if (!script || !user) return [];
  const isAdmin = user.roles?.includes('Admin') || user.roles?.includes('Power User');
  return Object.entries(TRANSITIONS)
    .filter(([status, rule]) => {
      if (!rule.from.includes(script.qualStatus)) return false;
      if (!rule.roles.some(r => user.roles.includes(r))) return false;
      if (rule.noSelf && !isAdmin && script.writer?.id === user.id) return false;
      // The pull-back out of Approved only exists while the script hasn't been
      // shot — the server refuses it with a 409 once a card has moved past
      // Scripted (productionStarted), so don't offer a button that can't work.
      if (status === 'Needs Review' && script.qualStatus === 'Approved' && script.productionStarted) return false;
      return true;
    })
    .map(([status]) => status);
}

function transitionBtnClass(status) {
  if (status === 'Needs Review')   return 'action-btn action-submit';
  if (status === 'Approved')       return 'action-btn action-approve';
  if (status === 'Needs Revision') return 'action-btn action-revision';
  return 'action-btn action-backburner';
}

function transitionBtnLabel(status, script) {
  // Same transition, two directions: forward from I/P is a submission, backward
  // from Approved is a pull-back, and the label has to say which.
  if (status === 'Needs Review') {
    return script?.qualStatus === 'Approved' ? '← Back to Needs Review' : 'Submit for Review →';
  }
  if (status === 'Approved')     return 'Approve ✓';
  if (status === 'I/P')          return '← Back to I/P';
  return status;
}

// Spec 5.2: extend a column's source values to fill `count` more rows.
// Detects: single-value repeat, pure numeric arithmetic progression,
// text+trailing-number progression ("Scene 1" → "Scene 2"), or falls back
// to cycling through the source values (which also handles A,B,A,B style).
function detectAndExtendColumn(sourceValues, count) {
  if (count <= 0) return [];
  if (sourceValues.length === 0) return Array(count).fill('');
  if (sourceValues.length === 1) return Array(count).fill(sourceValues[0]);

  // Pure numeric arithmetic progression.
  const nums = sourceValues.map(v => Number(v));
  if (nums.every(n => Number.isFinite(n)) && sourceValues.every(v => String(v).trim() !== '')) {
    const step = nums[1] - nums[0];
    let linear = true;
    for (let i = 2; i < nums.length; i++) {
      if (nums[i] - nums[i - 1] !== step) { linear = false; break; }
    }
    if (linear) {
      const out = [];
      let last = nums[nums.length - 1];
      for (let i = 0; i < count; i++) { last += step; out.push(String(last)); }
      return out;
    }
  }

  // Text-with-trailing-number ("Scene 1" → "Scene 2"). Common prefix required.
  const re = /^(.*?)(-?\d+)$/;
  const parts = sourceValues.map(v => (typeof v === 'string' ? v.match(re) : null));
  if (parts.every(p => p !== null)) {
    const prefixes = parts.map(p => p[1]);
    const numbers  = parts.map(p => Number(p[2]));
    if (prefixes.every(p => p === prefixes[0])) {
      const step = numbers[1] - numbers[0];
      let linear = true;
      for (let i = 2; i < numbers.length; i++) {
        if (numbers[i] - numbers[i - 1] !== step) { linear = false; break; }
      }
      if (linear) {
        const out = [];
        let last = numbers[numbers.length - 1];
        for (let i = 0; i < count; i++) { last += step; out.push(prefixes[0] + last); }
        return out;
      }
    }
  }

  // Fallback: cycle through source values (handles A,B,A,B or any non-linear pattern).
  const out = [];
  for (let i = 0; i < count; i++) out.push(sourceValues[i % sourceValues.length]);
  return out;
}

// Spec 3.1: row-height sync. Measures the natural content height of every
// renderable cell in the row (textareas via scrollHeight after reset, idle
// divs via offsetHeight after min-height reset) and applies the max to all,
// so a wrap in one cell expands all siblings to the same visual height.
// Works through the Phase B1 div↔textarea swap by treating both cell types.
function syncRowHeights(tr) {
  if (!tr) return;
  const cells = tr.querySelectorAll('.scene-input, .scene-cell-idle');
  if (cells.length === 0) return;
  // Reset first so cells that shrunk give back their natural height.
  for (let i = 0; i < cells.length; i++) {
    const el = cells[i];
    if (el.tagName === 'TEXTAREA') el.style.height = '0px';
    else                           el.style.minHeight = '0px';
  }
  let max = 0;
  for (let i = 0; i < cells.length; i++) {
    const el = cells[i];
    const h = el.tagName === 'TEXTAREA' ? el.scrollHeight : el.offsetHeight;
    if (h > max) max = h;
  }
  const px = max + 'px';
  for (let i = 0; i < cells.length; i++) {
    const el = cells[i];
    if (el.tagName === 'TEXTAREA') el.style.height = px;
    else                           el.style.minHeight = px;
  }
}

function autoResize(el) {
  // Row-aware: a wrap in this cell expands all cells in the row.
  const tr = el.closest('tr');
  if (tr) { syncRowHeights(tr); return; }
  el.style.height = '0px';
  el.style.height = el.scrollHeight + 'px';
}

// Bulk autoResize that chunks across animation frames so long scripts (200+ rows × 5 cols
// = 1000+ textareas) don't block the main thread for hundreds of ms. Used by initial load,
// column-width drag, font-size change, and window resize. Per-keystroke updates go through
// the smart diffing useLayoutEffect, not this helper. Iterates by row so each row's
// cells share a single max-height pass.
function autoResizeAll(chunkSize = 40) {
  const rows = document.querySelectorAll('.scene-table tr');
  if (rows.length === 0) return;
  if (rows.length <= chunkSize) {
    rows.forEach(syncRowHeights);
    return;
  }
  let i = 0;
  const step = () => {
    const end = Math.min(i + chunkSize, rows.length);
    for (; i < end; i++) syncRowHeights(rows[i]);
    if (i < rows.length) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}

// Render **bold**, *italic*, __underline__ markers as HTML (for print/Word export)
// Call AFTER HTML-escaping so markers aren't affected; process bold before italic to avoid partial matches
function renderMarkdown(s) {
  return s
    .replace(/\*\*(.+?)\*\*/gs, '<strong>$1</strong>')
    .replace(/__(.+?)__/gs, '<u>$1</u>')
    .replace(/\*([^*]+?)\*/gs, '<em>$1</em>');
}

// A right-click inside an editable text field belongs to the browser (its menu
// carries the spellcheck suggestions, which no web API can read) — a right-click
// on anything else in the table belongs to the app. Every contextmenu handler in
// this file runs this test first and bails without preventDefault() on a match.
// Walks up from the target in case the click lands on a child node.
function isEditableTarget(el) {
  if (!(el instanceof Element)) return false;
  const t = el.closest('textarea, input, [contenteditable]');
  if (!t) return false;
  if (t.tagName === 'TEXTAREA') return true;
  if (t.tagName === 'INPUT') {
    const type = (t.getAttribute('type') || 'text').toLowerCase();
    return ['text', 'search', 'url', 'tel', 'email', 'password', 'number'].includes(type);
  }
  return t.isContentEditable === true;
}

// ── Drag-select edge auto-scroll constants ────────────────────────────────────
// Hot zone depth measured from the scroll container's edges, and the per-frame
// scroll cap. Velocity is proportional to how deep the pointer sits in the zone
// (a crawl at the zone's inner edge, MAX at/past the container edge).
const AUTOSCROLL_EDGE_PX = 60;
const AUTOSCROLL_MAX_STEP_PX = 20;

// Clamp a viewport point to just inside the scroller's visible row area so
// elementFromPoint resolves a row even when the pointer sits outside the
// container (that is where the edge zones are). topEdge already accounts for
// the sticky column header overlaying the top of the scroll area.
function clampToScrollerRect(x, y, rect, topEdge) {
  return {
    x: Math.min(Math.max(x, rect.left + 2), rect.right - 2),
    y: Math.min(Math.max(y, topEdge + 2), rect.bottom - 2),
  };
}

// Strict row/column splitter. Every \n starts a new row; every delim ends a cell.
// Quotes are NOT honored on import — a stray unbalanced `"` in upstream data used to
// pull every subsequent row into one giant cell. Trade-off: spreadsheet cells with
// embedded newlines (Alt+Enter in Sheets) will split into multiple rows on paste.
function splitDelimitedRows(text, delim) {
  return text.split('\n').map(line => line.split(delim));
}

// RFC-4180 CSV parser — used ONLY by the file-based import (csvToScenes), whose
// input is our own exportSceneCSV output: every cell wrapped in "...", inner
// quotes doubled ("" -> "), and cells that legitimately contain commas or
// newlines (talking-head narration routinely does both). It honors quoting:
// commas/newlines inside a quoted field are preserved, "" becomes a literal ",
// and the wrapping quotes are stripped. An unterminated final quote is tolerated
// (EOF closes the field) so a single stray " can't swallow the rest of the file
// — the exact failure the naive splitDelimitedRows was guarding against, which
// is why paste (parseScenesPositional) is left on the naive splitter untouched.
function parseCSVRows(text) {
  const rows = [];
  let row = [], field = '', inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; }  // escaped quote
        else inQuotes = false;                            // close quote
      } else field += ch;
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      row.push(field); field = '';
    } else if (ch === '\n') {
      row.push(field); rows.push(row); row = []; field = '';
    } else field += ch;
  }
  row.push(field); rows.push(row);  // flush trailing field/row (no final newline)
  return rows;
}

// Positional paste \u2014 clipboard cells map column-by-column to the format's data columns,
// starting at the top-left. No header detection. Extra clipboard columns past the format's
// column count are dropped silently; missing columns stay empty. Each clipboard row becomes
// one script row, in order \u2014 including rows where every cell happens to be blank, since
// they may have data in clipboard columns the format doesn't slot. The only thing we strip
// is purely-blank rows at the trailing edge (corner-select artifacts from spreadsheets).
function parseScenesPositional(text, format, delim) {
  const cols = FORMAT_COLS[format] || [];
  if (!cols.length) return [];
  const normalized = text.replace(/^\ufeff/, '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const rawRows = splitDelimitedRows(normalized, delim);
  // Trim trailing rows that are entirely empty across ALL clipboard columns.
  let end = rawRows.length;
  while (end > 0 && rawRows[end - 1].every(c => !c || !c.trim())) end--;
  return rawRows.slice(0, end).map(vals => {
    const row = emptyRow(format);
    cols.forEach((c, i) => {
      if (vals[i] !== undefined) {
        // Don't trim internal newlines \u2014 only outer spaces/tabs.
        row[c.key] = String(vals[i]).replace(/^[ \t]+|[ \t]+$/g, '');
      }
    });
    return row;
  });
}

// Header-matched parse used by the file-based CSV import (which has a Download template flow).
// Quote-aware (parseCSVRows) so it round-trips our own quoted CSV export cleanly \u2014
// commas/newlines inside cells stay put and wrapping quotes are stripped.
function csvToScenes(text, format) {
  const cols = FORMAT_COLS[format] || [];
  if (!cols.length) return [];
  const labelToKey = {};
  cols.forEach(c => {
    labelToKey[c.label.toLowerCase()] = c.key;
    labelToKey[c.key.toLowerCase()]   = c.key;
  });
  const normalized = text.replace(/^\ufeff/, '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const rows = parseCSVRows(normalized);
  if (rows.length < 2) return [];
  const headerKeys = rows[0].map(h => labelToKey[h.trim().replace(/^"|"$/g, '').toLowerCase()] || null);
  return rows.slice(1).map(vals => {
    const row = emptyRow(format);
    headerKeys.forEach((key, i) => {
      if (key && vals[i] !== undefined) {
        row[key] = String(vals[i]).replace(/^[ \t]+|[ \t]+$/g, '');
      }
    });
    return row;
  }).filter(row => cols.some(c => (row[c.key] || '').trim().length > 0));
}

// \u2500\u2500 Clipboard TSV export \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500
// Strip **bold**, *italic*, __underline__ markers \u2014 output plain text only.
function stripScriptMarkdown(s) {
  return (s || '')
    .replace(/\*\*(.+?)\*\*/gs, '$1')
    .replace(/__(.+?)__/gs, '$1')
    .replace(/\*([^*]+?)\*/gs, '$1');
}

// TSV cell escape \u2014 wrap in double quotes if cell contains tab, newline, or quote.
function tsvEscapeCell(val) {
  const s = stripScriptMarkdown(val || '');
  if (/[\t\n\r"]/.test(s)) return '"' + s.replace(/"/g, '""') + '"';
  return s;
}

// Build TSV string for the current script (data rows only \u2014 no header row, per spec).
function buildScriptTSV(scenes, format) {
  const cols = FORMAT_COLS[format] || [];
  if (!cols.length || !Array.isArray(scenes)) return '';
  return scenes
    .map(row => cols.map(c => tsvEscapeCell(row[c.key])).join('\t'))
    .join('\n');
}

// Build a TSV string for an arbitrary subset of rows/columns (used by the app
// clipboard so a partial selection round-trips to Google Sheets / Excel too).
function buildSubsetTSV(rows, colKeys) {
  if (!Array.isArray(rows) || !Array.isArray(colKeys) || !colKeys.length) return '';
  return rows
    .map(row => colKeys.map(k => tsvEscapeCell(row[k])).join('\t'))
    .join('\n');
}

// ── App-clipboard column mapping ────────────────────────────────────────────
// Maps copied rows from a source format/selection onto a destination format.
// All three cases collapse to one index-aligned operation:
//   • same-format full rows  → identity (1:1 by column, full visual fidelity)
//   • cross-format full rows → positional (source col index N → dest col index N)
//   • partial-column paste   → positional, restricted to the selected columns
// Positional cross-format matches the existing Google-Sheets paste behavior
// (parseScenesPositional) rather than name-matching, since real formats barely
// overlap by column name. Returns { scenes, crossFormat }.
function mapClipRowsToDestination(clipPayload, destFormat, { columnMode } = {}) {
  const destCols   = FORMAT_COLS[destFormat] || [];
  const srcCols    = FORMAT_COLS[clipPayload.sourceFormat] || [];
  const sameFormat = clipPayload.sourceFormat === destFormat;
  const mode = columnMode || (clipPayload.selectionType === 'partial_cells' ? 'selected' : 'full');
  const scenes = (clipPayload.rows || []).map(clipRow => {
    const dest = emptyRow(destFormat);
    if (mode === 'selected') {
      // Each selected source column lands in the destination column at the same
      // index. Same-format → same index → effectively 1:1 by name.
      for (const srcKey of (clipPayload.selectedColumns || [])) {
        const srcIdx = srcCols.findIndex(c => c.key === srcKey);
        if (srcIdx < 0) continue;
        const destCol = destCols[srcIdx];
        if (destCol && clipRow[srcKey] != null) dest[destCol.key] = clipRow[srcKey];
      }
    } else if (sameFormat) {
      for (const c of destCols) if (clipRow[c.key] != null) dest[c.key] = clipRow[c.key];
      if (clipRow._rowColor)   dest._rowColor   = clipRow._rowColor;
      if (clipRow._cellColors) dest._cellColors = { ...clipRow._cellColors };
      if (clipRow._links)      dest._links      = JSON.parse(JSON.stringify(clipRow._links));
    } else {
      // Cross-format full rows: positional, truncated to the shorter format.
      // Cell colors/links are keyed by source column keys absent in the dest, so
      // drop them; keep only the row color.
      srcCols.forEach((sc, i) => {
        const dc = destCols[i];
        if (dc && clipRow[sc.key] != null) dest[dc.key] = clipRow[sc.key];
      });
      if (clipRow._rowColor) dest._rowColor = clipRow._rowColor;
    }
    return dest;
  });
  return { scenes, crossFormat: !sameFormat };
}

// \u2500\u2500 Clipboard HTML round-trip \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500
// Writing HTML on copy (alongside TSV) lets cell colors, row colors, and hyperlinks
// survive Heliconflow\u2192Heliconflow paste AND show up in Google Sheets paste. data-hf-*
// attributes give clean Heliconflow round-trip; inline style="background-color:..." and
// <a href="..."> tags handle Sheets compatibility.

function escapeHtml(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function escapeAttr(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;');
}

function buildScriptHTML(scenes, format) {
  const cols = FORMAT_COLS[format] || [];
  if (!cols.length || !Array.isArray(scenes)) return '';
  const trs = scenes.map(row => {
    const rowColor = row._rowColor;
    const rowAttrs = rowColor
      ? ` style="background-color:${escapeAttr(rowColor)}" data-hf-row-color="${escapeAttr(rowColor)}"`
      : '';
    const tds = cols.map(c => {
      const text = stripScriptMarkdown(row[c.key] || '');
      const cellColor = row._cellColors?.[c.key];
      const link = row._links?.[c.key];
      const styleAttr = cellColor ? ` style="background-color:${escapeAttr(cellColor)}"` : '';
      const attrs = [];
      if (cellColor) attrs.push(`data-hf-cell-color="${escapeAttr(cellColor)}"`);
      if (link?.url) {
        attrs.push(`data-hf-link-url="${escapeAttr(link.url)}"`);
        if (link.text) attrs.push(`data-hf-link-text="${escapeAttr(link.text)}"`);
      }
      const attrStr = attrs.length ? ' ' + attrs.join(' ') : '';
      const textHtml = escapeHtml(text).replace(/\n/g, '<br>');
      const linkHtml = link?.url
        ? `<a href="${escapeAttr(link.url)}">${escapeHtml(link.text || link.url)}</a>`
        : '';
      const inner = text && linkHtml ? `${textHtml} ${linkHtml}` : (textHtml || linkHtml);
      return `<td${styleAttr}${attrStr}>${inner}</td>`;
    }).join('');
    return `<tr${rowAttrs}>${tds}</tr>`;
  }).join('');
  return `<table>${trs}</table>`;
}

// Walk a <td>'s child nodes and assemble text content. <br>/<p>/<div> become \n.
// <a> children are skipped \u2014 the caller extracts the link separately so its text
// doesn't double up inside the cell value.
function htmlCellToText(el) {
  let out = '';
  for (const node of el.childNodes) {
    if (node.nodeType === 3) {
      out += node.nodeValue;
    } else if (node.nodeType === 1) {
      const tag = node.tagName;
      if (tag === 'BR') {
        out += '\n';
      } else if (tag === 'P' || tag === 'DIV') {
        if (out && !out.endsWith('\n')) out += '\n';
        out += htmlCellToText(node);
      } else if (tag === 'A') {
        // skipped \u2014 extracted separately as a link
      } else {
        out += htmlCellToText(node);
      }
    }
  }
  return out;
}

function extractBgColor(el) {
  const style = el.getAttribute && el.getAttribute('style');
  if (!style) return null;
  const m = style.match(/background(?:-color)?\s*:\s*([^;]+)/i);
  if (!m) return null;
  const val = m[1].trim();
  if (!val || /^(transparent|inherit|initial|unset|none)$/i.test(val)) return null;
  return val;
}

function isRowEffectivelyEmpty(row, cols) {
  if (cols.some(c => (row[c.key] || '').trim())) return false;
  if (row._rowColor) return false;
  if (row._cellColors && Object.keys(row._cellColors).length) return false;
  if (row._links && Object.keys(row._links).length) return false;
  return true;
}

function parseScenesFromHTML(html, format) {
  const cols = FORMAT_COLS[format] || [];
  if (!cols.length || !html) return [];
  let doc;
  try {
    doc = new DOMParser().parseFromString(html, 'text/html');
  } catch {
    return [];
  }
  const trs = doc.querySelectorAll('tr');
  if (trs.length === 0) return [];
  const scenes = [];
  trs.forEach(tr => {
    const row = emptyRow(format);
    const rowColor = tr.getAttribute('data-hf-row-color') || extractBgColor(tr);
    if (rowColor) row._rowColor = rowColor;
    const tds = tr.querySelectorAll('td, th');
    cols.forEach((c, i) => {
      const td = tds[i];
      if (!td) return;
      // Cell color
      const cellColor = td.getAttribute('data-hf-cell-color') || extractBgColor(td);
      if (cellColor) {
        if (!row._cellColors) row._cellColors = {};
        row._cellColors[c.key] = cellColor;
      }
      // Link \u2014 prefer our own attrs, fall back to the first <a> in the cell
      const linkUrlAttr = td.getAttribute('data-hf-link-url');
      const linkTextAttr = td.getAttribute('data-hf-link-text');
      if (linkUrlAttr) {
        if (!row._links) row._links = {};
        row._links[c.key] = { url: linkUrlAttr, text: linkTextAttr || '' };
      } else {
        const a = td.querySelector('a[href]');
        if (a) {
          if (!row._links) row._links = {};
          row._links[c.key] = { url: a.getAttribute('href'), text: a.textContent };
          a.remove(); // strip so its text isn't double-counted in the cell value
        }
      }
      // Cell text \u2014 htmlCellToText skips <a> children, which is what we want
      const text = htmlCellToText(td).replace(/^[ \t]+|[ \t]+$/g, '');
      row[c.key] = text;
    });
    scenes.push(row);
  });
  // Trim trailing rows that are entirely empty across text AND metadata.
  while (scenes.length && isRowEffectivelyEmpty(scenes[scenes.length - 1], cols)) {
    scenes.pop();
  }
  return scenes;
}

function downloadTemplate(format) {
  const cols = FORMAT_COLS[format];
  if (!cols) return;
  const header = cols.map(c => `"${c.label}"`).join(',');
  const blob = new Blob([header + '\n'], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `${format.toLowerCase()}-script-template.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── SceneRow (memoized) ──────────────────────────────────────────
// Per-row JSX extracted out so React.memo skips reconciliation for the rows whose
// data didn't change on a given keystroke — at 500 rows that drops the per-keystroke
// render cost from O(N) to O(1). Handlers are stable wrappers from the parent
// (`handlers` prop) that delegate through refs to the current functions; per-row
// state (selection, hover, autocomplete-on-this-row, etc.) is derived in the parent
// and passed as primitives or scoped objects, so memo's shallow compare only treats
// rows whose own state changed as different.
const SceneRow = React.memo(function SceneRow({
  idx, row, cols, format, totalRows,
  isReadOnly, editorFontSize, showQualNotesCol, canEditQualNotes,
  registryNames,        // string[] | null — live client-registry names (Skit/Mock only); null = no picker
  canEditRegistry,      // bool — shows the one-click "Add to list" on a not-in-list name
  isSelected, isCut, isHovered,
  dropMarker,           // 'above' | 'end' | null
  autocomplete,         // object | null (only when on this row)
  focusedColKey,        // c.key | null — drives the spec 1.2 active-cell border
  editingColKey,        // c.key | null — when set, that cell renders <textarea>; others render idle <div>
  editingMode,          // 'cursor-end' | 'select-all' | 'cursor-start' — read once on textarea mount
  selectedColRange,     // '' or 'c1-c2' (inclusive colIdx range) — spec 1.5 selection membership for THIS row
  multiSelectedColIdxs, // '' or comma-joined colIdxs (e.g. '0,2,5') — non-contiguous cells in THIS row
  moveTargetColRange,   // '' or 'c1-c2' — spec 4.1/4.2 move-drag target columns on THIS row
  autofillHandleColIdx, // colIdx | null — spec 5.2 anchor cell for the autofill handle
  autofillPreviewRange, // '' or 'c1-c2' — spec 5.2 fill-preview cells on THIS row
  colorPickerOpen,      // boolean
  colorPickerCellKey,   // c.key | null
  linkPopoverColKey,    // c.key | null
  linkDraft,            // { text, url } | null (only when popover on this row)
  setHoveredRow, setColorPickerRow, setColorPickerCell,
  setLinkPopover, setLinkDraft, setAutocomplete,
  setSelectedRows, setSelectionAnchor, setSelectAllActive, setFocusedCell,
  handlers,
  draftBodyRef, selectedRowsSizeRef, selectAllActiveRef, lastFocusBodyRef,
}) {
  // D-129 (2026-09-18): EPISODE BREAK / EPISODE n marker rows are not emitted as grid rows (the episode band
  // is the break), and collapsed rows are not emitted either — so "the next row" may not be in the DOM.
  // focusVisible walks past missing rows in the given direction; false when nothing visible is left.
  const focusVisible = (fromIdx, colKey, dir = 1) => {
    const total = draftBodyRef.current?.scenes?.length || 0;
    for (let i = fromIdx; i >= 0 && i < total; i += dir) {
      const el = document.querySelector(`[data-row="${i}"][data-col="${colKey}"]`);
      if (el) { el.focus(); return true; }
    }
    return false;
  };
  const {
    updateScene, applyMarkdown, addRow, moveRow,
    handleGutterMouseDown, updateRowColor, updateCellColor,
    removeLink, saveLink, pushUndo, saveBody,
  } = handlers;

  // Phase B2: derive which column indices in this row are inside the selection
  // range. '' means not in range; 'c1-c2' means inclusive [c1, c2]. Also folds
  // in the non-contiguous multi-selection's colIdxs for this row.
  const selectedColIdxSet = React.useMemo(() => {
    if (!selectedColRange && !multiSelectedColIdxs) return null;
    const s = new Set();
    if (selectedColRange) {
      const [c1Str, c2Str] = selectedColRange.split('-');
      const c1 = Number(c1Str), c2 = Number(c2Str);
      for (let i = c1; i <= c2; i++) s.add(i);
    }
    if (multiSelectedColIdxs) {
      for (const v of multiSelectedColIdxs.split(',')) s.add(Number(v));
    }
    return s;
  }, [selectedColRange, multiSelectedColIdxs]);
  // Phase C1: same shape for move-drag target columns on this row.
  const moveTargetColIdxSet = React.useMemo(() => {
    if (!moveTargetColRange) return null;
    const [c1Str, c2Str] = moveTargetColRange.split('-');
    const c1 = Number(c1Str), c2 = Number(c2Str);
    const s = new Set();
    for (let i = c1; i <= c2; i++) s.add(i);
    return s;
  }, [moveTargetColRange]);
  // Phase C2: autofill preview tint columns on this row.
  const autofillPreviewColIdxSet = React.useMemo(() => {
    if (!autofillPreviewRange) return null;
    const [c1Str, c2Str] = autofillPreviewRange.split('-');
    const c1 = Number(c1Str), c2 = Number(c2Str);
    const s = new Set();
    for (let i = c1; i <= c2; i++) s.add(i);
    return s;
  }, [autofillPreviewRange]);

  // Phase B1: when the editing cell changes (or its mode changes), grab focus
  // on the just-mounted textarea and apply caret/selection per mode. Runs in
  // a layout effect so focus lands before the browser paints, no flicker.
  React.useLayoutEffect(() => {
    if (!editingColKey) return;
    const el = document.querySelector(`tr[data-row-idx="${idx}"] textarea[data-col="${editingColKey}"]`);
    if (!el) return;
    el.focus();
    autoResize(el);
    const v = el.value || '';
    try {
      if (editingMode === 'select-all')        el.setSelectionRange(0, v.length);
      else if (editingMode === 'cursor-start') el.setSelectionRange(0, 0);
      else                                     el.setSelectionRange(v.length, v.length); // cursor-end
    } catch { /* element may not support selection */ }
  }, [editingColKey, editingMode, idx]);

  return (
    <tr
      data-row-idx={idx}
      className={(() => {
        const cls = ['scene-row'];
        if (row?.kind === 'direction') cls.push('row-direction'); // D-094: gold, never a character
        if (isSelected) cls.push('row-selected');
        if (isCut) cls.push('row-cut');
        if (dropMarker === 'above') cls.push('drop-above');
        else if (dropMarker === 'end') cls.push('drop-end');
        return cls.join(' ');
      })()}
      onMouseEnter={() => !isReadOnly && setHoveredRow(idx)}
      onMouseLeave={() => setHoveredRow(null)}
      style={row._rowColor ? { background: row._rowColor } : undefined}
    >
      <td
        className="scene-td scene-num"
        style={{ width: 68, textAlign: 'center', verticalAlign: 'middle', position: 'relative', padding: '6px 4px' }}
        onMouseDown={e => handleGutterMouseDown(idx, e)}
      >
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
          <span style={{ fontSize: 11, color: 'var(--tx-faint)', fontFamily: 'var(--mono)' }}>{idx + 1}</span>
          {!isReadOnly && isHovered && (
            <>
              <button
                title="Row color"
                onMouseDown={e => { e.preventDefault(); e.stopPropagation(); setColorPickerRow(colorPickerOpen ? null : idx); setColorPickerCell(null); }}
                onClick={e => e.stopPropagation()}
                style={{ width: '100%', height: 10, borderRadius: 3, border: row._rowColor ? '1px solid rgba(255,255,255,0.15)' : '1px dashed rgba(140,140,160,0.5)', background: row._rowColor || 'rgba(100,100,120,0.15)', cursor: 'pointer', padding: 0 }}
              />
              {colorPickerOpen && (
                <div style={{ position: 'absolute', left: '100%', top: 0, zIndex: 200, marginLeft: 4 }} onClick={e => e.stopPropagation()}>
                  <ColorSwatch onSelect={color => updateRowColor(idx, color)} />
                </div>
              )}
            </>
          )}
          {!isHovered && row._rowColor && (
            <div style={{ width: 10, height: 4, borderRadius: 2, background: row._rowColor, border: '1px solid rgba(0,0,0,0.15)' }} />
          )}
        </div>
      </td>
      {cols.map((c, colIdx) => (
        <td
          key={c.key}
          className="scene-td"
          data-active={focusedColKey === c.key ? 'true' : undefined}
          data-selected={selectedColIdxSet?.has(colIdx) ? 'true' : undefined}
          data-move-target={moveTargetColIdxSet?.has(colIdx) ? 'true' : undefined}
          data-autofill-preview={autofillPreviewColIdxSet?.has(colIdx) ? 'true' : undefined}
          style={{ position: 'relative', ...(row._cellColors?.[c.key] ? { background: row._cellColors[c.key] } : {}) }}
          onMouseDown={isReadOnly ? undefined : (e) => {
            // Click landed on the td itself (in its padding area), not on the
            // inner div/textarea. Forward to the inner element so the cell's
            // edge area is clickable too — no more dead "border" around text.
            if (e.target !== e.currentTarget) return;
            const editing = e.currentTarget.querySelector('textarea.scene-input');
            if (editing) { editing.focus(); return; }
            const inner = e.currentTarget.querySelector('.scene-cell-idle');
            if (!inner) return;
            inner.focus();
            handlers.clearTabOrigin();
            if (e.button === 0) handlers.startCellDrag(idx, c.key, e.shiftKey, e.ctrlKey || e.metaKey);
          }}
          onDoubleClick={isReadOnly ? undefined : (e) => {
            // An EMPTY cell collapses the inner idle <div> to ~0 height, so a
            // double-click anywhere in the cell lands on the <td> rather than the
            // div — and the td had no dblclick handler, so edit mode never opened.
            // (Single-click still worked because onMouseDown above forwards focus.)
            // Forward the double-click to enterEdit so empty cells are editable too.
            if (e.target !== e.currentTarget) return; // the inner div handles its own dblclick
            if (e.currentTarget.querySelector('textarea.scene-input')) return; // already editing
            handlers.enterEdit(idx, c.key, 'cursor-end');
          }}
          onMouseEnter={isReadOnly ? undefined : () => {
            // Mirror the inner div's drag-tracking handlers on the td so a
            // cursor moving through cell padding (or drifting columns during
            // autofill) still updates the active drag.
            handlers.updateCellDrag(idx, c.key);
            handlers.updateMoveTarget(idx, c.key);
            handlers.updateAutofillTarget(idx);
          }}
        >
          {/* Spec 4.1: move-drag handle on the focused idle cell. Thin strip
              at the top edge with a move cursor — mousedown starts a move-drag
              that's committed on the doc-level mouseup over a target cell. */}
          {!isReadOnly && focusedColKey === c.key && editingColKey !== c.key && (
            <div
              className="scene-cell-movehandle"
              onMouseDown={e => {
                e.preventDefault();
                e.stopPropagation();
                handlers.startMoveDrag(idx, colIdx);
              }}
              title="Drag to move cell content"
            />
          )}
          {/* Spec 5.2: autofill handle — small square at the bottom-right of
              the selection anchor. Drag down to fill subsequent rows with
              detected pattern (numeric, "Scene N", repeating, etc.). */}
          {!isReadOnly && autofillHandleColIdx === colIdx && editingColKey !== c.key && (
            <div
              className="scene-cell-autofillhandle"
              onMouseDown={e => {
                e.preventDefault();
                e.stopPropagation();
                handlers.startAutofillDrag();
              }}
              title="Drag down to fill"
            />
          )}
          {editingColKey !== c.key && (
            <div
              className={`scene-cell-idle scene-input ${c.cls}`}
              data-row={idx}
              data-col={c.key}
              tabIndex={isReadOnly ? -1 : 0}
              onFocus={isReadOnly ? undefined : () => {
                // Spec 1.1/1.2: focus marks cell as idle-selected (active border
                // shows). Edit mode only starts on type/Shift+Enter/F2/dblclick.
                setHoveredRow(idx);
                setFocusedCell({ rowIdx: idx, colKey: c.key });
                lastFocusBodyRef.current = draftBodyRef.current;
                if ((selectedRowsSizeRef?.current || 0) > 0) {
                  setSelectedRows(new Set());
                  setSelectionAnchor(null);
                }
                if (selectAllActiveRef?.current) setSelectAllActive(false);
              }}
              onMouseDown={isReadOnly ? undefined : (e) => {
                handlers.clearTabOrigin();
                // Spec 1.5: start (or shift-extend) a multi-cell range drag.
                if (e.button === 0) handlers.startCellDrag(idx, c.key, e.shiftKey, e.ctrlKey || e.metaKey);
              }}
              onMouseEnter={isReadOnly ? undefined : () => {
                handlers.updateCellDrag(idx, c.key);
                handlers.updateMoveTarget(idx, c.key);
                handlers.updateAutofillTarget(idx);
              }}
              onDoubleClick={isReadOnly ? undefined : () => {
                handlers.enterEdit(idx, c.key, 'cursor-end');
              }}
              onKeyDown={isReadOnly ? undefined : e => {
                // Run 2 (Character column): Alt+T fills the speaker from two rows
                // above — the other side of the exchange. Intercepted before the
                // alt-key passthrough below.
                if (c.key === 'character' && e.altKey && (e.key === 't' || e.key === 'T')) {
                  e.preventDefault();
                  const src = speakerTwoAbove(draftBodyRef, idx);
                  if (src) {
                    updateScene(idx, c.key, src);
                    handlers.enterEdit(idx, c.key, 'cursor-end'); // blur-on-move saves it
                  }
                  return;
                }
                // D-124 (Longform only): Ctrl+Enter / Cmd+Enter is an EXACT alias of Enter (row-advance).
                const ctrlEnterAlias = e.key === 'Enter' && (e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey && draftBodyRef.current?.format === 'Longform';
                // Let global handlers (Ctrl+Z/Ctrl+H, browser shortcuts) through.
                if (!ctrlEnterAlias && (e.ctrlKey || e.metaKey || e.altKey)) return;
                if (e.isComposing) return; // IME composition

                // Navigating to another cell by keyboard collapses any active
                // multi-cell range/multi-selection down to the destination cell
                // (Google-Sheets behavior), so a follow-up copy/delete/color acts on
                // the cell you moved TO — not the stale range. Shift+Enter / F2 open
                // edit mode (not navigation) and are excluded; Shift+Tab still counts.
                if (e.key === 'ArrowLeft' || e.key === 'ArrowRight' ||
                    e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'Tab' ||
                    (e.key === 'Enter' && !e.shiftKey)) {
                  handlers.collapseCellSelection();
                }

                const total = draftBodyRef.current?.scenes?.length || 0;
                const colIdx = cols.findIndex(col => col.key === c.key);

                // Spec 1.1: Shift+Enter / F2 → cursor-end edit mode (no overwrite).
                if ((e.key === 'Enter' && e.shiftKey) || e.key === 'F2') {
                  e.preventDefault();
                  handlers.enterEdit(idx, c.key, 'cursor-end');
                  return;
                }
                // Run 2 (Character column): Enter in an EMPTY Character cell does
                // what Alt+T does — repeat the speaker from two rows above.
                if (e.key === 'Enter' && c.key === 'character' && !String(row[c.key] || '').trim()) {
                  e.preventDefault();
                  const src = speakerTwoAbove(draftBodyRef, idx);
                  if (src) {
                    updateScene(idx, c.key, src);
                    handlers.enterEdit(idx, c.key, 'cursor-end');
                  }
                  return;
                }
                // Spec 1.4: Enter → next row at tab-origin column (or this column).
                if (e.key === 'Enter') {
                  e.preventDefault();
                  const nextIdx = idx + 1;
                  const tabOrigin = handlers.getTabOrigin();
                  const targetCol = tabOrigin != null ? tabOrigin : c.key;
                  handlers.clearTabOrigin();
                  if (nextIdx >= total || !focusVisible(nextIdx, targetCol, 1)) {
                    handlers.queueFocus(total, targetCol);
                    addRow();
                  }
                  return;
                }
                // Spec 1.4: Tab → next col / Shift+Tab → prev col.
                if (e.key === 'Tab') {
                  e.preventDefault();
                  if (!e.shiftKey) {
                    if (handlers.getTabOrigin() == null) handlers.setTabOrigin(c.key);
                    if (colIdx < cols.length - 1) {
                      document.querySelector(`[data-row="${idx}"][data-col="${cols[colIdx + 1].key}"]`)?.focus();
                    } else if (!(idx < total - 1 && focusVisible(idx + 1, cols[0].key, 1))) {
                      handlers.queueFocus(total, cols[0].key);
                      addRow();
                    }
                  } else {
                    handlers.clearTabOrigin();
                    if (colIdx > 0) {
                      document.querySelector(`[data-row="${idx}"][data-col="${cols[colIdx - 1].key}"]`)?.focus();
                    } else if (idx > 0) {
                      focusVisible(idx - 1, cols[cols.length - 1].key, -1);
                    }
                  }
                  return;
                }
                // Spec 1.3: arrow keys move between cells in idle mode (no edge check).
                if (e.key === 'ArrowLeft') {
                  e.preventDefault(); handlers.clearTabOrigin();
                  if (colIdx > 0) document.querySelector(`[data-row="${idx}"][data-col="${cols[colIdx - 1].key}"]`)?.focus();
                  else if (idx > 0) focusVisible(idx - 1, cols[cols.length - 1].key, -1);
                  return;
                }
                if (e.key === 'ArrowRight') {
                  e.preventDefault(); handlers.clearTabOrigin();
                  if (colIdx < cols.length - 1) document.querySelector(`[data-row="${idx}"][data-col="${cols[colIdx + 1].key}"]`)?.focus();
                  else if (idx < total - 1) focusVisible(idx + 1, cols[0].key, 1);
                  return;
                }
                if (e.key === 'ArrowUp') {
                  e.preventDefault(); handlers.clearTabOrigin();
                  if (idx > 0) focusVisible(idx - 1, c.key, -1);
                  return;
                }
                if (e.key === 'ArrowDown') {
                  e.preventDefault(); handlers.clearTabOrigin();
                  if (idx < total - 1) focusVisible(idx + 1, c.key, 1);
                  return;
                }
                // Spec 2.1: Delete OR Backspace clears the active cells.
                // - Delete: always clears, stays in idle.
                // - Backspace: clears the active grid. If it was a single cell,
                //   enter cursor edit-mode on it (Excel/Sheets convention so
                //   the user can immediately start retyping). On a multi-cell
                //   range, just clear and stay idle. (On Mac the main "delete"
                //   key IS Backspace, so this is the path most users hit.)
                if (e.key === 'Delete' || e.key === 'Backspace') {
                  e.preventDefault();
                  handlers.clearActiveCells();
                  // Backspace on a single cell additionally enters cursor mode
                  // so the user can immediately retype (Excel convention).
                  // selectedColRange is '' when there's no multi-cell range on
                  // THIS row, which means we're acting on the focused cell only.
                  if (e.key === 'Backspace' && !selectedColRange) {
                    handlers.enterEdit(idx, c.key, 'cursor-end');
                  }
                  return;
                }
                // Escape from idle → drop focus and clear any multi-cell range.
                if (e.key === 'Escape') {
                  e.preventDefault();
                  handlers.clearTabOrigin();
                  handlers.clearCellSelection();
                  e.target.blur();
                  return;
                }
                // Spec 1.1: any printable char (single-character key) → enter
                // edit with overwrite. The typed char becomes the new cell value.
                if (e.key.length === 1) {
                  e.preventDefault();
                  updateScene(idx, c.key, e.key);
                  handlers.enterEdit(idx, c.key, 'cursor-end');
                  // Run 2 (Character column): the picker opens from the FIRST typed
                  // character (registry names, shared word-prefix matcher).
                  if (c.key === 'character' && registryNames) {
                    const suggestions = matchCharacterNames(e.key, displayCharacters(registryNames));
                    setAutocomplete(suggestions.length > 0 ? { rowIdx: idx, colKey: c.key, suggestions, selIdx: 0 } : null);
                  }
                }
              }}
              style={{ whiteSpace: 'pre-wrap', cursor: 'text', minHeight: '1.5em', fontSize: editorFontSize }}
            >
              {c.key === 'character' && registryNames && !isReadOnly ? (
                <span style={{ display: 'flex', alignItems: 'flex-start', gap: 6, minWidth: 0 }}>
                  <span style={{ flex: 1, minWidth: 0, whiteSpace: 'pre-wrap' }}>{row[c.key] || ''}</span>
                  {!registryHasName(registryNames, row[c.key]) && /\p{L}/u.test(row[c.key] || '') && (
                    // Run 2 §4.3: free text is allowed but MARKED — and only an
                    // explicit click adds it to the registry (never auto-added).
                    <span
                      data-testid="not-in-list"
                      title="This name is not in the project's character list"
                      style={{
                        flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 3,
                        padding: '0 5px', borderRadius: 3, fontSize: 9, fontWeight: 700,
                        letterSpacing: '0.04em', textTransform: 'uppercase', lineHeight: '16px',
                        background: 'rgba(230,180,80,0.12)', color: '#d9a441',
                        border: '1px dashed rgba(230,180,80,0.45)',
                      }}
                    >
                      not in list
                      {canEditRegistry && (
                        <button
                          type="button"
                          data-testid="add-to-list"
                          title="Add to this project's character list"
                          onMouseDown={ev => ev.preventDefault()}
                          onClick={ev => { ev.stopPropagation(); handlers.addCharacterToRegistry(String(row[c.key] || '').trim()); }}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: 11, lineHeight: 1, padding: '0 1px', fontWeight: 800 }}
                        >＋</button>
                      )}
                    </span>
                  )}
                  {isHovered && (
                    <button
                      type="button"
                      data-testid="character-chevron"
                      title="Pick from the project's character list"
                      onMouseDown={ev => ev.preventDefault()}
                      onClick={ev => {
                        ev.stopPropagation();
                        handlers.enterEdit(idx, c.key, 'cursor-end');
                        const suggestions = displayCharacters(registryNames);
                        setAutocomplete(suggestions.length > 0 ? { rowIdx: idx, colKey: c.key, suggestions, selIdx: 0 } : null);
                      }}
                      style={{ flexShrink: 0, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--tx-faint)', fontSize: 10, lineHeight: '16px', padding: '0 2px' }}
                    >▾</button>
                  )}
                </span>
              ) : (row[c.key] || '')}
            </div>
          )}
          {editingColKey === c.key && (
          <textarea
            className={`scene-input ${c.cls}`}
            rows={1}
            data-row={idx}
            data-col={c.key}
            spellCheck={true}
            value={row[c.key] || ''}
            onChange={isReadOnly ? undefined : e => {
              const value = e.target.value;
              updateScene(idx, c.key, value);
              const body = draftBodyRef.current;
              const sceneCount = body?.scenes?.length || 0;
              if (c.key === 'character' && registryNames) {
                // Run 2 §4.3: the Character cell suggests from the client REGISTRY
                // (not from other rows), via the shared word-prefix matcher
                // (D-017/D-018 — same rule as the Characters column header search),
                // from the first character typed. Top option pre-highlighted so Tab
                // has something to accept.
                const suggestions = value.trim()
                  ? matchCharacterNames(value, displayCharacters(registryNames))
                  : [];
                setAutocomplete(suggestions.length > 0 ? { rowIdx: idx, colKey: c.key, suggestions, selIdx: 0 } : null);
              } else if (value.length >= 2 && sceneCount < 200) {
                // Prefix match only — a suggestion must START with what was typed.
                // This deliberately does NOT match mid-word: typing "cut" should not
                // offer "quick cutaway". Case-insensitive (both sides lowercased).
                const q = value.toLowerCase();
                const all = (body?.scenes || [])
                  .filter((_, i2) => i2 !== idx)
                  .map(r => (r[c.key] || '').trim())
                  .filter(v => v && v.toLowerCase().startsWith(q));
                const suggestions = [...new Set(all)].slice(0, 6);
                setAutocomplete(suggestions.length > 0 ? { rowIdx: idx, colKey: c.key, suggestions, selIdx: -1 } : null);
              } else {
                setAutocomplete(null);
              }
            }}
            onFocus={isReadOnly ? undefined : () => {
              setHoveredRow(idx);
              setFocusedCell({ rowIdx: idx, colKey: c.key });
              lastFocusBodyRef.current = draftBodyRef.current;
              if ((selectedRowsSizeRef?.current || 0) > 0) {
                setSelectedRows(new Set());
                setSelectionAnchor(null);
              }
              if (selectAllActiveRef?.current) setSelectAllActive(false);
            }}
            onBlur={isReadOnly ? undefined : (e) => {
              const rt = e.relatedTarget;
              // Stay in edit if focus is moving to the autocomplete dropdown.
              if (rt?.closest?.('[data-autocomplete]')) return;
              setAutocomplete(null);
              if (lastFocusBodyRef.current && lastFocusBodyRef.current !== draftBodyRef.current) {
                pushUndo(lastFocusBodyRef.current);
              }
              saveBody();
              // Pass 3: always exit edit on blur. The new cell (if any) lands in
              // idle-selected state — its onFocus sets focusedCell. If focus
              // leaves the editor entirely, clear focusedCell + hover too.
              handlers.exitEdit();
              const stillInEditor = rt?.matches?.('.scene-input, .scene-cell-idle');
              if (!stillInEditor) {
                setFocusedCell(null);
                setHoveredRow(null);
              }
            }}
            onInput={e => autoResize(e.target)}
            onMouseDown={isReadOnly ? undefined : () => { handlers.clearTabOrigin(); }}
            onKeyDown={isReadOnly ? undefined : e => {
              if ((e.ctrlKey || e.metaKey) && e.key === 'b') { e.preventDefault(); applyMarkdown('**', idx, c.key); return; }
              if ((e.ctrlKey || e.metaKey) && e.key === 'i') { e.preventDefault(); applyMarkdown('*',  idx, c.key); return; }
              if ((e.ctrlKey || e.metaKey) && e.key === 'u') { e.preventDefault(); applyMarkdown('__', idx, c.key); return; }
              const ac = autocomplete;
              if (ac && ac.rowIdx === idx && ac.colKey === c.key) {
                if (!e.shiftKey && e.key === 'ArrowDown') { e.preventDefault(); setAutocomplete(prev => prev ? { ...prev, selIdx: Math.min(prev.selIdx + 1, prev.suggestions.length - 1) } : prev); return; }
                if (!e.shiftKey && e.key === 'ArrowUp')   { e.preventDefault(); setAutocomplete(prev => prev ? { ...prev, selIdx: Math.max(prev.selIdx - 1, 0) } : prev); return; }
                // Only Enter (or clicking a choice) accepts a suggestion. Tab and
                // arrow keys must NOT auto-fill the top choice without consent — Tab
                // just closes the dropdown and moves to the next cell as normal.
                if (e.key === 'Enter') {
                  e.preventDefault();
                  const pickIdx = ac.selIdx >= 0 ? ac.selIdx : 0;
                  updateScene(idx, c.key, ac.suggestions[pickIdx]);
                  setAutocomplete(null);
                  return;
                }
                if (e.key === 'Tab') {
                  // Run 2 (Character column ONLY): Tab ACCEPTS the highlighted
                  // option, then the fall-through below advances to the next cell —
                  // the Line column. Every other column keeps dismiss-on-Tab
                  // (DEF-075 documents this divergence).
                  if (c.key === 'character' && registryNames && !e.shiftKey && ac.suggestions.length > 0) {
                    const pickIdx = ac.selIdx >= 0 ? ac.selIdx : 0;
                    updateScene(idx, c.key, ac.suggestions[pickIdx]);
                  }
                  setAutocomplete(null); /* fall through to Tab navigation below */
                }
                if (e.key === 'Escape') { setAutocomplete(null); return; }
              }
              // Run 2 (Character column): Alt+T — or Enter in an EMPTY cell —
              // fills the speaker from two rows above (the other side of the
              // exchange). Ctrl+T is browser-reserved and deliberately unused.
              if (c.key === 'character' && (
                    (e.altKey && (e.key === 't' || e.key === 'T')) ||
                    (e.key === 'Enter' && !e.shiftKey && !String(e.target.value || '').trim())
                  )) {
                e.preventDefault();
                const src = speakerTwoAbove(draftBodyRef, idx);
                if (src) { updateScene(idx, c.key, src); setAutocomplete(null); }
                return;
              }
              const total = draftBodyRef.current?.scenes?.length || 0;
              // D-124 (Longform only): Ctrl+Enter / Cmd+Enter while editing = Enter (row-advance). Other
              // formats keep their pre-existing behaviour for the chord (a plain newline is never inserted
              // by this editor, so the chord falls through to the same branch there too).
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                setAutocomplete(null);
                const nextIdx = idx + 1;
                // Spec 1.4: if Tab origin is recorded, Enter returns to that column on next row.
                const tabOrigin = handlers.getTabOrigin();
                const targetCol = tabOrigin != null ? tabOrigin : c.key;
                handlers.clearTabOrigin();
                if (nextIdx >= total || !focusVisible(nextIdx, targetCol, 1)) {
                  handlers.queueFocus(total, targetCol);
                  addRow();
                }
                return;
              }
              if (e.key === 'Tab') {
                e.preventDefault();
                setAutocomplete(null);
                const colIdx = cols.findIndex(col => col.key === c.key);
                if (!e.shiftKey) {
                  // Spec 1.4: record where tabbing started so Enter can return here.
                  if (handlers.getTabOrigin() == null) handlers.setTabOrigin(c.key);
                  if (colIdx < cols.length - 1) {
                    document.querySelector(`[data-row="${idx}"][data-col="${cols[colIdx + 1].key}"]`)?.focus();
                  } else if (!(idx < total - 1 && focusVisible(idx + 1, cols[0].key, 1))) {
                    handlers.queueFocus(total, cols[0].key);
                    addRow();
                  }
                } else {
                  // Shift+Tab: backward navigation cancels the forward tab-origin.
                  handlers.clearTabOrigin();
                  if (colIdx > 0) {
                    document.querySelector(`[data-row="${idx}"][data-col="${cols[colIdx - 1].key}"]`)?.focus();
                  } else if (idx > 0) {
                    focusVisible(idx - 1, cols[cols.length - 1].key, -1);
                  }
                }
                return;
              }
              // Spec 1.1: Escape reverts the cell to its pre-edit value and
              // returns to idle-selected (focus the cell's now-mounted div).
              if (e.key === 'Escape') {
                e.preventDefault();
                setAutocomplete(null);
                handlers.clearTabOrigin();
                handlers.revertEdit();
                // After revertEdit, editingCell becomes null; the cell remounts as
                // <div>. Defer focus to next tick so React commits the swap first.
                setTimeout(() => {
                  const el = document.querySelector(`[data-row="${idx}"][data-col="${c.key}"]`);
                  if (el) el.focus();
                }, 0);
                return;
              }
              if (!e.shiftKey && e.key === 'ArrowLeft') {
                if (e.target.selectionStart === 0 && e.target.selectionEnd === 0) {
                  e.preventDefault();
                  handlers.clearTabOrigin();
                  const colIdx = cols.findIndex(col => col.key === c.key);
                  if (colIdx > 0) {
                    const el = document.querySelector(`[data-row="${idx}"][data-col="${cols[colIdx - 1].key}"]`);
                    if (el) el.focus();
                  } else if (idx > 0) {
                    const el = document.querySelector(`[data-row="${idx - 1}"][data-col="${cols[cols.length - 1].key}"]`);
                    if (el) el.focus();
                  }
                }
              }
              if (!e.shiftKey && e.key === 'ArrowRight') {
                if (e.target.selectionStart === e.target.value.length && e.target.selectionEnd === e.target.value.length) {
                  e.preventDefault();
                  handlers.clearTabOrigin();
                  const colIdx = cols.findIndex(col => col.key === c.key);
                  if (colIdx < cols.length - 1) {
                    const el = document.querySelector(`[data-row="${idx}"][data-col="${cols[colIdx + 1].key}"]`);
                    if (el) el.focus();
                  } else if (idx < total - 1) {
                    const el = document.querySelector(`[data-row="${idx + 1}"][data-col="${cols[0].key}"]`);
                    if (el) el.focus();
                  }
                }
              }
              // ArrowUp/ArrowDown (no autocomplete open) intentionally do nothing
              // here: once you're editing a cell, up/down move the cursor within the
              // cell's own text and never jump to the next/previous cell. Leaving the
              // cell is Enter, Tab, or clicking out. (Autocomplete navigation is
              // handled above; Shift+Arrow still moves the whole row.)
              if (e.shiftKey && e.key === 'ArrowUp')   { e.preventDefault(); setAutocomplete(null); moveRow(idx, -1, c.key); }
              if (e.shiftKey && e.key === 'ArrowDown') { e.preventDefault(); setAutocomplete(null); moveRow(idx,  1, c.key); }
            }}
            placeholder={isReadOnly ? '' : `${c.label}…`}
            readOnly={isReadOnly}
            style={isReadOnly
              ? { cursor: 'default', color: 'var(--tx-secondary)', fontSize: editorFontSize }
              : { fontSize: editorFontSize }}
          />
          )}
          {row._links?.[c.key] && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '2px 4px 2px 0', fontSize: 10 }}>
              {isReadOnly ? (
                <a href={row._links[c.key].url} target="_blank" rel="noopener noreferrer"
                  style={{ color: 'var(--s-nr-t)', textDecoration: 'underline', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block' }}>
                  ↗ {row._links[c.key].text || row._links[c.key].url}
                </a>
              ) : (
                <>
                  <a href={row._links[c.key].url} target="_blank" rel="noopener noreferrer"
                    onClick={e => e.stopPropagation()}
                    style={{ color: 'var(--s-nr-t)', textDecoration: 'underline', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block' }}>
                    ↗ {row._links[c.key].text || row._links[c.key].url}
                  </a>
                  <button onMouseDown={e => { e.preventDefault(); e.stopPropagation(); removeLink(idx, c.key); }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--tx-faint)', fontSize: 11, padding: 0, lineHeight: 1, fontFamily: 'var(--font)' }}>×</button>
                </>
              )}
            </div>
          )}
          {!row._links?.[c.key] && (() => {
            const detectedUrl = extractCellUrl(row[c.key]);
            return detectedUrl ? (
              <div style={{ padding: '2px 4px 2px 0', fontSize: 10 }}>
                <a href={detectedUrl} target="_blank" rel="noreferrer"
                  onClick={e => e.stopPropagation()}
                  style={{ color: 'var(--violet)', textDecoration: 'underline', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block' }}>
                  ↗ Open link
                </a>
              </div>
            ) : null;
          })()}
          {!isReadOnly && (
            <>
              <button
                title="Cell color"
                onMouseDown={e => { e.preventDefault(); e.stopPropagation(); setColorPickerCell(colorPickerCellKey === c.key ? null : { rowIdx: idx, colKey: c.key }); setColorPickerRow(null); }}
                onClick={e => e.stopPropagation()}
                style={{
                  position: 'absolute', top: 4, right: 4,
                  width: 12, height: 12, borderRadius: 3, padding: 0, cursor: 'pointer',
                  border: row._cellColors?.[c.key] ? '1px solid rgba(255,255,255,0.15)' : '1px dashed rgba(140,140,160,0.5)',
                  background: row._cellColors?.[c.key] || 'rgba(100,100,120,0.15)',
                  opacity: (isHovered || row._cellColors?.[c.key]) ? 1 : 0,
                  pointerEvents: (isHovered || row._cellColors?.[c.key]) ? 'auto' : 'none',
                  transition: 'opacity 0.1s',
                }}
              />
              {colorPickerCellKey === c.key && (
                <div style={{ position: 'absolute', top: 18, right: 4, zIndex: 200 }} onClick={e => e.stopPropagation()}>
                  <ColorSwatch onSelect={color => updateCellColor(idx, c.key, color)} />
                </div>
              )}
              {LINK_COLS[format]?.includes(c.key) && (
                <>
                  <button
                    title="Insert link"
                    onMouseDown={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      const selText = window.getSelection()?.toString() || '';
                      setLinkDraft({ text: row._links?.[c.key]?.text || selText, url: row._links?.[c.key]?.url || '' });
                      setLinkPopover(linkPopoverColKey === c.key ? null : { rowIdx: idx, colKey: c.key });
                      setColorPickerCell(null); setColorPickerRow(null);
                    }}
                    onClick={e => e.stopPropagation()}
                    style={{
                      position: 'absolute', top: 18, right: 2,
                      background: 'none', border: 'none', cursor: 'pointer',
                      minWidth: 32, minHeight: 32, padding: 8,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      boxSizing: 'border-box', lineHeight: 1,
                      fontSize: 11, color: row._links?.[c.key] ? 'var(--violet)' : 'var(--tx-faint)',
                      opacity: (isHovered || row._links?.[c.key]) ? 1 : 0,
                      pointerEvents: (isHovered || row._links?.[c.key]) ? 'auto' : 'none',
                    }}
                  >🔗</button>
                  {linkPopoverColKey === c.key && linkDraft && (
                    <div style={{ position: 'absolute', ...(idx === 0 ? { top: '100%', marginTop: 4 } : { bottom: '100%', marginBottom: 4 }), right: 0, zIndex: 400 }} onClick={e => e.stopPropagation()}>
                      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-mid)', borderRadius: 10, padding: 14, width: 260, boxShadow: '0 4px 20px rgba(0,0,0,0.5)' }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--tx-secondary)', marginBottom: 10 }}>Insert Link</div>
                        <input
                          autoFocus
                          placeholder="Display text"
                          value={linkDraft.text}
                          onChange={e => setLinkDraft(prev => ({ ...prev, text: e.target.value }))}
                          style={{ width: '100%', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 7, padding: '7px 10px', fontSize: 12, color: 'var(--tx-primary)', fontFamily: 'var(--font)', marginBottom: 8, boxSizing: 'border-box' }}
                        />
                        <input
                          placeholder="URL (https://…)"
                          value={linkDraft.url}
                          onChange={e => setLinkDraft(prev => ({ ...prev, url: e.target.value }))}
                          onKeyDown={e => {
                            if (e.key === 'Enter') { e.preventDefault(); saveLink(idx, c.key, linkDraft.text, linkDraft.url); }
                            if (e.key === 'Escape') setLinkPopover(null);
                          }}
                          style={{ width: '100%', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 7, padding: '7px 10px', fontSize: 12, color: 'var(--tx-primary)', fontFamily: 'var(--font)', marginBottom: 10, boxSizing: 'border-box' }}
                        />
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button onClick={() => setLinkPopover(null)}
                            style={{ flex: 1, background: 'var(--bg-card2)', border: '1px solid var(--border)', borderRadius: 7, padding: '7px 0', fontSize: 12, color: 'var(--tx-secondary)', cursor: 'pointer', fontFamily: 'var(--font)' }}>Cancel</button>
                          <button
                            disabled={!linkDraft.url.trim()}
                            onClick={() => saveLink(idx, c.key, linkDraft.text, linkDraft.url)}
                            style={{ flex: 1, background: linkDraft.url.trim() ? 'var(--violet)' : 'var(--bg-card2)', border: 'none', borderRadius: 7, padding: '7px 0', fontSize: 12, color: linkDraft.url.trim() ? '#fff' : 'var(--tx-faint)', cursor: linkDraft.url.trim() ? 'pointer' : 'default', fontFamily: 'var(--font)', fontWeight: 600 }}>Save</button>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
              {autocomplete && autocomplete.rowIdx === idx && autocomplete.colKey === c.key && autocomplete.suggestions.length > 0 && (
                <div data-autocomplete style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 300, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', boxShadow: '0 4px 16px rgba(0,0,0,0.3)', maxHeight: 180, overflowY: 'auto' }}>
                  {autocomplete.suggestions.map((s, i) => (
                    <div
                      key={i}
                      onMouseDown={e => { e.preventDefault(); updateScene(idx, c.key, s); setAutocomplete(null); }}
                      onMouseEnter={() => setAutocomplete(prev => prev ? { ...prev, selIdx: i } : prev)}
                      style={{ padding: '7px 10px', fontSize: 12, cursor: 'pointer', color: 'var(--tx-secondary)', background: autocomplete.selIdx === i ? 'rgba(255,255,255,0.06)' : 'transparent', borderBottom: i < autocomplete.suggestions.length - 1 ? '1px solid var(--border)' : 'none', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                    >
                      {s}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </td>
      ))}
      {showQualNotesCol && (
        <td className="scene-td" style={{ position: 'relative' }}>
          <textarea
            className="scene-input notes"
            rows={1}
            data-row={idx}
            data-col="qualNotes"
            spellCheck={true}
            value={row.qualNotes || ''}
            onChange={canEditQualNotes ? e => { updateScene(idx, 'qualNotes', e.target.value); } : undefined}
            onFocus={canEditQualNotes ? () => { setHoveredRow(idx); setFocusedCell({ rowIdx: idx, colKey: 'qualNotes' }); lastFocusBodyRef.current = draftBodyRef.current; } : undefined}
            onBlur={canEditQualNotes ? e => { setFocusedCell(null); if (lastFocusBodyRef.current && lastFocusBodyRef.current !== draftBodyRef.current) pushUndo(lastFocusBodyRef.current); saveBody(); if (!e.relatedTarget?.classList.contains('scene-input')) setHoveredRow(null); } : undefined}
            onInput={e => autoResize(e.target)}
            placeholder={canEditQualNotes ? 'Note…' : ''}
            readOnly={!canEditQualNotes}
            style={!canEditQualNotes ? { cursor: 'default', color: 'var(--tx-secondary)', fontSize: editorFontSize } : { fontSize: editorFontSize }}
          />
        </td>
      )}
    </tr>
  );
});

// ── Main component ───────────────────────────────────────────────

export default function Scripts() {
  const { user, hasRole } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // List state
  const [scripts,      setScripts]      = useState([]);
  const [listLoad,     setListLoad]     = useState(true);
  const [search,       setSearch]       = useState(() => searchParams.get('q') || '');
  const [statusFilter, setStatusFilter] = useState(() => searchParams.get('status') || '');
  const [clientFilter, setClientFilter] = useState(() => searchParams.get('client') || '');
  const [writerFilter, setWriterFilter] = useState(() => searchParams.get('writer') || '');
  // Character search — lives in the Characters COLUMN HEADER, not the toolbar.
  // Two pieces of state on purpose:
  //   characterQuery  — what is in the box right now; drives the suggestions.
  //   characterPrefix — the debounced value actually sent to the server.
  // They are separate because every change to the applied prefix refetches the
  // list (this is the one filter that cannot be refined client-side: GET
  // /api/scripts never returns script_body, so the server does the matching),
  // and refetching 565 rows per keystroke is not a search box.
  //
  // `?character=` (the retired dropdown's exact-name param) is still read once on
  // mount so an existing bookmarked URL keeps filtering — a full name is a valid
  // prefix. From then on only `?characterPrefix=` is written.
  const [characterQuery,   setCharacterQuery]   = useState(
    () => searchParams.get('characterPrefix') || searchParams.get('character') || '');
  const [characterPrefix,  setCharacterPrefix]  = useState(
    () => searchParams.get('characterPrefix') || searchParams.get('character') || '');
  const [characterOptions, setCharacterOptions] = useState([]);
  // Whole-column expand: collapsed (default) every cell is a single scrolling
  // strip, expanded EVERY row wraps to show all of its tags. One flag, not one
  // per row — the point is to compare characters down the column at a glance.
  const [charsExpanded,    setCharsExpanded]    = useState(false);
  // Archived is a VIEW switch (mode), not a status chip — it's orthogonal to
  // qual_status (B4). Default view hides archived + Posted; the Archived view
  // shows ONLY archived, across all statuses, with the status chips filtering
  // within it.
  const [archivedView, setArchivedView] = useState(() => searchParams.get('view') === 'archived');
  // Deleted is a FOURTH view — the recycle bin. Soft-deleted scripts are hidden
  // from every other view; this is the only place they surface, and the only
  // place they can be restored from. Admin / Power User only (server enforces).
  const [deletedView,  setDeletedView]  = useState(() => searchParams.get('view') === 'deleted');
  // Chip counts come from the server (the default list excludes Posted+archived,
  // so they can't be counted client-side): { active: {<status>:n}, archived: {total, <status>:n},
  // deleted: {total, <status>:n} — `deleted` absent entirely for non-admins }.
  const [srvCounts,    setSrvCounts]    = useState({ active: {}, archived: { total: 0 } });
  // Templates are a third VIEW alongside the production line and the Archive
  // shelf. A template is inert (no qual status, never produced), so it gets its
  // own list shape rather than being squeezed into the production grid.
  const [templatesView, setTemplatesView] = useState(() => searchParams.get('view') === 'templates');
  const [templates,     setTemplates]     = useState([]);
  const [templatesLoad, setTemplatesLoad] = useState(false);
  const [saveTplPrompt, setSaveTplPrompt] = useState(null); // null | { scriptId, defaultName }
  const [useTplPrompt,  setUseTplPrompt]  = useState(null); // null | { template }
  const [delTplConfirm, setDelTplConfirm] = useState(null); // null | { template }
  const [scriptSort,   setScriptSort]   = useState(() => {
    const fromUrl = { col: searchParams.get('sortBy') || '', dir: searchParams.get('sortDir') || 'asc' };
    if (fromUrl.col) return fromUrl;
    try {
      const s = JSON.parse(sessionStorage.getItem('scripts_sort') || 'null');
      if (s?.col) return s;
    } catch {}
    return { col: '', dir: 'asc' };
  });
  const [clients,      setClients]      = useState([]);
  const [members,      setMembers]      = useState([]);
  const [noteEdits,    setNoteEdits]    = useState({}); // { [scriptId]: draftValue } for inline list editing

  // Editor state
  const [selected,      setSelected]      = useState(null);
  const [draftBody,     setDraftBody]     = useState(null);
  const [draftNotes,    setDraftNotes]    = useState('');
  const [draftTitle,    setDraftTitle]    = useState('');
  const [saving,        setSaving]        = useState(false);
  const [lastSaved,     setLastSaved]     = useState(null);
  // Surfaces a failed save so silent data loss can't recur. null = healthy.
  const [saveError,     setSaveError]     = useState(null); // null | { tooLarge } | { largeDeletion } | { retrying }
  // True when the draft has edits not yet CONFIRMED saved on the server. Drives a
  // persistent "Unsaved" indicator so a save that never landed can't masquerade as saved.
  const [isDirty,       setIsDirty]       = useState(false);
  // P2: same-cell collisions reported by the last op-save (another editor changed a
  // cell you also changed). Non-blocking; surfaced in a dismissible banner. LWW already
  // applied server-side — this just makes the rare silent same-cell loss visible.
  const [cellConflicts, setCellConflicts] = useState([]);

  // Live word counter — words-per-minute is a per-user personal preference
  // (follows the user across every script), persisted in localStorage. `wpm`
  // only ever holds a valid, clamped value so it can safely divide; `wpmInput`
  // is the raw text in the box while typing (may be transiently empty/invalid).
  const [wpm, setWpm] = useState(() => {
    const stored = parseInt(localStorage.getItem(WPM_STORAGE_KEY), 10);
    return Number.isFinite(stored) ? clampWpm(stored) : WPM_DEFAULT;
  });
  const [wpmInput, setWpmInput] = useState(() => String(wpm));
  const wpmPersistTimer = useRef(null);

  // Capture openScriptId before setSearchParams on mount can clear location.state
  const pendingAutoOpenRef = useRef(location.state?.openScriptId || null);
  // Return ticket handed over by the Production Calendar's Script List: the
  // editor's back button navigates to backTo.path (with backTo.state) instead
  // of the Scripts list. Captured at mount for the same reason as above.
  const backToRef = useRef(location.state?.backTo || null);

  // Refs so beforeunload and goBackToList always read the latest values
  const selectedRef   = useRef(null);
  const draftBodyRef  = useRef(null);
  const draftNotesRef = useRef('');

  // The exact body/notes references handed to setDraftBody/setDraftNotes at load
  // time. The debounced autosave compares against these and skips while they're
  // still untouched, so merely *opening* a script never echoes its (possibly
  // stale or empty-fallback) body straight back to the server. Without this, a
  // hard refresh could race the GET ahead of the beforeunload flush, load a
  // stale/null body, then autosave it 2s later and clobber the real script.
  const loadedBodyRef  = useRef(null);
  const loadedNotesRef = useRef('');

  // The last body/notes references CONFIRMED saved on the server (200 returned).
  // dirty = current draft differs from these. Updated on load and on every confirmed
  // save; used by the Unsaved indicator and the unmount flush so unconfirmed edits
  // are never silently treated as durable.
  const savedBodyRef   = useRef(null);
  const savedNotesRef  = useRef('');
  // P2: the server revision (`rev`) the current savedBodyRef is based on. Seeded from
  // the loaded script, advanced on every confirmed op-save and every poll that pulls
  // newer content. Sent as `baseRev` and used by the /changes poll as `sinceRev`.
  const revRef         = useRef(0);
  // P2: guards so the 5s poll never overlaps itself or races an in-flight save.
  const pollingRef     = useRef(false);
  const savingRef      = useRef(false);
  // Auto-retry bookkeeping for transient (network / 5xx) save failures.
  const saveRetryRef   = useRef(0);
  const saveRetryTimer = useRef(null);

  // Undo stack — stores draftBody snapshots, max 50 steps
  const undoStack        = useRef([]);
  const lastFocusBodyRef = useRef(null);
  const handleUndoRef    = useRef(null);
  const handleRedoRef    = useRef(null);
  const redoStack        = useRef([]);

  // SceneRow memoization scaffolding:
  // 1) refs that handlers inside the memoized row read for latest state
  // 2) handlersLatestRef updated each render with current functions
  // 3) rowHandlers stable wrapper object (useMemo with empty deps) that delegates
  //    through handlersLatestRef. Passing this to <SceneRow> means the row's memo
  //    shallow-compare sees handlers as referentially equal across renders.
  const selectedRowsSizeRef = useRef(0);
  const selectAllActiveRef  = useRef(false);
  const handlersLatestRef   = useRef({});
  // Latest app-clipboard handlers/state for the capture-phase keydown listener,
  // so it never reads a stale closure.
  const appClipKbdRef       = useRef({});

  // Version history state
  const [versionsOpen,    setVersionsOpen]    = useState(false);
  const [versions,        setVersions]        = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [restoreConfirm,        setRestoreConfirm]        = useState(null);
  const [approvedTransitionWarn, setApprovedTransitionWarn] = useState(false);
  const [pendingTransition,      setPendingTransition]      = useState(null);
  const [errorModal,             setErrorModal]             = useState(null); // { title, message }
  const [transitioning, setTransit]       = useState(false);
  const [showNew,       setShowNew]       = useState(false);
  // For the New Script modal: prefill a client and (when launched from inside an
  // open script) open the created script in a new browser tab. { clientId, openInNewTab }
  const [newScriptDefaults, setNewScriptDefaults] = useState(null);
  const [deleteConfirm,   setDeleteConfirm]   = useState(false);
  const [hoveredRow,      setHoveredRow]      = useState(null);
  const [colorPickerRow,  setColorPickerRow]  = useState(null);
  const [colorPickerCell, setColorPickerCell] = useState(null); // { rowIdx, colKey }
  const [autocomplete,    setAutocomplete]    = useState(null); // { rowIdx, colKey, suggestions, selIdx }
  const [selectedRows,       setSelectedRows]       = useState(new Set());
  const [multiDeleteConfirm, setMultiDeleteConfirm] = useState(false);
  // Anchor row for Shift+click range selection. Updated by plain click and Ctrl/Cmd+click.
  const [selectionAnchor,    setSelectionAnchor]    = useState(null);
  // Drag-to-select state. Plain ref (not state) so mousemove doesn't trigger re-renders.
  // { active: bool, anchorIdx: number, startX, startY, suppressClick: bool }
  const dragSelectRef = useRef({ active: false, anchorIdx: null, startX: 0, startY: 0, suppressClick: false });
  // Phase 3 drag-to-reposition state.
  // dropTargetIdx is the gap index (0..scenes.length) where moved rows will land.
  const [dropTargetIdx, setDropTargetIdx] = useState(null);
  // Floating preview chip that follows the cursor during a reposition drag.
  const [dragPreview, setDragPreview] = useState(null); // null | { x, y, count }
  const repositionRef = useRef({ active: false, startX: 0, startY: 0 });

  // Phase 4 row clipboard (internal — does not touch navigator.clipboard).
  // null | { rows: [sceneObj…], wasCut: bool, cutIndices: number[] (only if wasCut) }
  const rowClipboardRef = useRef(null);
  // Rows currently marked as "cut" — rendered dimmed until paste or Escape.
  const [cutRows, setCutRows] = useState(new Set());
  // Brief toast message shown after copy/cut.
  const [copyFeedback, setCopyFeedback] = useState(null);

  // Phase 5: right-click context menu on the gutter.
  // null | { x, y, targetRows: number[], clickedCell: { rowIdx, colKey } | null }
  const [contextMenu, setContextMenu] = useState(null);
  const ctxMenuRef = useRef(null); // measured for viewport clamping (see useLayoutEffect)
  const [linkPopover,        setLinkPopover]        = useState(null); // { rowIdx, colKey }
  const [linkDraft,          setLinkDraft]          = useState({ text: '', url: '' });
  const [selectedScripts,    setSelectedScripts]    = useState(new Set());
  const [scriptMassDelete,   setScriptMassDelete]   = useState(false);
  const [bulkStatusBusy,     setBulkStatusBusy]     = useState(false);
  // Reassign-to-another-project modal. `reassignData` is what the server says is
  // possible for the current selection ({ movable, blocked, targets }) and
  // `reassignResult` is what actually happened, shown in the same modal so the
  // renamed scripts are read where the move was ordered.
  const [reassignData,      setReassignData]       = useState(null);
  const [reassignResult,    setReassignResult]     = useState(null);
  const [reassignBusy,      setReassignBusy]       = useState(false);
  const [scriptCtxMenu,      setScriptCtxMenu]      = useState(null); // { x, y, script }
  const [activityLog,        setActivityLog]        = useState([]);
  const [activityOpen,       setActivityOpen]       = useState(false);
  const [activityLoading,    setActivityLoading]    = useState(false);
  const [editorFontSize,     setEditorFontSize]     = useState(12);
  const [focusedCell,        setFocusedCell]        = useState(null); // { rowIdx, colKey }
  // Phase B1 (spec 1.1/1.2/1.3): two-state cell model. editingCell is set only
  // when a textarea is actively mounted for that cell; idle cells render as
  // <div>s. mode controls how the textarea's caret/selection is set on mount.
  // originalValue is captured at edit-start so Escape can revert.
  const [editingCell,        setEditingCell]        = useState(null);
  const editingCellRef       = useRef(null);
  useEffect(() => { editingCellRef.current = editingCell; }, [editingCell]);
  // Phase B2 (spec 1.5): rectangular selection range across cells.
  // null = no range; otherwise { startRow, startCol, endRow, endCol } with
  // startCol/endCol as column indices into `cols`. A 1x1 range counts as
  // "no visual highlight" — the active border alone handles the single-cell case.
  const [cellSelection,      setCellSelection]      = useState(null);
  // Non-contiguous cell selection: Set of "rowIdx:colKey" strings. Built via
  // Ctrl/Cmd+click. Coexists with cellSelection — when Ctrl+click happens with
  // an active rect range, the rect's cells are frozen into this set first so
  // they're not lost.
  const [cellMultiSelection, setCellMultiSelection] = useState(() => new Set());
  // Single source of truth for copy/paste: which selection the user last made.
  // 'cells' (drag/click a cell) or 'rows' (click the gutter). getActiveSelection
  // reads THIS rather than guessing from which selection state is non-empty —
  // that guessing is what made copy/paste flip between cells and rows.
  const [selectionMode, setSelectionMode] = useState(null); // 'cells' | 'rows' | null
  const draggingRef          = useRef(false);
  // Last "row:col" a live cell drag extended to — dedupes the document-level
  // mousemove and the auto-scroll rAF loop so they don't setState per event.
  const cellDragLastRef      = useRef(null);
  // Phase C1 (spec 4.1): move-drag in progress.
  // { sourceRow, sourceCol (colIdx), targetRow, targetCol (colIdx) } | null
  const [moveDrag,           setMoveDrag]           = useState(null);
  const moveDragRef          = useRef(null);
  useEffect(() => { moveDragRef.current = moveDrag; }, [moveDrag]);
  // Phase C2 (spec 5.2): autofill-drag in progress.
  // { sourceR1, sourceR2, sourceC1, sourceC2, targetEndRow } | null
  const [autofillDrag,       setAutofillDrag]       = useState(null);
  const autofillDragRef      = useRef(null);
  useEffect(() => { autofillDragRef.current = autofillDrag; }, [autofillDrag]);
  useEffect(() => {
    const onUp = () => {
      draggingRef.current = false;
      cellDragLastRef.current = null;
      handlersLatestRef.current.stopEdgeAutoScroll?.();
      if (moveDragRef.current)     handlersLatestRef.current.commitMoveDrag?.();
      if (autofillDragRef.current) handlersLatestRef.current.commitAutofillDrag?.();
    };
    // Spec 4.1/4.2/5.2: doc-level mousemove keeps drags tracking even when the
    // cursor crosses cell padding or drifts into adjacent columns where the
    // per-cell mouseenter handlers can miss. Uses elementFromPoint on the
    // current pointer coordinates to identify the cell under the cursor.
    const onMove = (e) => {
      // Live cell-range SELECTION drag: extend under the pointer (per-cell
      // mouseenter misses fast moves) and feed the edge auto-scroller.
      if (draggingRef.current) {
        handlersLatestRef.current.cellDragPointerMove?.(e.clientX, e.clientY);
      }
      const af = autofillDragRef.current;
      const md = moveDragRef.current;
      if (!af && !md) return;
      const el = document.elementFromPoint(e.clientX, e.clientY);
      if (!el) return;
      const td = el.closest?.('.scene-td');
      if (!td) return;
      const tr = td.parentElement;
      if (!tr?.dataset?.rowIdx) return;
      const rowIdx = Number(tr.dataset.rowIdx);
      if (!Number.isFinite(rowIdx)) return;
      if (af) handlersLatestRef.current.updateAutofillTarget(rowIdx);
      if (md) {
        const colInner = td.querySelector?.('[data-col]');
        const colKey = colInner?.dataset?.col;
        if (colKey) handlersLatestRef.current.updateMoveTarget(rowIdx, colKey);
      }
    };
    document.addEventListener('mouseup', onUp);
    document.addEventListener('mousemove', onMove);
    return () => {
      document.removeEventListener('mouseup', onUp);
      document.removeEventListener('mousemove', onMove);
      handlersLatestRef.current.stopEdgeAutoScroll?.(); // no loop survives unmount
    };
  }, []);
  const [scriptLinkCopied,   setScriptLinkCopied]   = useState(false);
  const [shareOpen,          setShareOpen]          = useState(false);
  const [shareLinks,         setShareLinks]         = useState([]);
  const [shareLinksLoad,     setShareLinksLoad]     = useState(false);
  const [shareGenerating,    setShareGenerating]    = useState(false);
  const [shareNewUrl,        setShareNewUrl]        = useState(null);
  const [shareCopied,        setShareCopied]        = useState(null);
  const [clioOpen,           setClioOpen]           = useState(false);
  const [findReplaceOpen,    setFindReplaceOpen]    = useState(false);

  // ── Spreadsheet clipboard sync ───────────────────────────────────
  // # cell select-all state — when active, Ctrl+C/V apply to the whole script.
  const [selectAllActive, setSelectAllActive] = useState(false);
  const [hashCellHover,   setHashCellHover]   = useState(false);
  // Hidden focused textarea — catches paste/copy events when select-all is active so the
  // browser fires those events on an element we control (instead of a cell textarea or
  // dropping them entirely when nothing editable is focused).
  const pasteCatcherRef = useRef(null);

  // Resizable columns — list view
  const { template: scriptColTemplateNoQn, startDrag: startScriptColDragNoQn } = useResizableColumns('colWidths_scripts_ch',    SCRIPT_COL_DEFAULTS);
  const { template: scriptColTemplateQn,   startDrag: startScriptColDragQn   } = useResizableColumns('colWidths_scripts_qn_ch', SCRIPT_COL_DEFAULTS_QN);

  // ── Cross-script app clipboard ───────────────────────────────────
  // Persistent sessionStorage-backed clipboard for copying a selection from one
  // script and pasting it into another (or elsewhere in the same script).
  // Distinct from the in-tab-only rowClipboardRef and the # select-all Google
  // Sheets clipboard — this one survives navigation and new same-session tabs.
  const {
    payload:        appClip,
    rowCount:       appClipCount,
    writeClipboard: writeAppClip,
    clearClipboard: clearAppClip,
  } = useScriptClipboard();
  // Dismissible toast shown after an app-clipboard copy (has a Clear action, so
  // it can't reuse the pointer-events-none copyFeedback toast).
  const [appClipToast, setAppClipToast] = useState(null); // null | string
  const appClipToastTimer = useRef(null);
  // Cross-format paste warning (whole-row paste only). Holds the resolver of a
  // Promise that pasteFromAppClipboard awaits, so the async flow reads in sequence.
  const [crossFormatPastePrompt, setCrossFormatPastePrompt] = useState(null); // null | { sourceFormat, destFormat, resolve }

  // Resizable columns — editor (percentage-based). Last entry is always the Qual Notes column.
  // Skit:           character 13%, line 27%, notes 13%, scene 13%, props 13%, qualNotes 21%
  // TH:             narration 30%, aRoll 10%, bRoll 10%, vfx 10%, sfx 10%, props 10%, qualNotes 20%
  // AI:             narration 42%, visuals 42%, qualNotes 16%
  // Mockumentary:   character 11%, line 18%, notes 11%, aroll 8%, broll 8%, vfx 9%, props 9%, location 8%, qualNotes 18%
  const { pcts: skitEditorPcts,   startDrag: startSkitEditorDrag,   reset: resetSkitEditorCols   } = useResizableTableColumns('colWidths_editor_Skit_v2',         [13, 27, 13, 13, 13, 21]);
  const { pcts: thEditorPcts,     startDrag: startThEditorDrag,     reset: resetThEditorCols     } = useResizableTableColumns('colWidths_editor_TH_v2',           [30, 10, 10, 10, 10, 10, 20]);
  const { pcts: aiEditorPcts,     startDrag: startAiEditorDrag,     reset: resetAiEditorCols     } = useResizableTableColumns('colWidths_editor_AI_v2',           [42, 42, 16]);
  const { pcts: mockEditorPcts,   startDrag: startMockEditorDrag,   reset: resetMockEditorCols   } = useResizableTableColumns('colWidths_editor_Mockumentary_v2', [11, 18, 11, 8, 8, 9, 9, 8, 18]);
  const { pcts: recipeEditorPcts, startDrag: startRecipeEditorDrag, reset: resetRecipeEditorCols } = useResizableTableColumns('colWidths_editor_Recipe_v2',       [55, 30, 15]);
  // Longform: sceneNumber 8%, character 16%, line 44%, scene 16%, qualNotes 16%
  const { pcts: lfEditorPcts,     startDrag: startLfEditorDrag,     reset: resetLfEditorCols     } = useResizableTableColumns('colWidths_editor_Longform_v1',     [8, 16, 44, 16, 16]);
  // "With Production Notes" variants — used only when the Production Notes column
  // is visible (approved scripts). The column is included in-place so every drag
  // handle maps 1:1 to the on-screen columns. Order = displayCols order + qualNotes.
  // Skit:  character, line, PRODNOTES, notes, scene, props, qualNotes
  // TH:    narration, PRODNOTES, aRoll, bRoll, vfx, sfx, props, qualNotes
  // Mock:  character, line, PRODNOTES, notes, aroll, broll, vfx, props, location, qualNotes
  // Recipe: recipe, PRODNOTES, gestures, qualNotes
  const { pcts: skitEditorPctsP,   startDrag: startSkitEditorDragP,   reset: resetSkitEditorColsP   } = useResizableTableColumns('colWidths_editor_Skit_prod_v1',         [12, 24, 12, 12, 12, 12, 16]);
  const { pcts: thEditorPctsP,     startDrag: startThEditorDragP,     reset: resetThEditorColsP     } = useResizableTableColumns('colWidths_editor_TH_prod_v1',           [26, 12, 9, 9, 9, 9, 9, 17]);
  const { pcts: mockEditorPctsP,   startDrag: startMockEditorDragP,   reset: resetMockEditorColsP   } = useResizableTableColumns('colWidths_editor_Mockumentary_prod_v1', [10, 16, 10, 10, 7, 7, 8, 8, 8, 16]);
  const { pcts: recipeEditorPctsP, startDrag: startRecipeEditorDragP, reset: resetRecipeEditorColsP } = useResizableTableColumns('colWidths_editor_Recipe_prod_v1',       [42, 22, 22, 14]);
  // Longform: sceneNumber, character, line, PRODNOTES, scene, qualNotes
  const { pcts: lfEditorPctsP,     startDrag: startLfEditorDragP,     reset: resetLfEditorColsP     } = useResizableTableColumns('colWidths_editor_Longform_prod_v1',     [7, 14, 38, 14, 14, 13]);

  // Server decides the shelf: Archived view → only archived; Posted chip → only
  // Posted; Filming chip → only in-production; default → none of those.
  // Client/writer/search/other-status refinement stays client-side. Refetches
  // only when the shelf changes (view or a shelf chip), so switching among
  // normal status chips is still instant.
  const postedChip  = statusFilter === 'Posted';
  const filmingChip = statusFilter === 'Filming';
  const loadScripts = useCallback(async () => {
    setListLoad(true);
    try {
      const p = new URLSearchParams();
      if (deletedView)       p.set('deleted', 'true');
      else if (archivedView) p.set('archived', 'true');
      else if (postedChip)   p.set('qualStatus', 'Posted');
      else if (filmingChip)  p.set('qualStatus', 'Filming');
      // The one filter that has to be server-side (see characterPrefix above),
      // so it joins the shelf params and refetches when it changes.
      if (characterPrefix)   p.set('characterPrefix', characterPrefix);
      const res = await api.get('/api/scripts' + (p.toString() ? `?${p}` : ''));
      setScripts(res.data);
    } catch (err) {
      console.error('Failed to load scripts:', err);
    } finally {
      setListLoad(false);
    }
  }, [archivedView, deletedView, postedChip, filmingChip, characterPrefix]);

  useEffect(() => { loadScripts(); }, [loadScripts]);

  // Debounce the typed query into the applied prefix. A query with no letter in
  // it applies NO filter: the column will not render such a value as a tag
  // (hasLetter), so filtering the list by it would leave rows whose Characters
  // cell is visibly empty and no way to see why.
  useEffect(() => {
    const q = characterQuery.trim();
    const next = hasLetter(q) ? q : '';
    if (next === characterPrefix) return;          // nothing to apply, no refetch
    const t = setTimeout(() => setCharacterPrefix(next), 250);
    return () => clearTimeout(t);
  }, [characterQuery, characterPrefix]);

  // Chip counts, in the current client/writer/search scope (mirrors GET / scope).
  const loadCounts = useCallback(async () => {
    try {
      const p = new URLSearchParams();
      if (clientFilter) p.set('clientId', clientFilter);
      if (writerFilter) p.set('writerId', writerFilter);
      if (search)       p.set('search', search);
      const res = await api.get('/api/scripts/status-counts' + (p.toString() ? `?${p}` : ''));
      setSrvCounts(res.data || { active: {}, archived: { total: 0 } });
    } catch { /* counts are best-effort */ }
  }, [clientFilter, writerFilter, search]);

  useEffect(() => { loadCounts(); }, [loadCounts]);

  // Suggestions for the Characters column's header search, SUBSETTED by the
  // current client/writer/view exactly as the retired dropdown's options were:
  // the server returns only the characters of the scripts this scope would list,
  // so picking a client narrows the suggestions and adding a writer narrows them
  // again. Deliberately not scoped by `search` — they describe the shelf, and
  // making them flicker per keystroke would be worse than leaving them stable.
  const loadCharacterOptions = useCallback(async () => {
    const p = new URLSearchParams();
    if (clientFilter) p.set('clientId', clientFilter);
    if (writerFilter) p.set('writerId', writerFilter);
    if (deletedView)        p.set('view', 'deleted');
    else if (archivedView)  p.set('view', 'archived');
    else if (templatesView) p.set('view', 'templates');
    try {
      const res = await api.get('/api/scripts/characters' + (p.toString() ? `?${p}` : ''));
      setCharacterOptions(Array.isArray(res.data) ? res.data : []);
      // No stale-value reset here, unlike the dropdown this replaced. That reset
      // existed because a selection the narrowed scope had never heard of left
      // the list empty with no visible cause — a closed <select> showing a name
      // does not explain itself. The header search does: the text the user typed
      // is sitting in the box, so silently clearing it would be the surprising
      // behaviour, not keeping it.
    } catch {
      setCharacterOptions([]); // suggestions are best-effort; the list still works
    }
  }, [clientFilter, writerFilter, archivedView, templatesView, deletedView]);

  useEffect(() => { loadCharacterOptions(); }, [loadCharacterOptions]);

  // Sync sort + filter state to URL params and sessionStorage
  useEffect(() => {
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      if (search) p.set('q', search); else p.delete('q');
      if (statusFilter) p.set('status', statusFilter); else p.delete('status');
      if (deletedView) p.set('view', 'deleted');
      else if (archivedView) p.set('view', 'archived');
      else if (templatesView) p.set('view', 'templates');
      else p.delete('view');
      if (clientFilter) p.set('client', clientFilter); else p.delete('client');
      if (writerFilter) p.set('writer', writerFilter); else p.delete('writer');
      // Written as characterPrefix; `character` is dropped so a URL carrying the
      // retired dropdown's param does not keep both around after it is read once.
      p.delete('character');
      if (characterPrefix) p.set('characterPrefix', characterPrefix); else p.delete('characterPrefix');
      if (scriptSort.col) p.set('sortBy', scriptSort.col); else p.delete('sortBy');
      if (scriptSort.dir !== 'asc') p.set('sortDir', scriptSort.dir); else p.delete('sortDir');
      return p;
    }, { replace: true });
    if (scriptSort.col) {
      sessionStorage.setItem('scripts_sort', JSON.stringify(scriptSort));
    } else {
      sessionStorage.removeItem('scripts_sort');
    }
  }, [scriptSort, search, statusFilter, clientFilter, writerFilter, characterPrefix, archivedView, templatesView, deletedView]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load clients + members on mount for filter + new script modal
  useEffect(() => {
    api.get('/api/clients').then(r => setClients(r.data)).catch(() => {});
    api.get('/api/team-members').then(r => setMembers(r.data)).catch(() => {});
  }, []);

  // Keep refs in sync so beforeunload/goBackToList always see the latest values
  useEffect(() => { selectedRef.current   = selected;   }, [selected]);
  useEffect(() => { draftBodyRef.current  = draftBody;  }, [draftBody]);
  useEffect(() => { draftNotesRef.current = draftNotes; }, [draftNotes]);
  useEffect(() => { savingRef.current     = saving;     }, [saving]);
  // Synced unconditionally each render (cheap), so SceneRow's stable handlers always
  // see the latest selection size + select-all flag without taking those as props
  // (which would break memoization).
  selectedRowsSizeRef.current = selectedRows.size;
  selectAllActiveRef.current  = selectAllActive;

  // Clear undo + redo stacks when a different script is opened
  useEffect(() => {
    undoStack.current = []; redoStack.current = []; lastFocusBodyRef.current = null;
  }, [selected?.id]);

  // Ctrl+Z / Ctrl+Shift+Z / Ctrl+Y — undo/redo when focus is outside a textarea
  // (textareas use browser-native undo for intra-cell typing, per spec 2.2).
  // Ctrl+H always opens Find & Replace, even inside a cell — spec 6.1.
  // Ctrl+X / Ctrl+V / Ctrl+D — selection actions (spec 2.3, 2.4) when in idle
  // mode. Guarded by NOT being in a textarea so they don't interfere with
  // native textarea cut/paste during editing.
  useEffect(() => {
    if (!selected) return;
    const onKeyDown = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      if (e.key === 'h' || e.key === 'H') {
        e.preventDefault();
        setFindReplaceOpen(true);
        return;
      }
      if (document.activeElement?.tagName === 'TEXTAREA') return;
      // Normalize case: with Shift held, the Z key reports as 'Z', so the redo
      // chord (Ctrl+Shift+Z) must match uppercase too — otherwise only Ctrl+Y
      // ever triggered redo.
      const k = e.key.toLowerCase();
      if (k === 'z' && !e.shiftKey) {
        e.preventDefault(); handleUndoRef.current?.();
      } else if ((k === 'z' && e.shiftKey) || k === 'y') {
        e.preventDefault(); handleRedoRef.current?.();
      } else if (e.key === 'x' || e.key === 'X') {
        // Only act if focus is on a scene cell — otherwise let browser handle.
        if (document.activeElement?.classList?.contains('scene-cell-idle')) {
          e.preventDefault();
          handlersLatestRef.current.cutActiveCells();
        }
      } else if (e.key === 'v' || e.key === 'V') {
        // While a row cut is pending, Ctrl+V is a paste-to-MOVE and belongs to the
        // row-clipboard handler below — yield so the move still works even if a
        // scene cell happens to be focused.
        if (cutRows.size > 0) return;
        if (document.activeElement?.classList?.contains('scene-cell-idle')) {
          e.preventDefault();
          // Claim the paste so the legacy row-clipboard handler (below) can't
          // ALSO fire on the same keypress — that double-fire is what pasted
          // every line twice when the app-clipboard capture handler declined.
          e.stopImmediatePropagation();
          handlersLatestRef.current.pasteIntoActiveCells();
        }
      } else if (e.key === 'd' || e.key === 'D') {
        if (document.activeElement?.classList?.contains('scene-cell-idle')) {
          e.preventDefault();
          handlersLatestRef.current.fillDownActiveCells();
        }
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [selected, cutRows]); // cutRows: V yields to the row-move handler while a cut is pending

  // Spreadsheet clipboard sync: deactivate select-all whenever the open script changes.
  useEffect(() => {
    setSelectAllActive(false);
  }, [selected?.id]);

  // Escape deactivates # select-all (when no cell is being edited).
  useEffect(() => {
    if (!selectAllActive) return;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      setSelectAllActive(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [selectAllActive]);

  // Click outside the script table or its clipboard toolbar deactivates select-all.
  useEffect(() => {
    if (!selectAllActive) return;
    const onMouseDown = (e) => {
      if (e.button !== 0) return;
      if (!(e.target instanceof Element)) return;
      if (e.target.closest('.scene-table')) return;
      if (e.target.closest('[data-clipboard-toolbar]')) return;
      setSelectAllActive(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [selectAllActive]);

  // When select-all turns on, focus the hidden paste-catcher so that the next Ctrl+C/V
  // fires the paste/copy events on an element we own (the catcher's own onPaste/onCopy
  // handlers process the events synchronously via e.clipboardData).
  useEffect(() => {
    if (!selectAllActive) return;
    const id = requestAnimationFrame(() => pasteCatcherRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [selectAllActive]);

  // Belt-and-suspenders: any Ctrl+C/V at the document level while select-all is active
  // refocuses the catcher first, so the event fires on it even if focus drifted.
  useEffect(() => {
    if (!selectAllActive) return;
    const onKeyDown = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      if (e.key === 'c' || e.key === 'C' || e.key === 'v' || e.key === 'V') {
        const a = document.activeElement;
        if (a !== pasteCatcherRef.current) {
          pasteCatcherRef.current?.focus();
        }
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [selectAllActive]);

  // Phase 4: row clipboard keyboard shortcuts (Ctrl/Cmd+C/X/V/D, Escape).
  // - C/X/V defer to native browser clipboard when the user is editing inside a textarea or input.
  // - D is always intercepted (suppresses the browser "Add bookmark" dialog).
  // - Escape cancels an in-progress cut.
  useEffect(() => {
    if (!selected) return;
    const onKeyDown = (e) => {
      // When # select-all is active, the dedicated spreadsheet-sync handler owns C/V.
      if (selectAllActive) return;
      const ctrl = e.ctrlKey || e.metaKey;
      const tag = document.activeElement?.tagName;
      const inTextarea = tag === 'TEXTAREA';
      const inInput = tag === 'INPUT';

      if (ctrl && (e.key === 'd' || e.key === 'D') && !isReadOnly) {
        e.preventDefault();
        if (!inTextarea && !inInput && selectedRows.size > 0) duplicateSelectedRows();
        return;
      }
      if (inTextarea || inInput) return; // C/X/V → let native clipboard handle text editing

      if (ctrl && (e.key === 'c' || e.key === 'C') && selectedRows.size > 0) {
        e.preventDefault();
        copySelectedRows();
        return;
      }
      if (ctrl && (e.key === 'x' || e.key === 'X') && selectedRows.size > 0 && !isReadOnly) {
        e.preventDefault();
        cutSelectedRows();
        return;
      }
      if (ctrl && (e.key === 'v' || e.key === 'V') && rowClipboardRef.current && !isReadOnly) {
        e.preventDefault();
        // Single-owner guard: if any other Ctrl+V handler already acted this
        // keypress it stopped propagation before us, so reaching here means we
        // own the paste. Stop here too so no third handler can double it.
        e.stopImmediatePropagation();
        pasteRows();
        return;
      }
      if (e.key === 'Escape' && cutRows.size > 0) {
        setCutRows(new Set());
        rowClipboardRef.current = null;
        return;
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [selected, selectedRows, cutRows, selectAllActive]); // eslint-disable-line react-hooks/exhaustive-deps

  // Flush pending changes when the tab/browser is closed
  // Best-effort durable flush of the latest UNCONFIRMED draft. Used for both full
  // page unload (beforeunload) and in-app unmount (navigating away from the editor),
  // which the debounced timer would otherwise drop. Reads refs so it always sends the
  // newest values, and no-ops when there's nothing unsaved. keepalive (not sendBeacon)
  // because it must carry the Authorization header. A destructive flush is still caught
  // by the server shrink guard (409, not applied).
  const flushUnsaved = () => {
    const base0 = savedBodyRef.current;
    const draft = draftBodyRef.current;
    if (!selectedRef.current || !draft) return;
    const notesChanged = draftNotesRef.current !== savedNotesRef.current;
    if (draft === base0 && !notesChanged) return;
    const authHeader = api.defaults.headers.common['Authorization'];
    if (!authHeader) return;
    const apiBase = process.env.REACT_APP_API_URL || 'https://api.heliconflow.app';
    const headers = { 'Content-Type': 'application/json', Authorization: authHeader };
    // P2: flush as ops so a last-second unmount/unload still MERGES rather than clobbers
    // a concurrent editor. Whole-body fallback only when there's no baseline or the format
    // changed (which ops can't express). keepalive (not sendBeacon) so the auth header rides.
    const useLegacy = !base0 || base0.format !== draft.format;
    if (useLegacy) {
      fetch(`${apiBase}/api/scripts/${selectedRef.current.id}`, {
        method: 'PATCH', headers,
        body: JSON.stringify({ scriptBody: draft, qualNotes: draftNotesRef.current }),
        keepalive: true,
      });
      return;
    }
    const ops = diffToOps(base0, draft);
    if (ops.length === 0 && !notesChanged) return;
    fetch(`${apiBase}/api/scripts/${selectedRef.current.id}/ops`, {
      method: 'PATCH', headers,
      body: JSON.stringify({ baseRev: revRef.current, ops,
        ...(notesChanged && { qualNotes: draftNotesRef.current }), saveType: 'autosave' }),
      keepalive: true,
    });
  };

  useEffect(() => {
    const handleBeforeUnload = () => flushUnsaved();
    window.addEventListener('beforeunload', handleBeforeUnload);
    // Cleanup also fires on component unmount (in-app navigation away from the editor),
    // flushing any pending edit the 2s debounce hadn't saved yet. This closes the
    // silent-loss path where leaving the page mid-edit dropped the queued autosave.
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      flushUnsaved();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep the Unsaved indicator in sync: dirty whenever the draft differs from the
  // last confirmed-saved server state.
  useEffect(() => {
    setIsDirty(!!selected && (draftBody !== savedBodyRef.current || draftNotes !== savedNotesRef.current));
  }, [draftBody, draftNotes, selected]);

  // Debounced autosave — fires 2s after last change
  useEffect(() => {
    if (!selected || !draftBody) return;
    // Skip the echo-save right after a script loads: only autosave once the user
    // has actually edited the body or notes. Every edit handler replaces draftBody
    // with a new object (and draftNotes with a new string), so the first real edit
    // breaks reference-equality and re-enables saving. This prevents a hard refresh
    // from writing a stale/empty just-loaded body back over good server data.
    if (draftBody === loadedBodyRef.current && draftNotes === loadedNotesRef.current) return;
    const timer = setTimeout(saveBody, 2000); // eslint-disable-line react-hooks/exhaustive-deps
    return () => clearTimeout(timer);
  }, [draftBody, draftNotes]); // saveBody intentionally omitted — reads latest closure

  // ── P2: background change poll ───────────────────────────────────────────────
  // While the editor is open and the tab is visible, poll every 5s for changes other
  // editors have saved (GET /:id/changes?sinceRev=rev). Bounds the convergence window
  // to ≤5s even for a passive viewer who never saves — exactly the reviewer-with-a-stale-
  // copy pattern that caused the data loss. Pulls once immediately on re-focus; pauses
  // while hidden (no point polling a backgrounded tab) and skips while a save is in flight
  // (the save response already reconciles). Foreign changes are folded in via
  // adoptServerBody, which preserves the user's un-acked local edits.
  useEffect(() => {
    if (!selected) return;
    const pull = async () => {
      if (pollingRef.current || savingRef.current) return;
      // Defer pulling while the user is actively editing a cell: adopting a foreign change
      // mid-keystroke would call setDraftBody and disrupt the cursor / in-progress text.
      // They catch up within 5s of blurring (and saves reconcile via the save response).
      if (editingCellRef.current) return;
      if (document.visibilityState !== 'visible') return;
      pollingRef.current = true;
      try {
        const res = await api.get(`/api/scripts/${selected.id}/changes`, { params: { sinceRev: revRef.current } });
        if (res.data?.changed) {
          // Only adopt (and let adoptServerBody advance revRef) when it's safe to. If a
          // save is in flight or state isn't ready, leave revRef untouched so the NEXT
          // poll retries — never advance past an unadopted change (that would drop it).
          if (res.data.scriptBody && savedBodyRef.current && !savingRef.current && draftBodyRef.current) {
            adoptServerBody(res.data.scriptBody, savedBodyRef.current, res.data.rev,
                            draftNotesRef.current !== savedNotesRef.current);
          }
        } else if (typeof res.data?.rev === 'number') {
          revRef.current = res.data.rev; // caught up — safe to advance
        }
      } catch { /* transient — next tick retries */ }
      finally { pollingRef.current = false; }
    };
    const interval = setInterval(pull, 5000);
    const onVisible = () => { if (document.visibilityState === 'visible') pull(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearInterval(interval); document.removeEventListener('visibilitychange', onVisible); };
  }, [selected?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Resize all scene textareas when a script loads.
  // Double-RAF ensures the browser has resolved tableLayout:fixed column widths
  // before we measure scrollHeight — setTimeout(0) fires too early.
  useEffect(() => {
    if (!selected) return;
    let raf2;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        autoResizeAll();
      });
    });
    return () => { cancelAnimationFrame(raf1); cancelAnimationFrame(raf2); };
  }, [selected?.id]);

  // Keep textarea heights in sync after draftBody mutations.
  // Walks the prev→current diff regardless of length difference and resizes only the
  // cells that actually moved. Per-keystroke that's 1 cell. addRow-at-end is 5 cells
  // (the new row only — existing rows already have correct heights). Insert/delete in
  // the middle is N cells (everything from the splice point on, since indices shifted).
  // Falls back to chunked autoResizeAll only when many cells differ (paste, undo).
  // Also handles "focus the new row after addRow" without a setTimeout — adders set
  // pendingFocusRef, we read+clear it here so focus lands in the same commit as the row.
  const lastResizeBodyRef = useRef(null);
  const pendingFocusRef = useRef(null);
  // Longform scene flow (D-118): armed by "Add scene" BEFORE the script reloads; consumed by
  // selectScript when the reloaded body lands, so the scene's first row is appended onto the
  // SERVER body (never a stale draft) — { scriptId, sceneNumber, description }.
  const pendingSceneAppendRef = useRef(null);
  // Spec 1.4: remembers the column the user started tabbing from, so Enter
  // after Tab returns to that column on the next row. Cleared on Enter,
  // arrow-nav, mousedown, or Escape.
  const tabOriginColRef = useRef(null);
  useLayoutEffect(() => {
    if (!draftBody) return;
    const prev = lastResizeBodyRef.current;
    lastResizeBodyRef.current = draftBody;
    const scenes = draftBody.scenes || [];
    if (!prev) {
      autoResizeAll();
    } else {
      const prevScenes = prev.scenes || [];
      const changedEls = [];
      const maxLen = Math.max(scenes.length, prevScenes.length);
      for (let i = 0; i < maxLen; i++) {
        const row = scenes[i];
        const prevRow = prevScenes[i];
        if (!row) continue; // row was deleted at this index — nothing to resize
        if (row === prevRow) continue; // reference preserved → height already correct
        for (const key in row) {
          if (key.charCodeAt(0) === 95) continue; // skip metadata keys (_rowColor, _links, etc.)
          if (!prevRow || row[key] !== prevRow[key]) {
            const el = document.querySelector(`[data-row="${i}"][data-col="${key}"]`);
            if (el) changedEls.push(el);
          }
        }
      }
      if (changedEls.length > 200) {
        // Paste / undo-restore: chunk the resize so we don't block the main thread.
        autoResizeAll();
      } else {
        for (let i = 0; i < changedEls.length; i++) autoResize(changedEls[i]);
      }
    }
    // Same-flush focus for newly-added rows (Enter / Tab at end). No setTimeout — focus
    // happens before paint so the user never sees the new row briefly without a cursor.
    if (pendingFocusRef.current) {
      const { rowIdx, colKey, scroll } = pendingFocusRef.current;
      pendingFocusRef.current = null;
      const el = document.querySelector(`[data-row="${rowIdx}"][data-col="${colKey}"]`);
      if (el) { el.focus(); autoResize(el); }
      // Longform scene flow (D-118) only: the series panel above the grid may re-lay out after
      // the reload, so bring the appended row into view after paint. Skit adders never set this.
      // Twice: once after paint, once after the panel's own async fetches (episodes, cast) have
      // settled the layout — the second nudge is a no-op when the row is already centred.
      if (el && scroll) { const nudge = () => { try { el.scrollIntoView({ block: 'center' }); } catch { /* no-op */ } }; requestAnimationFrame(nudge); setTimeout(nudge, 450); }
    }
  }, [draftBody]);

  // Phase 2: click anywhere outside the gutter (and outside the multi-row action
  // footer / multi-delete modal) clears the row selection. Right-clicks are
  // intentionally ignored here — they trigger the row context menu without
  // changing selection.
  useEffect(() => {
    if (selectedRows.size === 0) return;
    const handler = (e) => {
      if (e.button !== 0) return; // left-click only
      const t = e.target;
      if (!(t instanceof Element)) return;
      // Clicks on the gutter are handled by handleGutterMouseDown — don't clear here.
      if (t.closest('td.scene-num')) return;
      // Preserve selection while the user interacts with the multi-row footer or its modal.
      if (t.closest('[data-keep-row-selection]')) return;
      // While the gutter context menu is open, clicks on its items shouldn't clear yet.
      if (t.closest('[data-gutter-context-menu]')) return;
      setSelectedRows(new Set());
      setSelectionAnchor(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [selectedRows.size]);

  // Phase 5: suppress right-click mousedown default inside scene rows. Without this,
  // a right-click on a textarea focuses the cell, whose onFocus clears the multi-row
  // selection BEFORE the contextmenu event reads it — so the menu would only see the
  // single right-clicked row.
  useEffect(() => {
    if (!selected) return;
    const onMouseDown = (e) => {
      if (e.button !== 2) return;
      if (!(e.target instanceof Element)) return;
      // Editable target → the browser owns this right-click (it needs the
      // default mousedown to place the caret on the misspelled word before
      // its native menu opens). Our menu won't open for it anyway.
      if (isEditableTarget(e.target)) return;
      if (e.target.closest('td.scene-num')) return;
      if (!e.target.closest('tr.scene-row')) return;
      e.preventDefault();
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [selected]);

  // Phase 5: row context menu — bound at document level so a single listener
  // catches every right-click that lands in a scene row, regardless of how
  // deeply nested the click target is.
  useEffect(() => {
    if (!selected) return;
    const onContextMenu = (e) => {
      if (!(e.target instanceof Element)) return;
      // Right-click inside an in-edit cell textarea / qual-notes textarea /
      // link-popover input → let the browser's native menu open (spellcheck
      // suggestions live there). No preventDefault, no app menu, no selection
      // change.
      if (isEditableTarget(e.target)) return;
      // Right-click anywhere in a scene row — including the gutter (td.scene-num) —
      // opens the context menu. (Gutter right-click previously fell through to the
      // browser's native menu; now it targets the clicked row like a cell does.)
      const tr = e.target.closest('tr.scene-row');
      if (!tr) return; // Not inside a scene row — let native menu show.
      const rowIdxStr = tr.getAttribute('data-row-idx');
      const idx = rowIdxStr != null ? Number(rowIdxStr) : null;
      if (idx == null || Number.isNaN(idx)) return;
      // canEditBody is computed later in the render — read isReadOnly via DOM check instead
      // by looking for a readonly attribute on the textareas. Simpler: bail if no draftBody.
      if (!draftBody) return;
      e.preventDefault();
      const target = selectedRows.has(idx) && selectedRows.size > 0
        ? [...selectedRows].sort((a, b) => a - b)
        : [idx];
      // Capture the clicked cell (if any) so the menu can offer cell coloring.
      const cellEl = e.target.closest('td.scene-td');
      const colKey = cellEl?.querySelector('[data-col]')?.getAttribute('data-col') || null;
      const clickedCell = (colKey && cellEl && !cellEl.classList.contains('scene-num'))
        ? { rowIdx: idx, colKey }
        : null;
      setContextMenu({ x: e.clientX, y: e.clientY, targetRows: target, clickedCell });
    };
    document.addEventListener('contextmenu', onContextMenu);
    return () => document.removeEventListener('contextmenu', onContextMenu);
  }, [selected, selectedRows, draftBody]);

  // Phase 5: close the gutter context menu on outside-click or Escape.
  useEffect(() => {
    if (!contextMenu) return;
    const onMouseDown = (e) => {
      if (!(e.target instanceof Element)) return;
      if (e.target.closest('[data-gutter-context-menu]')) return;
      setContextMenu(null);
    };
    const onKey = (e) => { if (e.key === 'Escape') setContextMenu(null); };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [contextMenu]);

  // Keep the context menu fully on-screen regardless of where the click landed.
  // The menu's height varies (color swatches, optional cell-color section), so we
  // measure the real rendered box and clamp — running in a layout effect so the
  // correction happens before paint (no visible flicker). Falls back to scrolling
  // if the menu is somehow taller than the viewport (maxHeight is set in style).
  useLayoutEffect(() => {
    if (!contextMenu) return;
    const el = ctxMenuRef.current;
    if (!el) return;
    const m = 8; // viewport margin
    const r = el.getBoundingClientRect();
    let left = contextMenu.x;
    let top  = contextMenu.y;
    if (left + r.width  > window.innerWidth  - m) left = window.innerWidth  - r.width  - m;
    if (top  + r.height > window.innerHeight - m) top  = window.innerHeight - r.height - m;
    el.style.left = `${Math.max(m, left)}px`;
    el.style.top  = `${Math.max(m, top)}px`;
  }, [contextMenu]);

  // Re-resize when column widths change (so text rewraps after column drag)
  useEffect(() => {
    if (!selected) return;
    let raf2;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        autoResizeAll();
      });
    });
    return () => { cancelAnimationFrame(raf1); cancelAnimationFrame(raf2); };
  }, [skitEditorPcts, thEditorPcts, aiEditorPcts, mockEditorPcts, recipeEditorPcts]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-resize when font size changes so rows expand/contract to fit the new line height
  useEffect(() => {
    if (!selected) return;
    let raf2;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        autoResizeAll();
      });
    });
    return () => { cancelAnimationFrame(raf1); cancelAnimationFrame(raf2); };
  }, [editorFontSize]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-resize when the browser window/viewport changes size (zoom, resize)
  useEffect(() => {
    if (!selected) return;
    const handleResize = () => {
      requestAnimationFrame(() => {
        autoResizeAll();
      });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [selected]);

  // Auto-open a specific script when navigated from a Video Card or ?script= URL param.
  // pendingAutoOpenRef captures openScriptId at mount time — setSearchParams({ replace: true })
  // fires on mount and clears location.state before listLoad toggles.
  useEffect(() => {
    const id = pendingAutoOpenRef.current || searchParams.get('script');
    if (!listLoad && id && !selected) {
      pendingAutoOpenRef.current = null;
      // Keep ?script=<id> in the URL so refreshing stays on this script
      // (selectScript ensures the param is present for in-app/state navigation too).
      selectScript(id, { fromAutoOpen: true });
    }
  }, [listLoad]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load share links when panel opens
  useEffect(() => {
    if (shareOpen && selected?.id) {
      loadShareLinks(selected.id);
      setShareNewUrl(null);
    }
  }, [shareOpen, selected?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sidebar "Scripts" click always re-navigates to /scripts (changing location.key).
  // If we're in the editor when that happens, return to list view.
  useEffect(() => {
    if (selected) goBackToList();
  }, [location.key]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close color pickers and link popover on any outside click
  useEffect(() => {
    if (colorPickerRow === null && !colorPickerCell && !linkPopover) return;
    const close = () => { setColorPickerRow(null); setColorPickerCell(null); setLinkPopover(null); };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [colorPickerRow, colorPickerCell, linkPopover]);

  // ── Derived ───────────────────────────────────────────────────────

  const writerOptions = useMemo(() => {
    const map = {};
    // Seed from existing scripts so writers always appear even if role changed
    scripts.forEach(s => {
      if (s.writerId && s.writer?.name) map[s.writerId] = s.writer.name;
    });
    // Also include any active member with Writer or Power User role
    members.forEach(m => {
      if (m.isActive !== false && m.roles?.some(r => r.role === 'Writer' || r.role === 'Power User')) {
        map[m.id] = m.name;
      }
    });
    return Object.entries(map)
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [scripts, members]);

  // Per-status chip counts now come from the server (srvCounts) so they can cover
  // Posted and archived scripts, which are excluded from the default fetched list.

  const displayScripts = useMemo(() => {
    let result = scripts;
    // 'Filming' is a server-side shelf, not a qual_status: the fetched list is
    // already exactly the in-production set, and those scripts carry ordinary
    // qual statuses (mostly Approved), so filtering by name here would empty it.
    if (statusFilter && !filmingChip) result = result.filter(s => s.qualStatus === statusFilter);
    if (clientFilter) result = result.filter(s => s.clientId === clientFilter);
    if (writerFilter) result = result.filter(s => s.writerId === writerFilter);
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(s =>
        s.scriptName?.toLowerCase().includes(q) ||
        s.client?.name?.toLowerCase().includes(q) ||
        s.writer?.name?.toLowerCase().includes(q)
      );
    }
    if (!scriptSort.col) return result;
    const { col, dir } = scriptSort;
    return [...result].sort((a, b) => {
      let av = '', bv = '';
      if      (col === 'Script Name') { av = a.scriptName || '';    bv = b.scriptName || ''; }
      else if (col === 'Project')      { av = a.client?.name || '';  bv = b.client?.name || ''; }
      else if (col === 'Writer')      { av = a.writer?.name || '';  bv = b.writer?.name || ''; }
      else if (col === 'Status')      { av = listStatus(a) || '';  bv = listStatus(b) || ''; }
      else if (col === 'Date')        { av = a.dateEntered || '';   bv = b.dateEntered || ''; }
      else if (col === 'Approved')    { av = a.approvedAt || '';    bv = b.approvedAt || ''; }
      else return 0;
      return dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    });
  }, [scripts, statusFilter, filmingChip, clientFilter, writerFilter, search, scriptSort]);

  // ── Handlers ─────────────────────────────────────────────────────

  const selectScript = async (id, { fromAutoOpen = false } = {}) => {
    // For regular list clicks: push a history entry (browser back → returns to list).
    // Skip for auto-open cases (VideoCard state nav / ?script= URL) — those don't
    // need it and the extra navigate was clearing location.state before setSelected fired.
    // Reflect the open script in the URL *before* the async load so a refresh
    // restores it. This MUST happen while `selected` is still null: the
    // [location.key] effect below calls goBackToList() on any navigation while a
    // script is open, so changing the URL after setSelected would bounce us to the list.
    if (!fromAutoOpen) {
      const params = new URLSearchParams(location.search);
      params.set('script', id);
      navigate('/scripts?' + params.toString()); // push → browser-back returns to the list
    } else if (searchParams.get('script') !== id) {
      // Opened via in-app state nav (e.g. a Video Card) with no ?script= param —
      // add it without a history push so a refresh still restores it.
      const params = new URLSearchParams(location.search);
      params.set('script', id);
      navigate('/scripts?' + params.toString(), { replace: true });
    }
    try {
      const res = await api.get(`/api/scripts/${id}`);
      const s = res.data;
      setSelected(s);
      const fmt = s.scriptBody?.format || s.client?.allowedScriptFormats?.find(f => f !== 'None') || null;
      const rawBody = s.scriptBody || (fmt ? { format: fmt, scenes: [emptyRow(fmt)] } : null);
      const idBody = rawBody?.scenes
        ? normalizeSceneIds({ ...rawBody, scenes: rawBody.scenes.map(sc =>
            sc._propsItems?.length && !sc.props
              ? { ...sc, props: sc._propsItems.join(', ') }
              : sc
          )})
        : rawBody;
      // FIX-2: a Longform body stored with raw scene numbers is renumbered here, into the baseline, so
      // the D-130 effect below finds nothing to change and opening the script is not an edit (it used to
      // autosave a whole-script renumber, 525 ops on PGE-test-V1-MA). The server applies the same
      // normalization to the whole body on the next real save, so the stored body converges then.
      const migratedBody = idBody?.format === 'Longform' && Array.isArray(idBody.scenes)
        ? { ...idBody, scenes: lfe.normalizeSceneNumbers(idBody.scenes).rows }
        : idBody;
      // D-118: append the just-confirmed scene's first dialogue row onto the freshly loaded
      // body and land in its Character cell. loadedBodyRef / savedBodyRef below keep the
      // loaded body (without the appended row), so the appended row is a real edit — autosave (and D-110 sync) fire.
      const pendingAppend = pendingSceneAppendRef.current; pendingSceneAppendRef.current = null;
      let openingBody = migratedBody;
      if (pendingAppend && pendingAppend.scriptId === s.id && migratedBody?.format === 'Longform') {
        const scenes = migratedBody.scenes || [];
        const row = { ...emptyRow('Longform'), sceneNumber: String(pendingAppend.sceneNumber), scene: String(pendingAppend.description || '').trim() };
        openingBody = { ...migratedBody, scenes: [...scenes, row] };
        pendingFocusRef.current = { rowIdx: scenes.length, colKey: 'character', scroll: true };
      }
      setDraftBody(openingBody);
      setDraftNotes(s.qualNotes || '');
      // Clear any selection carried over from a previously-open script so a paste
      // into this one doesn't anchor on a stale cell from the source script.
      setCellSelection(null);
      setCellMultiSelection(new Set());
      setSelectedRows(new Set());
      setSelectionAnchor(null);
      setFocusedCell(null);
      setSelectionMode(null);
      // Mark this freshly-loaded state as "not yet edited" so the debounced
      // autosave skips it until the user actually changes something.
      loadedBodyRef.current  = migratedBody;
      loadedNotesRef.current = s.qualNotes || '';
      // Freshly loaded == confirmed-saved server state, so the draft starts clean.
      savedBodyRef.current   = migratedBody;
      savedNotesRef.current  = s.qualNotes || '';
      // P2: seed the revision the draft is based on (drives op-save baseRev + the poll).
      revRef.current         = s.rev || 0;
      setIsDirty(false);
      setCellConflicts([]);
      setSaveError(null);
      saveRetryRef.current   = 0;
      if (saveRetryTimer.current) { clearTimeout(saveRetryTimer.current); saveRetryTimer.current = null; }
      setDraftTitle(s.scriptName || '');
      setLastSaved(null);
      setActivityLog([]);
      setActivityOpen(false);
      const storedSize = parseInt(localStorage.getItem(`fontSize_script_${s.id}`), 10);
      setEditorFontSize(storedSize >= 8 && storedSize <= 24 ? storedSize : 12);
    } catch (err) {
      console.error('Failed to load script:', err);
    }
  };

  const goBackToList = () => {
    saveBody(); // flush any pending changes before clearing state
    setSelected(null);
    setDraftBody(null);
    setDraftNotes('');
    // Drop ?script= so the list (not the editor) is what a refresh restores now.
    setSearchParams(prev => { const p = new URLSearchParams(prev); p.delete('script'); return p; }, { replace: true });
    loadScripts();
  };

  const doTransition = async (newStatus) => {
    setTransit(true);
    try {
      const payload = { status: newStatus, qualNotes: draftNotes };
      const res = await api.patch(`/api/scripts/${selected.id}/status`, payload);
      setSelected(res.data);
      // D-126: leaving Approved deleted a Longform series' episode cards; approving minted them — re-read them.
      if ((res.data?.scriptBody?.format || format) === 'Longform') { setLfEpisodesVersion(v => v + 1); loadLfEpisodes(selected.id); }
      setDraftNotes(res.data.qualNotes || '');
    } catch (err) {
      setErrorModal({
        title: 'Status Update Failed',
        message: err.response?.data?.error || 'Failed to update status.',
      });
    } finally {
      setTransit(false);
    }
  };

  const handleTransition = (newStatus) => {
    if (!selected || transitioning) return;
    if (selected.qualStatus === 'Approved') {
      setPendingTransition(newStatus);
      setApprovedTransitionWarn(true);
      return;
    }
    doTransition(newStatus);
  };

  // Shared save-failure handling for both the op-save and legacy paths.
  const handleSaveError = (err, manual) => {
    console.error('Save failed:', err);
    const status = err?.response?.status;
    // Shrink guard: server refused a save that would wipe most of the script (usually
    // a stale copy). Surface loudly; the server has already backed up the old version.
    if (status === 409 && err.response.data?.error === 'large_deletion') {
      setSaveError({ largeDeletion: err.response.data });
    } else if (status === 413) {
      setSaveError({ tooLarge: true });
    } else if (saveRetryRef.current < 3) {
      // Transient (network blip / 5xx): auto-retry with backoff before giving up, so a
      // momentary failure doesn't strand unsaved work. The draft stays dirty throughout.
      saveRetryRef.current += 1;
      setSaveError({ retrying: true });
      saveRetryTimer.current = setTimeout(() => saveBody(manual), 4000 * saveRetryRef.current);
    } else {
      // Exhausted retries — surface a hard failure with a manual Retry affordance.
      setSaveError({ failed: true });
    }
  };

  // P2: fold a merged/pulled server body into the editor, preserving the user's own
  // un-acked edits (those made relative to `baseBody`). `serverBody` is authoritative for
  // everyone else's changes; the user's in-flight edits are re-applied as ops on top so
  // typing during a save/poll round-trip is never lost. Advances the confirmed baseline
  // (savedBodyRef) and the rev. Reference-equality is preserved in the common no-change
  // case so the Unsaved indicator stays accurate and the cursor isn't disturbed.
  const adoptServerBody = (serverBody, baseBody, newRev, notesDirty) => {
    const pendingOps = diffToOps(baseBody, draftBodyRef.current);
    // Did anyone else change content? Compare by id + cell value (NOT JSON.stringify —
    // Postgres JSONB doesn't preserve key order, so a stringify compare false-positives
    // on every save). An empty diff means the server body matches what we based our edits
    // on, so we can keep the current draft reference (clean) instead of rebuilding it.
    const foreign = diffToOps(baseBody, serverBody).length > 0;
    if (foreign) {
      // Someone else changed cells/rows: rebuild the draft from the server body with our
      // pending edits replayed. With no pending edits we adopt the server body verbatim
      // (same reference → clean), otherwise reconcile.
      setDraftBody(pendingOps.length ? applyOps(serverBody, pendingOps) : serverBody);
      savedBodyRef.current = serverBody;
    } else {
      // Server matched what we based our edits on. Keep the draft reference; only the
      // baseline advances. If the user kept typing, those edits stay dirty.
      savedBodyRef.current = pendingOps.length ? serverBody : draftBodyRef.current;
    }
    if (typeof newRev === 'number') revRef.current = newRev;
    setIsDirty(pendingOps.length > 0 || !!notesDirty);
  };

  // P2: saves a *diff* of operations (setCell/insert/delete/move by stable row id) against
  // the last-confirmed body, so concurrent edits to different cells/rows merge instead of
  // clobbering. The server returns the merged body, which we reconcile via adoptServerBody.
  // Format changes (and the first save before a baseline exists) can't be expressed as ops
  // — they fall back to the legacy whole-body PATCH, which the server still accepts during
  // the cutover grace period.
  const saveBody = async (manual = false) => {
    if (!selected || !draftBody) return;
    // Permission MUST mirror canEditBody (and the backend gate): any Writer may save any
    // script (team policy), not only its owner. The previous guard omitted plain 'Writer',
    // so a non-owner writer could edit the grid but every save silently no-op'd — a silent
    // data-loss path. Keep this in sync with canEditBody.
    const isShootLeadEditing = location.state?.isShootLeadForScript && (hasRole('Producer'));
    const mayEdit = hasRole('Admin', 'Manager', 'Power User', 'Qual Reviewer', 'Writer')
                 || selected?.writer?.id === user?.id || isShootLeadEditing;
    if (!mayEdit) return;
    if (saveRetryTimer.current) { clearTimeout(saveRetryTimer.current); saveRetryTimer.current = null; }
    // Snapshot exactly what we're sending so the "confirmed" mark is for THIS content,
    // even if the user keeps typing while the request is in flight.
    const bodyAtSave   = draftBody;
    const notesAtSave  = draftNotes;
    const baseBody     = savedBodyRef.current;
    const notesChanged = notesAtSave !== savedNotesRef.current;
    const useLegacy    = !baseBody || baseBody.format !== bodyAtSave.format;

    if (!useLegacy) {
      const ops = diffToOps(baseBody, bodyAtSave);
      if (ops.length === 0 && !notesChanged) return; // nothing to persist
      setSaving(true);
      try {
        const res = await api.patch(`/api/scripts/${selected.id}/ops`, {
          baseRev: revRef.current,
          ops,
          ...(notesChanged && { qualNotes: notesAtSave }),
          saveType: manual ? 'manual' : 'autosave',
        });
        setLastSaved(new Date());
        setSaveError(null);
        saveRetryRef.current = 0;
        if (notesChanged) savedNotesRef.current = notesAtSave;
        adoptServerBody(res.data.scriptBody || bodyAtSave, bodyAtSave, res.data.rev,
                        draftNotesRef.current !== savedNotesRef.current);
        if (bodyAtSave?.format === 'Longform') setLfEpisodesVersion(v => v + 1);
        // Same-cell collisions: another editor changed a cell you also changed. LWW is
        // already applied; surface it so the rare silent same-cell loss is visible.
        const sameCell = (res.data.conflicts || []).filter(c => c.type === 'sameCell');
        if (sameCell.length) setCellConflicts(prev => [...prev.slice(-19), ...sameCell]);
        if (manual && activityOpen) {
          const logRes = await api.get(`/api/audit/script/${selected.id}`);
          setActivityLog(logRes.data);
        }
      } catch (err) { handleSaveError(err, manual); }
      finally { setSaving(false); }
      return;
    }

    // Legacy whole-body save (format change / no baseline yet).
    setSaving(true);
    try {
      const res = await api.patch(`/api/scripts/${selected.id}`, {
        scriptBody: bodyAtSave,
        qualNotes:  notesAtSave,
        ...(manual && { saveType: 'manual' }),
      });
      setLastSaved(new Date());
      setSaveError(null);
      saveRetryRef.current  = 0;
      savedBodyRef.current  = bodyAtSave;
      savedNotesRef.current = notesAtSave;
      if (typeof res.data?.rev === 'number') revRef.current = res.data.rev;
      setIsDirty(draftBodyRef.current !== bodyAtSave || draftNotesRef.current !== notesAtSave);
      if (manual && activityOpen) {
        const logRes = await api.get(`/api/audit/script/${selected.id}`);
        setActivityLog(logRes.data);
      }
    } catch (err) { handleSaveError(err, manual); }
    finally { setSaving(false); }
  };

  // Force-apply a save the shrink guard blocked. Only reachable from an explicit
  // user click on "Save anyway", so confirmShrink is intentional.
  const saveAnyway = async () => {
    if (!selected || !draftBody) return;
    const bodyAtSave   = draftBody;
    const notesAtSave  = draftNotes;
    const baseBody     = savedBodyRef.current;
    const notesChanged = notesAtSave !== savedNotesRef.current;
    const useLegacy    = !baseBody || baseBody.format !== bodyAtSave.format;
    setSaving(true);
    try {
      if (useLegacy) {
        const res = await api.patch(`/api/scripts/${selected.id}`, {
          scriptBody: bodyAtSave, qualNotes: notesAtSave, saveType: 'manual', confirmShrink: true,
        });
        savedBodyRef.current  = bodyAtSave;
        savedNotesRef.current = notesAtSave;
        if (typeof res.data?.rev === 'number') revRef.current = res.data.rev;
        setIsDirty(draftBodyRef.current !== bodyAtSave || draftNotesRef.current !== notesAtSave);
      } else {
        const ops = diffToOps(baseBody, bodyAtSave);
        const res = await api.patch(`/api/scripts/${selected.id}/ops`, {
          baseRev: revRef.current, ops,
          ...(notesChanged && { qualNotes: notesAtSave }),
          saveType: 'manual', confirmShrink: true,
        });
        if (notesChanged) savedNotesRef.current = notesAtSave;
        adoptServerBody(res.data.scriptBody || bodyAtSave, bodyAtSave, res.data.rev,
                        draftNotesRef.current !== savedNotesRef.current);
      }
      if (bodyAtSave?.format === 'Longform') setLfEpisodesVersion(v => v + 1);
      setLastSaved(new Date());
      setSaveError(null);
    } catch (err) {
      console.error('Save failed:', err);
      setSaveError({ tooLarge: err?.response?.status === 413 });
    } finally {
      setSaving(false);
    }
  };

  const pushUndo = (body) => {
    if (!body) return;
    const stack = undoStack.current;
    // setDraftBody always creates a new reference, so referential equality is the
    // correct dedup signal. (Previous version JSON.stringify'd both bodies — for a
    // 500-row script that's two ~250 KB serializations per call, called on every
    // addRow / paste / drag-reposition.)
    if (stack.length > 0 && stack[stack.length - 1] === body) return;
    undoStack.current = [...stack.slice(-49), body];
    // Any new mutation invalidates the redo branch — standard editor semantics.
    if (redoStack.current.length) redoStack.current = [];
  };

  // Keep refs current so the document keydown handler always calls the latest version.
  handleUndoRef.current = () => {
    if (undoStack.current.length === 0) return;
    const prev = undoStack.current[undoStack.current.length - 1];
    undoStack.current = undoStack.current.slice(0, -1);
    if (draftBody) redoStack.current = [...redoStack.current.slice(-49), draftBody];
    setDraftBody(prev);
  };
  handleRedoRef.current = () => {
    if (redoStack.current.length === 0) return;
    const next = redoStack.current[redoStack.current.length - 1];
    redoStack.current = redoStack.current.slice(0, -1);
    if (draftBody) undoStack.current = [...undoStack.current.slice(-49), draftBody];
    setDraftBody(next);
  };

  const updateScene = (idx, key, value) => {
    const scenes = [...(draftBody?.scenes || [])];
    scenes[idx] = { ...scenes[idx], [key]: value };
    setDraftBody({ ...draftBody, scenes });
  };

  // Phase B1: cell edit-mode helpers. Always set focusedCell too so the active
  // border (spec 1.2) tracks the editing cell. `mode` is read by the textarea
  // on mount to decide selection placement: 'cursor-end' (default), 'select-all'
  // (next keystroke overwrites), 'cursor-start'.
  const enterEdit = (rowIdx, colKey, mode = 'cursor-end') => {
    const cur = draftBodyRef.current?.scenes?.[rowIdx]?.[colKey] || '';
    setFocusedCell({ rowIdx, colKey });
    setEditingCell({ rowIdx, colKey, mode, originalValue: cur });
    // Entering edit on a single cell ends any multi-cell range — the row's
    // selectedColRange will recompute to '' next render.
    setCellSelection(null);
    if (cellMultiSelection.size) setCellMultiSelection(new Set());
  };
  const exitEdit = () => { setEditingCell(null); };

  // Find/Replace navigation helper: brings the matched cell into edit mode
  // (so it becomes a textarea where text can actually be selected) and sets
  // the selection range to the match. If the cell is already being edited,
  // just updates the selection — doesn't re-capture originalValue and stomp
  // on prior replacements.
  const focusFindMatch = (rowIdx, colKey, start, end) => {
    const cur = editingCellRef.current;
    if (!cur || cur.rowIdx !== rowIdx || cur.colKey !== colKey) {
      enterEdit(rowIdx, colKey, 'cursor-end');
    }
    requestAnimationFrame(() => {
      const el = document.querySelector(`tr[data-row-idx="${rowIdx}"] textarea[data-col="${colKey}"]`);
      if (!el) return;
      try { el.setSelectionRange(start, end); } catch { /* not selectable */ }
    });
  };
  const revertEdit = () => {
    const e = editingCellRef.current;
    if (!e) return;
    const body = draftBodyRef.current;
    if (body) {
      const scenes = [...(body.scenes || [])];
      scenes[e.rowIdx] = { ...scenes[e.rowIdx], [e.colKey]: e.originalValue };
      setDraftBody({ ...body, scenes });
    }
    setEditingCell(null);
  };

  // Longform line flow (D-117 → D-124, 2026-09-18): the row appended by Enter / Ctrl+Enter / Tab at
  // the end of the grid (the Skit editor's row-advance, unchanged) inherits the scene number of the
  // nearest numbered row above — mid-episode. From an EPISODE BREAK row the advance STARTS THE NEXT
  // EPISODE: the new dialogue row is seeded with scene 1 (scene numbers restart per episode, D-123).
  // The footer "Add scene row" and Tab-append go through the same addRow, so they inherit alike.
  // Every other format gets the plain empty row exactly as before.
  const longformInheritedScene = (scenes) => lfe.inheritedSceneForAppend(scenes);
  const addRow = () => {
    pushUndo(draftBody);
    const scenes = draftBody?.scenes || [];
    const row = draftBody?.format === 'Longform'
      ? { ...emptyRow('Longform'), sceneNumber: longformInheritedScene(scenes) }
      : emptyRow(draftBody?.format);
    setDraftBody({ ...draftBody, scenes: [...scenes, row] });
  };
  // Longform scene flow (D-118): append a scene's FIRST dialogue row after the last row —
  // scene # given (or auto-next), its description in Scene Description — and land in the
  // Character cell in the same commit (pendingFocusRef, exactly like Enter-at-end). The
  // row goes through the normal draft → autosave path, so D-110 character sync sees it.
  // D-123: "next scene" = the CURRENT episode's max scene + 1 (the rows after the last break), or 1.
  const longformNextSceneNumber = (scenes) => lfe.nextSceneNumber(scenes);
  const appendSceneRow = (sceneNumber, description = '') => {
    const body = draftBodyRef.current || draftBody;
    if (!body) return;
    pushUndo(body);
    const scenes = body.scenes || [];
    const n = Number.isFinite(Number(sceneNumber)) && Number(sceneNumber) > 0 ? String(Number(sceneNumber)) : String(longformNextSceneNumber(scenes));
    const row = { ...emptyRow('Longform'), sceneNumber: n, scene: String(description || '').trim() };
    pendingFocusRef.current = { rowIdx: scenes.length, colKey: 'character', scroll: true };
    setDraftBody({ ...body, format: 'Longform', scenes: [...scenes, row] });
  };
  // D-126 (2026-09-18): episodes are AUTHORED IN THE SCRIPT. "＋ Add episode" appends an episode header
  // row ("EPISODE n", a direction/open-marker row the parser and lib/longformEpisodes.js both read) and
  // the episode's first row seeded with scene 1, cursor in Character — on a blank series this is
  // Episode 1 ("Start writing"). The episode's card is minted from the script by the server sync
  // (approval / body save while Approved), never by this button.
  const addEpisodeToScript = (title = '') => {
    const body = draftBodyRef.current || draftBody;
    if (!body) return;
    pushUndo(body);
    const scenes = body.scenes || [];
    const n = lfe.episodeBlocks(scenes).length + 1;
    const header = { ...emptyRow('Longform'), kind: 'direction', character: '', line: lfe.episodeHeaderLine(n, title), sceneNumber: '' };
    const first = { ...emptyRow('Longform'), sceneNumber: '1' };
    pendingFocusRef.current = { rowIdx: scenes.length + 1, colKey: 'character', scroll: true };
    setDraftBody({ ...body, format: 'Longform', scenes: [...scenes, header, first] });
  };
  const startWritingScene1 = () => addEpisodeToScript('');
  // Rename an episode IN THE SCRIPT: rewrite its header row ("EPISODE k — Title"), or insert one at the
  // top of its block when the episode was opened by a plain EPISODE BREAK. The card's title follows on save.
  const setEpisodeTitleInScript = (k, title) => {
    const body = draftBodyRef.current || draftBody;
    if (!body) return;
    const scenes = [...(body.scenes || [])];
    const b = lfe.episodeBlocks(scenes)[k - 1];
    if (!b) return;
    pushUndo(body);
    const line = lfe.episodeHeaderLine(k, title);
    if (lfe.isOpenMarker(scenes[b.start])) scenes[b.start] = { ...scenes[b.start], line };
    else scenes.splice(b.start, 0, { ...emptyRow('Longform'), kind: 'direction', character: '', line, sceneNumber: '' });
    setDraftBody({ ...body, format: 'Longform', scenes });
  };
  // "Add scene" in the Scene breakdown saved its breakdown rows server-side and reloaded the
  // script; once the reload has committed, append the scene's first dialogue row.
  const handleLongformSceneAdded = (sceneNumber, description) => {
    const n = Number(sceneNumber);
    pendingSceneAppendRef.current = { scriptId: selected?.id, sceneNumber: Number.isFinite(n) && n > 0 ? n : longformNextSceneNumber(draftBodyRef.current?.scenes), description };
  };

  const insertRow = (afterIdx) => {
    pushUndo(draftBody);
    const scenes = [...(draftBody?.scenes || [])];
    scenes.splice(afterIdx + 1, 0, emptyRow(draftBody?.format));
    setDraftBody({ ...draftBody, scenes });
    // Existing selection indices would shift after the insert — drop them.
    setSelectedRows(new Set());
    setSelectionAnchor(null);
  };

  const deleteRow = (idx) => {
    pushUndo(draftBody);
    const scenes = [...(draftBody?.scenes || [])];
    scenes.splice(idx, 1);
    setDraftBody({ ...draftBody, scenes });
    setHoveredRow(null);
    setSelectedRows(new Set());
    setSelectionAnchor(null);
  };

  const moveRow = (idx, dir, colKey) => {
    const scenes = [...(draftBody?.scenes || [])];
    const target = idx + dir;
    if (target < 0 || target >= scenes.length) return;
    pushUndo(draftBody);
    [scenes[idx], scenes[target]] = [scenes[target], scenes[idx]];
    setDraftBody({ ...draftBody, scenes });
    setHoveredRow(target);
    // Phase 2.7: clear selection after any row move.
    setSelectedRows(new Set());
    setSelectionAnchor(null);
    // After React re-renders, re-focus the same column in the row's new position
    if (colKey) {
      setTimeout(() => {
        const el = document.querySelector(`[data-row="${target}"][data-col="${colKey}"]`);
        if (el) el.focus();
      }, 0);
    }
  };

  const handlePrint = () => {
    const printCols = displayCols(format, showProdNotesCol);
    const allScenes = draftBody?.scenes || [];
    const scenes    = allScenes.filter(row => printCols.some(c => (row[c.key] || '').trim()));
    const esc = s => (s || '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');

    const headerCells = `<th style="width:32px">#</th>` +
      printCols.map((c, ci) => `<th style="width:${renderedColPcts[ci]}%">${esc(c.label)}</th>`).join('');

    const bodyRows = scenes.map((row, i) => {
      const rowBg = row._rowColor ? `background:${row._rowColor};` : '';
      const cells = printCols.map(c => {
        const cellBg = row._cellColors?.[c.key] ? `background:${row._cellColors[c.key]};` : '';
        const val  = renderMarkdown(esc(row[c.key] || '')).replace(/\n/g, '<br>');
        const link = row._links?.[c.key];
        const linkHtml = link?.url ? `<br><a href="${link.url}" style="color:#5499d4;text-decoration:underline;font-size:0.85em">${esc(link.text || link.url)}</a>` : '';
        return `<td style="border:1px solid #ddd;padding:6px 8px;vertical-align:top;line-height:1.5;word-break:break-word;overflow-wrap:break-word;${cellBg}">${val}${linkHtml}</td>`;
      }).join('');
      return `<tr style="break-inside:avoid;${rowBg}">
        <td style="border:1px solid #ddd;padding:6px 8px;text-align:center;color:#888;font-size:0.85em;background:#f8f8f8;width:32px;">${i + 1}</td>
        ${cells}
      </tr>`;
    }).join('');

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
<title>${esc(selected.scriptName)}</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:Arial,sans-serif;font-size:${editorFontSize}px;color:#111;background:#fff;padding:28px 32px}
  h1{font-size:1.4em;font-weight:700;color:#111;padding-bottom:10px;margin-bottom:16px;border-bottom:2px solid #222;letter-spacing:0.01em}
  *{-webkit-print-color-adjust:exact;print-color-adjust:exact}
  table{width:100%;border-collapse:collapse;table-layout:fixed}
  thead tr{background:#f0f0f0}
  th{background:#f0f0f0;color:#333;border:1px solid #ccc;padding:6px 8px;font-size:0.9em;font-weight:600;text-align:left;word-break:break-word;overflow-wrap:break-word}
  td,th{word-break:break-word;overflow-wrap:break-word}
  #print-bar{position:fixed;top:0;left:0;right:0;background:#f5f5f5;border-bottom:1px solid #ddd;padding:8px 20px;display:flex;align-items:center;gap:14px;font-family:Arial,sans-serif;font-size:12px;z-index:999}
  #print-bar button{background:#333;color:#fff;border:none;padding:6px 14px;border-radius:4px;font-size:12px;cursor:pointer}
  #print-bar span{color:#888}
  #content{margin-top:48px}
  @media print{#print-bar{display:none!important}#content{margin-top:0}}
</style>
</head><body>
<div id="print-bar">
  <button onclick="window.print()">&#9113; Print / Save as PDF</button>
  <span>or press Ctrl+P</span>
</div>
<div id="content">
<h1>${esc(selected.scriptName)}</h1>
${printCols.length > 0 ? `<table><thead><tr>${headerCells}</tr></thead><tbody>${bodyRows}</tbody></table>` : '<p style="color:#666;font-size:12px">No scene table for this script format.</p>'}
</div>
</body></html>`;

    const w = window.open('', '_blank', 'width=900,height=700');
    if (!w) { alert('Pop-up blocked — please allow pop-ups for this site and try again.'); return; }
    w.document.write(html);
    w.document.close();
  };

  // Picks the row targets for a color operation: if the clicked row is part
  // of the active multi-row selection, color every selected row; otherwise
  // just the clicked row.
  const rowColorTargets = (idx) =>
    (selectedRows.has(idx) && selectedRows.size > 1) ? [...selectedRows] : [idx];

  const applyRowColorToIndices = (indices, color) => {
    if (!indices.length) return;
    pushUndo(draftBody);
    const scenes = [...(draftBody?.scenes || [])];
    for (const i of indices) {
      if (scenes[i]) scenes[i] = { ...scenes[i], _rowColor: color };
    }
    setDraftBody({ ...draftBody, scenes });
    setColorPickerRow(null);
  };

  const updateRowColor = (idx, color) => {
    applyRowColorToIndices(rowColorTargets(idx), color);
  };

  // Picks the cell targets for a color operation:
  //   - If the clicked cell is part of the non-contiguous cellMultiSelection,
  //     color every cell in that set.
  //   - Else if the active rectangular cellSelection (Phase B2) covers the
  //     clicked cell and isn't a 1x1, color every cell in the rectangle.
  //   - Else just the clicked cell.
  const cellColorTargets = (idx, colKey) => {
    const colIdx = cols.findIndex(c => c.key === colKey);
    const clickedId = `${idx}:${colKey}`;
    if (cellMultiSelection.size > 0 && cellMultiSelection.has(clickedId)) {
      return [...cellMultiSelection].map(id => {
        const sep = id.indexOf(':');
        return { rowIdx: Number(id.slice(0, sep)), colKey: id.slice(sep + 1) };
      });
    }
    const cs = cellSelection;
    if (cs && (cs.startRow !== cs.endRow || cs.startCol !== cs.endCol)) {
      const r1 = Math.min(cs.startRow, cs.endRow);
      const r2 = Math.max(cs.startRow, cs.endRow);
      const c1 = Math.min(cs.startCol, cs.endCol);
      const c2 = Math.max(cs.startCol, cs.endCol);
      if (idx >= r1 && idx <= r2 && colIdx >= c1 && colIdx <= c2) {
        const out = [];
        for (let r = r1; r <= r2; r++) {
          for (let c = c1; c <= c2; c++) out.push({ rowIdx: r, colKey: cols[c].key });
        }
        return out;
      }
    }
    return [{ rowIdx: idx, colKey }];
  };

  const applyCellColorToTargets = (targets, color) => {
    if (!targets.length) return;
    pushUndo(draftBody);
    const scenes = [...(draftBody?.scenes || [])];
    const byRow = new Map();
    for (const t of targets) {
      if (!byRow.has(t.rowIdx)) byRow.set(t.rowIdx, []);
      byRow.get(t.rowIdx).push(t.colKey);
    }
    for (const [rowIdx, keys] of byRow) {
      if (!scenes[rowIdx]) continue;
      const cc = { ...(scenes[rowIdx]._cellColors || {}) };
      for (const k of keys) cc[k] = color;
      scenes[rowIdx] = { ...scenes[rowIdx], _cellColors: cc };
    }
    setDraftBody({ ...draftBody, scenes });
    setColorPickerCell(null);
  };

  const updateCellColor = (idx, colKey, color) => {
    applyCellColorToTargets(cellColorTargets(idx, colKey), color);
  };

  const toggleRowSelect = (idx) => {
    setSelectedRows(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };

  // Phase 2 selection helpers — gutter-driven row selection (Google-Sheets style).
  const clearRowSelection = () => {
    setSelectedRows(new Set());
    setSelectionAnchor(null);
  };

  const selectRowOnly = (idx) => {
    setSelectedRows(new Set([idx]));
    setSelectionAnchor(idx);
  };

  const selectRowRange = (anchorIdx, idx) => {
    if (anchorIdx == null) { selectRowOnly(idx); return; }
    const [lo, hi] = anchorIdx < idx ? [anchorIdx, idx] : [idx, anchorIdx];
    const next = new Set();
    for (let i = lo; i <= hi; i++) next.add(i);
    setSelectedRows(next);
    // Anchor stays at original anchor — matches Sheets shift-click behavior
  };

  const toggleRowInSelection = (idx) => {
    setSelectedRows(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
    setSelectionAnchor(idx);
  };

  // Phase 4: row clipboard helpers (copy / cut / paste / duplicate).
  const cloneRows = (indices) =>
    indices.map(i => JSON.parse(JSON.stringify(draftBody.scenes[i])));

  const flashCopyFeedback = (msg, ms = 1500) => {
    setCopyFeedback(msg);
    setTimeout(() => setCopyFeedback(prev => (prev === msg ? null : prev)), ms);
  };

  const copySelectedRows = () => {
    if (selectedRows.size === 0) return;
    const sorted = [...selectedRows].sort((a, b) => a - b);
    rowClipboardRef.current = { rows: cloneRows(sorted), wasCut: false };
    setCutRows(new Set());
    writeRowsToAppClip(sorted);   // keep the cross-script app clipboard in sync
    flashCopyFeedback(`Copied ${sorted.length} row${sorted.length !== 1 ? 's' : ''}`);
  };

  const cutSelectedRows = () => {
    if (selectedRows.size === 0) return;
    const sorted = [...selectedRows].sort((a, b) => a - b);
    rowClipboardRef.current = { rows: cloneRows(sorted), wasCut: true, cutIndices: sorted };
    setCutRows(new Set(sorted));
    // Clear the selection so the user can pick a paste destination.
    setSelectedRows(new Set());
    setSelectionAnchor(null);
    flashCopyFeedback(`Cut ${sorted.length} row${sorted.length !== 1 ? 's' : ''} — paste to move`, 2200);
  };

  const pasteRows = () => {
    const clip = rowClipboardRef.current;
    if (!clip || !clip.rows.length) return;
    pushUndo(draftBody);
    const scenes = [...(draftBody?.scenes || [])];
    // Destination: above the first selected row, or end of script if no selection.
    let insertIdx = selectedRows.size > 0 ? Math.min(...selectedRows) : scenes.length;

    if (clip.wasCut && Array.isArray(clip.cutIndices)) {
      // Remove cut rows from their original positions (highest first to avoid index shift).
      const cutSorted = [...clip.cutIndices].sort((a, b) => b - a);
      for (const i of cutSorted) scenes.splice(i, 1);
      // Adjust insertIdx for any cut rows that were before the destination.
      const cutsBefore = clip.cutIndices.filter(i => i < insertIdx).length;
      insertIdx -= cutsBefore;
      if (insertIdx < 0) insertIdx = 0;
      if (insertIdx > scenes.length) insertIdx = scenes.length;
    }

    // Clone again so repeated pastes (copy mode) don't share references.
    const fresh = clip.rows.map(r => JSON.parse(JSON.stringify(r)));
    scenes.splice(insertIdx, 0, ...fresh);
    setDraftBody({ ...draftBody, scenes });

    // Newly inserted rows become the selection so the user can see where they landed.
    const newSelection = new Set();
    for (let i = 0; i < fresh.length; i++) newSelection.add(insertIdx + i);
    setSelectedRows(newSelection);
    setSelectionAnchor(insertIdx);

    if (clip.wasCut) {
      rowClipboardRef.current = null;
      setCutRows(new Set());
    }
  };

  const duplicateSelectedRows = () => {
    if (selectedRows.size === 0) return;
    pushUndo(draftBody);
    const sorted = [...selectedRows].sort((a, b) => a - b);
    const dupes = cloneRows(sorted);
    const insertIdx = sorted[sorted.length - 1] + 1;
    const scenes = [...(draftBody?.scenes || [])];
    scenes.splice(insertIdx, 0, ...dupes);
    setDraftBody({ ...draftBody, scenes });
    const newSelection = new Set();
    for (let i = 0; i < dupes.length; i++) newSelection.add(insertIdx + i);
    setSelectedRows(newSelection);
    setSelectionAnchor(insertIdx);
  };

  // Phase 5 helpers — context-menu actions operate on explicit indices instead of
  // current selection state (avoids stale-state issues when right-clicking outside
  // the existing selection).
  // D-094 (Longform): tag rows as DIRECTION (gold; never a character; Scene Scrub
  // treats them as non-speaking) or back to dialogue. A direction row keeps its
  // text in Line and drops the Character.
  const setRowsKind = (indices, kind) => {
    pushUndo(draftBody);
    const scenes = [...(draftBody?.scenes || [])];
    for (const i of indices) {
      if (!scenes[i]) continue;
      const { kind: _k, ...rest } = scenes[i];
      scenes[i] = kind ? { ...rest, kind, character: '' } : rest;
    }
    setDraftBody({ ...draftBody, scenes });
  };
  const insertRowAt = (idx, count = 1) => {
    pushUndo(draftBody);
    const scenes = [...(draftBody?.scenes || [])];
    const safeIdx = Math.max(0, Math.min(idx, scenes.length));
    const blanks = Array.from({ length: Math.max(1, count) }, () => emptyRow(draftBody?.format));
    scenes.splice(safeIdx, 0, ...blanks);
    setDraftBody({ ...draftBody, scenes });
    setSelectedRows(new Set());
    setSelectionAnchor(null);
  };
  // D-112 (Longform): insert an EPISODE BREAK direction row at `idx` (end of the
  // script when idx is past the end). The row is exactly what the PDF/paste parser
  // emits and what /episodes/from-breaks recognises: kind 'direction', line
  // "EPISODE BREAK", scene number = the scene it closes (the row just above).
  const insertEpisodeBreakAt = (idx) => {
    pushUndo(draftBody);
    const scenes = [...(draftBody?.scenes || [])];
    const safeIdx = Math.max(0, Math.min(idx == null ? scenes.length : idx, scenes.length));
    const above = [...scenes.slice(0, safeIdx)].reverse().find(r => String(r?.sceneNumber ?? '').trim() !== '');
    const row = { ...emptyRow(draftBody?.format), kind: 'direction', character: '', line: 'EPISODE BREAK', sceneNumber: above ? String(above.sceneNumber).trim() : '' };
    scenes.splice(safeIdx, 0, row);
    setDraftBody({ ...draftBody, scenes });
    setSelectedRows(new Set());
    setSelectionAnchor(null);
  };

  const deleteRowsByIndices = (indices) => {
    if (!indices.length) return;
    pushUndo(draftBody);
    const scenes = [...(draftBody?.scenes || [])];
    const sortedDesc = [...indices].sort((a, b) => b - a);
    for (const i of sortedDesc) scenes.splice(i, 1);
    // Keep at least one row so the editor remains usable.
    if (scenes.length === 0) scenes.push(emptyRow(draftBody?.format));
    setDraftBody({ ...draftBody, scenes });
    setSelectedRows(new Set());
    setSelectionAnchor(null);
    setHoveredRow(null);
    setCutRows(new Set());
  };

  const duplicateRowsByIndices = (indices) => {
    if (!indices.length) return;
    pushUndo(draftBody);
    const sorted = [...indices].sort((a, b) => a - b);
    const dupes = sorted.map(i => JSON.parse(JSON.stringify(draftBody.scenes[i])));
    const insertIdx = sorted[sorted.length - 1] + 1;
    const scenes = [...(draftBody?.scenes || [])];
    scenes.splice(insertIdx, 0, ...dupes);
    setDraftBody({ ...draftBody, scenes });
    const newSelection = new Set();
    for (let i = 0; i < dupes.length; i++) newSelection.add(insertIdx + i);
    setSelectedRows(newSelection);
    setSelectionAnchor(insertIdx);
  };

  const copyRowsByIndices = (indices) => {
    if (!indices.length) return;
    const sorted = [...indices].sort((a, b) => a - b);
    rowClipboardRef.current = {
      rows: sorted.map(i => JSON.parse(JSON.stringify(draftBody.scenes[i]))),
      wasCut: false,
    };
    setCutRows(new Set());
    writeRowsToAppClip(sorted);   // keep the cross-script app clipboard in sync
    flashCopyFeedback(`Copied ${sorted.length} row${sorted.length !== 1 ? 's' : ''}`);
  };

  const cutRowsByIndices = (indices) => {
    if (!indices.length) return;
    const sorted = [...indices].sort((a, b) => a - b);
    rowClipboardRef.current = {
      rows: sorted.map(i => JSON.parse(JSON.stringify(draftBody.scenes[i]))),
      wasCut: true,
      cutIndices: sorted,
    };
    setCutRows(new Set(sorted));
    setSelectedRows(new Set());
    setSelectionAnchor(null);
    flashCopyFeedback(`Cut ${sorted.length} row${sorted.length !== 1 ? 's' : ''} — paste to move`, 2200);
  };

  // Paste from the right-click menu: insert above the highest-priority target row.
  const pasteAboveRow = (idx) => {
    const clip = rowClipboardRef.current;
    if (!clip || !clip.rows.length) return;
    pushUndo(draftBody);
    const scenes = [...(draftBody?.scenes || [])];
    let insertIdx = idx;
    if (clip.wasCut && Array.isArray(clip.cutIndices)) {
      const cutSorted = [...clip.cutIndices].sort((a, b) => b - a);
      for (const i of cutSorted) scenes.splice(i, 1);
      const cutsBefore = clip.cutIndices.filter(i => i < insertIdx).length;
      insertIdx -= cutsBefore;
      if (insertIdx < 0) insertIdx = 0;
      if (insertIdx > scenes.length) insertIdx = scenes.length;
    }
    const fresh = clip.rows.map(r => JSON.parse(JSON.stringify(r)));
    scenes.splice(insertIdx, 0, ...fresh);
    setDraftBody({ ...draftBody, scenes });
    const newSelection = new Set();
    for (let i = 0; i < fresh.length; i++) newSelection.add(insertIdx + i);
    setSelectedRows(newSelection);
    setSelectionAnchor(insertIdx);
    if (clip.wasCut) {
      rowClipboardRef.current = null;
      setCutRows(new Set());
    }
  };

  // Phase 3: move the currently selected rows to a new position.
  // targetGapIdx is a value in [0..scenes.length]; rows are inserted at that gap,
  // becoming contiguous regardless of whether sources were contiguous.
  const repositionSelectedTo = (targetGapIdx) => {
    if (selectedRows.size === 0) return;
    const scenes = [...(draftBody?.scenes || [])];
    const sortedSources = [...selectedRows].sort((a, b) => a - b);
    // No-op if dropping into the gap that's already where the (contiguous) block sits.
    const firstSrc = sortedSources[0];
    const lastSrc  = sortedSources[sortedSources.length - 1];
    const contiguous = lastSrc - firstSrc + 1 === sortedSources.length;
    if (contiguous && (targetGapIdx === firstSrc || targetGapIdx === lastSrc + 1)) {
      setSelectedRows(new Set());
      setSelectionAnchor(null);
      return;
    }
    pushUndo(draftBody);
    // Snapshot the source rows in their current order, then remove from highest idx down.
    const moved = sortedSources.map(i => scenes[i]);
    for (let i = sortedSources.length - 1; i >= 0; i--) {
      scenes.splice(sortedSources[i], 1);
    }
    // Shift the target left by however many source rows were before it.
    const sourcesBefore = sortedSources.filter(i => i < targetGapIdx).length;
    const adjustedTarget = targetGapIdx - sourcesBefore;
    scenes.splice(adjustedTarget, 0, ...moved);
    setDraftBody({ ...draftBody, scenes });
    // Spec 2.6 / 2.7: selection clears immediately after the drop.
    setSelectedRows(new Set());
    setSelectionAnchor(null);
  };

  // ── Drag-select edge auto-scroll ─────────────────────────────────────────
  // ONE shared implementation used by BOTH selection drags (gutter row drag
  // and cell range drag). While a drag is live, every mousemove feeds the last
  // pointer position here; a rAF loop scrolls `.script-scroll-body` whenever
  // that position sits in an edge hot zone, then re-resolves the row/cell now
  // under the stationary pointer and extends the selection (mousemove does NOT
  // fire when only the page scrolls, so the loop must do the re-resolving).
  // Move/fill drags (reposition, autofill, move-drag) never call this.
  const autoScrollRef = useRef(null); // { raf, lastX, lastY, horizontal, resolveAt, teardown } | null

  const stopEdgeAutoScroll = () => {
    const s = autoScrollRef.current;
    if (!s) return;
    autoScrollRef.current = null;
    if (s.raf != null) cancelAnimationFrame(s.raf);
    s.teardown();
  };

  // Called from a selection drag's mousemove. Starts the rAF loop on first call
  // and refreshes the stored pointer position on every subsequent one. Stopping
  // is handled by mouseup (in each drag's own teardown) plus Escape, window
  // blur, and the pointer leaving the document; a later in-document mousemove
  // restarts the loop if the drag is still live.
  const ensureEdgeAutoScroll = (x, y, { horizontal = false, resolveAt }) => {
    const existing = autoScrollRef.current;
    if (existing) {
      existing.lastX = x; existing.lastY = y; existing.resolveAt = resolveAt;
      return;
    }
    const scroller = document.querySelector('.script-scroll-body');
    if (!scroller) return;
    const state = { raf: null, lastX: x, lastY: y, horizontal, resolveAt, teardown: () => {} };

    const onKey = (e) => { if (e.key === 'Escape') stopEdgeAutoScroll(); };
    const onBlur = () => stopEdgeAutoScroll();
    const onDocLeave = () => stopEdgeAutoScroll();
    document.addEventListener('keydown', onKey, true);
    window.addEventListener('blur', onBlur);
    document.documentElement.addEventListener('mouseleave', onDocLeave);
    state.teardown = () => {
      document.removeEventListener('keydown', onKey, true);
      window.removeEventListener('blur', onBlur);
      document.documentElement.removeEventListener('mouseleave', onDocLeave);
    };

    const step = () => {
      if (autoScrollRef.current !== state) return; // stopped or replaced
      const rect = scroller.getBoundingClientRect();
      // The sticky column header overlays the top of the scroll area — the top
      // zone starts below it, over rows the user can actually see.
      const headH = scroller.querySelector('.scene-head')?.getBoundingClientRect().height || 0;
      const topEdge = rect.top + headH;
      let dy = 0, dx = 0;
      if (state.lastY > rect.bottom - AUTOSCROLL_EDGE_PX) {
        dy = Math.min(AUTOSCROLL_MAX_STEP_PX,
          ((state.lastY - (rect.bottom - AUTOSCROLL_EDGE_PX)) / AUTOSCROLL_EDGE_PX) * AUTOSCROLL_MAX_STEP_PX);
      } else if (state.lastY < topEdge + AUTOSCROLL_EDGE_PX) {
        dy = -Math.min(AUTOSCROLL_MAX_STEP_PX,
          (((topEdge + AUTOSCROLL_EDGE_PX) - state.lastY) / AUTOSCROLL_EDGE_PX) * AUTOSCROLL_MAX_STEP_PX);
      }
      // Horizontal zones: cell-range drags only, and only when the container
      // actually scrolls sideways (today it does not — columns are %-width).
      if (state.horizontal && scroller.scrollWidth > scroller.clientWidth) {
        if (state.lastX > rect.right - AUTOSCROLL_EDGE_PX) {
          dx = Math.min(AUTOSCROLL_MAX_STEP_PX,
            ((state.lastX - (rect.right - AUTOSCROLL_EDGE_PX)) / AUTOSCROLL_EDGE_PX) * AUTOSCROLL_MAX_STEP_PX);
        } else if (state.lastX < rect.left + AUTOSCROLL_EDGE_PX) {
          dx = -Math.min(AUTOSCROLL_MAX_STEP_PX,
            (((rect.left + AUTOSCROLL_EDGE_PX) - state.lastX) / AUTOSCROLL_EDGE_PX) * AUTOSCROLL_MAX_STEP_PX);
        }
      }
      if (dy || dx) {
        const beforeTop = scroller.scrollTop, beforeLeft = scroller.scrollLeft;
        if (dy) scroller.scrollTop = beforeTop + dy;
        if (dx) scroller.scrollLeft = beforeLeft + dx;
        // Re-resolve only when the view actually moved (not clamped at either
        // end) — that is when different rows sit under the stationary pointer.
        if (scroller.scrollTop !== beforeTop || scroller.scrollLeft !== beforeLeft) {
          state.resolveAt(state.lastX, state.lastY, { rect, topEdge });
        }
      }
      state.raf = requestAnimationFrame(step);
    };
    autoScrollRef.current = state;
    state.raf = requestAnimationFrame(step);
  };

  // Resolve + extend for a live GUTTER row drag. From the rAF loop the point is
  // clamped into the scroller and aimed at the gutter column's x (in the edge
  // zones the pointer may sit over cells or outside the table entirely; the
  // drag extends by row, so only the y position matters). Guarded by lastHover
  // so a 400-row script doesn't re-render once per frame.
  const resolveGutterRowAt = (x, y, geo) => {
    const ds = dragSelectRef.current;
    if (!ds.active) return;
    let px = x, py = y;
    if (geo) {
      ({ x: px, y: py } = clampToScrollerRect(x, y, geo.rect, geo.topEdge));
      const gutterTd = document.querySelector('.script-scroll-body td.scene-num');
      if (gutterTd) {
        const gr = gutterTd.getBoundingClientRect();
        px = gr.left + gr.width / 2;
      }
    }
    const el = document.elementFromPoint(px, py);
    const tr = el?.closest('tr.scene-row');
    const rowIdxStr = tr?.getAttribute('data-row-idx');
    if (rowIdxStr == null) return;
    const hover = Number(rowIdxStr);
    if (Number.isNaN(hover) || hover === ds.lastHover) return;
    ds.lastHover = hover;
    selectRowRange(ds.anchorIdx, hover);
  };

  // Resolve + extend for a live CELL range drag (also called from the permanent
  // document-level mousemove so fast moves and out-of-table tracking work).
  // Guarded by cellDragLastRef against per-frame re-renders.
  const resolveCellRangeAt = (x, y, geo) => {
    if (!draggingRef.current) return;
    let px = x, py = y;
    if (geo) ({ x: px, y: py } = clampToScrollerRect(x, y, geo.rect, geo.topEdge));
    const el = document.elementFromPoint(px, py);
    const td = el?.closest?.('td.scene-td');
    if (!td || td.classList.contains('scene-num')) return;
    const rowIdx = Number(td.parentElement?.dataset?.rowIdx);
    if (!Number.isFinite(rowIdx)) return;
    const colKey = td.querySelector?.('[data-col]')?.dataset?.col;
    if (!colKey || colKeyToIdx[colKey] == null) return;
    const key = `${rowIdx}:${colKey}`;
    if (cellDragLastRef.current === key) return;
    cellDragLastRef.current = key;
    updateCellDrag(rowIdx, colKey);
  };

  // Document-level mousemove entry point for cell drags (wired in the permanent
  // drag-tracking effect via handlersLatestRef).
  const cellDragPointerMove = (x, y) => {
    resolveCellRangeAt(x, y, null);
    ensureEdgeAutoScroll(x, y, { horizontal: true, resolveAt: resolveCellRangeAt });
  };

  // mousedown on a gutter cell. Decides immediately for shift/ctrl, defers drag detection to mousemove.
  const handleGutterMouseDown = (idx, e) => {
    // Let the existing inline buttons (+/×/↑/↓ etc.) handle their own events.
    // Their handlers stopPropagation, so this fires only on clicks on the gutter background.
    if (isReadOnly) return;
    if (e.button !== 0) return; // left-click only

    // Mutually exclusive with cell selection — clear any active cell range so a
    // row copy/paste reads the selected rows, not a leftover cell selection.
    setSelectionMode('rows');
    if (cellSelection) setCellSelection(null);
    if (cellMultiSelection.size) setCellMultiSelection(new Set());

    if (e.shiftKey) {
      e.preventDefault();
      selectRowRange(selectionAnchor, idx);
      return;
    }
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      toggleRowInSelection(idx);
      return;
    }
    // Phase 3: mousedown on a row that's already selected starts a drag-to-reposition
    // gesture (past a 5px threshold). A plain click without drag-movement reduces the
    // selection to this single row (standard spreadsheet behavior).
    if (selectedRows.has(idx) && selectedRows.size > 0) {
      e.preventDefault(); // prevent text-selection drag in adjacent textareas
      repositionRef.current = { active: false, startX: e.clientX, startY: e.clientY };
      let currentDrop = null;

      const onMove = (ev) => {
        const rs = repositionRef.current;
        if (!rs.active) {
          const dx = ev.clientX - rs.startX;
          const dy = ev.clientY - rs.startY;
          if (dx * dx + dy * dy < 25) return; // 5px threshold
          rs.active = true;
          setDragPreview({ x: ev.clientX, y: ev.clientY, count: selectedRows.size });
        } else {
          setDragPreview(prev => prev ? { ...prev, x: ev.clientX, y: ev.clientY } : prev);
        }
        // Figure out which gap the cursor is closest to.
        // elementFromPoint finds the row under the cursor; cursor-Y vs row mid-line
        // decides above-vs-below.
        const el = document.elementFromPoint(ev.clientX, ev.clientY);
        const tr = el?.closest('tr.scene-row');
        if (tr) {
          const rect = tr.getBoundingClientRect();
          const rowIdxStr = tr.getAttribute('data-row-idx');
          const rowIdx = rowIdxStr != null ? Number(rowIdxStr) : null;
          if (rowIdx != null && !Number.isNaN(rowIdx)) {
            const mid = rect.top + rect.height / 2;
            const gap = ev.clientY < mid ? rowIdx : rowIdx + 1;
            currentDrop = gap;
            setDropTargetIdx(gap);
          }
        }
      };

      const onUp = () => {
        const rs = repositionRef.current;
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        if (rs.active && currentDrop != null) {
          repositionSelectedTo(currentDrop);
        } else if (!rs.active) {
          // No drag movement — reduce multi-selection to this single row.
          selectRowOnly(idx);
        }
        setDropTargetIdx(null);
        setDragPreview(null);
        repositionRef.current = { active: false, startX: 0, startY: 0 };
      };

      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
      return;
    }

    // Plain mousedown — prepare for either a click-select or a drag-select.
    // The drag is only initiated past a movement threshold (handled in mousemove below).
    dragSelectRef.current = {
      active: false,
      anchorIdx: idx,
      startX: e.clientX,
      startY: e.clientY,
      suppressClick: false,
      lastHover: idx,
    };

    const onMove = (ev) => {
      const ds = dragSelectRef.current;
      if (ds.anchorIdx == null) return;
      if (!ds.active) {
        const dx = ev.clientX - ds.startX;
        const dy = ev.clientY - ds.startY;
        if (dx * dx + dy * dy < 25) return; // 5px threshold
        ds.active = true;
        // Begin drag selection — anchor becomes the only selected row initially.
        setSelectedRows(new Set([ds.anchorIdx]));
        setSelectionAnchor(ds.anchorIdx);
      }
      // Resolve the element under the cursor; if it's a gutter cell, extend the range.
      const el = document.elementFromPoint(ev.clientX, ev.clientY);
      const td = el?.closest('td.scene-num');
      const tr = td?.closest('tr.scene-row');
      const rowIdxStr = tr?.getAttribute('data-row-idx');
      if (rowIdxStr != null) {
        const hover = Number(rowIdxStr);
        if (!Number.isNaN(hover) && hover !== ds.lastHover) {
          ds.lastHover = hover;
          selectRowRange(ds.anchorIdx, hover);
        }
      }
      // Edge auto-scroll: scrolls when the pointer sits in a top/bottom hot
      // zone and keeps extending the selection while the view moves.
      ensureEdgeAutoScroll(ev.clientX, ev.clientY, { horizontal: false, resolveAt: resolveGutterRowAt });
    };

    const onUp = () => {
      stopEdgeAutoScroll();
      const ds = dragSelectRef.current;
      if (ds.active) {
        ds.suppressClick = true;
      } else {
        // No drag — treat as a plain click: select this row only.
        selectRowOnly(ds.anchorIdx);
      }
      ds.active = false;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  const deleteSelectedRows = () => {
    pushUndo(draftBody);
    const indices = [...selectedRows].sort((a, b) => b - a);
    const scenes = [...(draftBody?.scenes || [])];
    indices.forEach(i => scenes.splice(i, 1));
    setDraftBody({ ...draftBody, scenes });
    setSelectedRows(new Set());
    setMultiDeleteConfirm(false);
    setHoveredRow(null);
  };

  const moveSelectedBlock = (dir) => {
    if (selectedRows.size === 0) return;
    const sorted = [...selectedRows].sort((a, b) => a - b);
    const scenes = [...(draftBody?.scenes || [])];
    const n = scenes.length;

    // Build contiguous groups from the sorted selection
    const groups = [];
    let gs = sorted[0], ge = sorted[0];
    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i] === ge + 1) { ge = sorted[i]; }
      else { groups.push([gs, ge]); gs = sorted[i]; ge = sorted[i]; }
    }
    groups.push([gs, ge]);

    if (dir === -1) {
      if (sorted[0] === 0) return;
      pushUndo(draftBody);
      // Process groups top→bottom: move the element above each group to below it
      for (const [start, end] of groups) {
        if (start === 0) continue;
        const displaced = scenes.splice(start - 1, 1)[0];
        scenes.splice(end, 0, displaced);
      }
    } else {
      if (sorted[sorted.length - 1] === n - 1) return;
      pushUndo(draftBody);
      // Process groups bottom→top: move the element below each group to above it
      for (let g = groups.length - 1; g >= 0; g--) {
        const [start, end] = groups[g];
        if (end === n - 1) continue;
        const displaced = scenes.splice(end + 1, 1)[0];
        scenes.splice(start, 0, displaced);
      }
    }
    setDraftBody({ ...draftBody, scenes });
    // Phase 2.7: clear selection after any move (single or block).
    setSelectedRows(new Set());
    setSelectionAnchor(null);
  };

  const saveLink = (rowIdx, colKey, text, url) => {
    if (!url.trim()) return;
    const scenes = [...(draftBody?.scenes || [])];
    const _links = { ...(scenes[rowIdx]._links || {}), [colKey]: { text: text.trim() || url.trim(), url: url.trim() } };
    scenes[rowIdx] = { ...scenes[rowIdx], _links };
    setDraftBody({ ...draftBody, scenes });
    setLinkPopover(null);
  };

  const removeLink = (rowIdx, colKey) => {
    const scenes = [...(draftBody?.scenes || [])];
    const _links = { ...(scenes[rowIdx]._links || {}) };
    delete _links[colKey];
    scenes[rowIdx] = { ...scenes[rowIdx], _links };
    setDraftBody({ ...draftBody, scenes });
  };

  const handleExportDocx = () => {
    const printCols = displayCols(format, showProdNotesCol);
    const allScenes = draftBody?.scenes || [];
    const scenes    = allScenes.filter(row => printCols.some(c => (row[c.key] || '').trim()));
    const esc = s => (s || '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');

    const headerCells = `<th style="width:40px;background:#f0f0f0;border:1px solid #ccc;padding:6px 8px;font-size:10pt;font-weight:bold;text-align:center">#</th>` +
      printCols.map((c, ci) => `<th style="width:${renderedColPcts[ci]}%;background:#f0f0f0;border:1px solid #ccc;padding:6px 8px;font-size:10pt;font-weight:bold;text-align:left">${esc(c.label)}</th>`).join('');

    const bodyRows = scenes.map((row, i) => {
      const cells = printCols.map(c => {
        let val = renderMarkdown(esc(row[c.key] || '')).replace(/\n/g, '<br>');
        const link = row._links?.[c.key];
        if (link?.url) val += `${val ? '<br>' : ''}<a href="${esc(link.url)}" style="color:#5499d4">${esc(link.text || link.url)}</a>`;
        return `<td style="border:1px solid #ccc;padding:6px 8px;font-size:10pt;vertical-align:top">${val}</td>`;
      }).join('');
      return `<tr><td style="border:1px solid #ccc;padding:6px 8px;text-align:center;color:#888;font-size:9pt">${i + 1}</td>${cells}</tr>`;
    }).join('');

    const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
<head><meta charset="utf-8"><title>${esc(selected.scriptName)}</title>
<style>body{font-family:Arial,sans-serif;font-size:10pt;margin:1in}h1{font-size:16pt;font-weight:bold;margin-bottom:4pt}.sub{font-size:10pt;color:#555;margin-bottom:16pt}table{border-collapse:collapse;width:100%;table-layout:fixed}</style>
</head><body>
<h1>${esc(selected.scriptName)}</h1>
<div class="sub">${esc(selected.client?.name || '')} · ${esc(selected.writer?.name || '')}</div>
<table><thead><tr>${headerCells}</tr></thead><tbody>${bodyRows}</tbody></table>
</body></html>`;

    const blob = new Blob(['\ufeff', html], { type: 'application/msword' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `${(selected.scriptName || 'script').replace(/[^a-z0-9\s-]/gi, '').trim()}.doc`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const applyMarkdown = (marker, rowIdx, colKey) => {
    const el = document.querySelector(`[data-row="${rowIdx}"][data-col="${colKey}"]`);
    if (!el) return;
    const start = el.selectionStart;
    const end   = el.selectionEnd;
    const val   = el.value;
    const sel   = val.slice(start, end);
    let newVal, newStart, newEnd;
    if (start === end) {
      newVal   = val.slice(0, start) + marker + marker + val.slice(end);
      newStart = newEnd = start + marker.length;
    } else if (sel.startsWith(marker) && sel.endsWith(marker) && sel.length > marker.length * 2) {
      const inner = sel.slice(marker.length, -marker.length);
      newVal   = val.slice(0, start) + inner + val.slice(end);
      newStart = start; newEnd = start + inner.length;
    } else {
      newVal   = val.slice(0, start) + marker + sel + marker + val.slice(end);
      newStart = start; newEnd = end + marker.length * 2;
    }
    updateScene(rowIdx, colKey, newVal);
    setTimeout(() => {
      const el2 = document.querySelector(`[data-row="${rowIdx}"][data-col="${colKey}"]`);
      if (el2) el2.setSelectionRange(newStart, newEnd);
    }, 0);
  };

  // Phase B2: format/cols/B2 helpers need to be defined BEFORE handlersLatestRef
  // since `const` is temporally dead until declaration — otherwise the assignment
  // below throws ReferenceError and the page renders blank.
  const format = draftBody?.format;
  // Production Notes column: visible to anyone viewing the script once it has
  // ever been approved (derived server-side as selected.hasBeenApproved), and
  // it stays visible if the script later leaves Approved. Defined here (not with
  // the other gates below) because `cols` must be built before handlersLatestRef.
  const showProdNotesCol = !!selected?.hasBeenApproved;
  // useMemo keeps `cols` referentially stable (displayCols returns a fresh array
  // when prodNotes is spliced in) so SceneRow memoization isn't defeated.
  const cols   = useMemo(() => displayCols(format, showProdNotesCol), [format, showProdNotesCol]);

  // ── Longform hierarchy (D-121, 2026-09-17): GROUPED RENDERING of the flat body — rendering only.
  // The body stays the flat Skit-shaped array; band <tr>s are emitted around the rows:
  //   • a SCENE band before the first row of each contiguous run of one sceneNumber (Scene N + its
  //     Scene Description, row count, chevron);
  //   • an EPISODE band before each EPISODE BREAK direction row ("Episode break", or "Ep N — title"
  //     once an episode whose range starts right after the break exists), plus a leading band for the
  //     first episode when episodes exist.
  // Collapse/expand is UI state only (scene number / break row id); collapsed rows are simply not
  // emitted, so data-row indices, keyboard flow (D-117/D-118), selection and autosave are untouched.
  const [lfCollapsedScenes, setLfCollapsedScenes] = useState(() => new Set());
  const [lfCollapsedEpisodes, setLfCollapsedEpisodes] = useState(() => new Set());
  const [lfEpisodes, setLfEpisodes] = useState([]);
  const lfEpisodesScriptRef = useRef(null);
  // D-126: every successful body save may have minted / renamed / removed episode cards server-side —
  // bump so the series panel (and the bands) re-read the cards.
  const [lfEpisodesVersion, setLfEpisodesVersion] = useState(0);
  const loadLfEpisodes = useCallback(async (scriptId) => {
    if (!scriptId) { setLfEpisodes([]); return; }
    try { const r = await api.get(`/api/longform/scripts/${scriptId}/episodes`); if (lfEpisodesScriptRef.current === scriptId) setLfEpisodes(r.data.episodes || []); } catch { /* bands fall back to "Episode break" */ }
  }, []);
  useEffect(() => {
    const isLf = format === 'Longform' && selected?.id;
    lfEpisodesScriptRef.current = isLf ? selected.id : null;
    if (isLf) loadLfEpisodes(selected.id); else setLfEpisodes([]);
    setLfCollapsedScenes(new Set()); setLfCollapsedEpisodes(new Set());
  }, [selected?.id, format, loadLfEpisodes]);
  // D-123 (2026-09-18): bands are POSITIONAL. lib/longformEpisodes.js splits the flat body into blocks
  // at the EPISODE BREAK / EPISODE n markers (Ep N ≡ the N-th block); the episode band before a marker
  // row is labelled with the episode that block maps to — "Ep N — title · scenes a–b[ · Planned]" with
  // the range read from the block's OWN rows (scene numbers restart at 1 per episode) — or "Episode
  // break" when no such episode exists yet. Scene bands show the row's own (per-episode) scene number;
  // their collapse key includes the block so "Scene 1" of Ep 2 never folds with "Scene 1" of Ep 1.
  const lfGroupPlan = useMemo(() => {
    if (format !== 'Longform') return null;
    const scenes = draftBody?.scenes || [];
    const plan = new Array(scenes.length);
    const blocks = lfe.episodeBlocks(scenes);
    const blockOfRow = new Array(scenes.length).fill(null);
    for (const b of blocks) for (const j of b.rowIndexes) blockOfRow[j] = b;
    const epFor = (blockIndex) => lfEpisodes.find(e => e.episodeNumber === blockIndex) || null;
    // D-129: the band IS the episode break — one band per block, above its first row; the marker rows
    // themselves (EPISODE BREAK / EPISODE n headers) are not emitted. Label: "Ep N · title|untitled ·
    // scenes a–b · Planned | <stage> | no card yet".
    const bandFor = (b) => {
      const ep = epFor(b.index); const title = lfe.blockTitle(scenes, b);
      const range = b.sceneFrom == null ? `${b.rowCount} row${b.rowCount === 1 ? '' : 's'}` : (b.sceneFrom === b.sceneTo ? `scene ${b.sceneFrom}` : `scenes ${b.sceneFrom}–${b.sceneTo}`);
      const state = !ep ? 'no card yet' : (ep.productionActive === false ? 'Planned' : ep.status);
      return { key: `e:${b.index}`, episodeNumber: b.index, title, untitled: !title, range, state, cardId: ep?.id || null, label: `Ep ${b.index} · ${title || 'untitled'} · ${range} · ${state}` };
    };
    let prevScene = null, curEpKey = 'e:0', epCollapsed = false, sceneCollapsed = false;
    for (let i = 0; i < scenes.length; i++) {
      const r = scenes[i]; const p = { episodeBand: null, sceneBand: null, hidden: false, marker: false };
      const b = blockOfRow[i];
      if (b && i === b.start) { const band = bandFor(b); curEpKey = band.key; p.episodeBand = band; epCollapsed = lfCollapsedEpisodes.has(curEpKey); prevScene = null; }
      if (lfe.isBreakMarker(r)) { p.marker = true; p.hidden = true; plan[i] = p; continue; }
      const sk = String(r?.sceneNumber ?? '').trim();
      if (sk !== prevScene) {
        let n = 1; for (let j = i + 1; j < scenes.length && String(scenes[j]?.sceneNumber ?? '').trim() === sk && !lfe.isBreakMarker(scenes[j]); j++) n++;
        const key = `s:${curEpKey}:${sk}`;
        p.sceneBand = { key, sceneNumber: sk, description: String(r?.scene || '').trim(), rows: n, episodeNumber: b?.index ?? null };
        sceneCollapsed = lfCollapsedScenes.has(key); prevScene = sk;
      }
      // A collapsed episode shows only its band — its rows and scene bands are not emitted (the band now sits
      // on the episode's first content row, which also opens a scene, so the scene band must go too).
      if (epCollapsed) { p.hidden = true; p.sceneBand = null; }
      else if (sceneCollapsed) p.hidden = true;
      plan[i] = p;
    }
    return plan;
  }, [format, draftBody, lfEpisodes, lfCollapsedScenes, lfCollapsedEpisodes]);
  // D-129 band actions: rename (writes the header row, D-127) and remove the break that opens episode k
  // (merges it into the episode above: drops its header row and the close marker right before it).
  const [lfBandEditing, setLfBandEditing] = useState(null); // { k, value }
  const removeEpisodeBoundary = (k) => {
    const body = draftBodyRef.current || draftBody;
    if (!body) return;
    const scenes = [...(body.scenes || [])];
    const b = lfe.episodeBlocks(scenes)[k - 1];
    if (!b || k < 2) return;
    pushUndo(body);
    const drop = new Set();
    if (lfe.isOpenMarker(scenes[b.start])) drop.add(b.start);
    for (let j = b.start - 1; j >= 0 && lfe.isBreakMarker(scenes[j]); j--) drop.add(j);
    const next = scenes.filter((_, j) => !drop.has(j));
    setDraftBody({ ...body, format: 'Longform', scenes: next });
    setSelectedRows(new Set()); setSelectionAnchor(null);
  };
  // D-130 (2026-09-18): scene numbers are forced into succession inside each episode. Whatever a row
  // carries only decides "same scene as the row above" or "next scene"; the draft is re-numbered 1, 2, 3…
  // per episode after every change (and on load — a legacy body is corrected and autosaved). Idempotent,
  // so the effect settles in one extra render; row indices never move, only the Scene # strings.
  useEffect(() => {
    if (format !== 'Longform' || !draftBody?.scenes) return;
    const { rows, changed } = lfe.normalizeSceneNumbers(draftBody.scenes);
    if (changed) setDraftBody({ ...draftBody, scenes: rows });
  }, [draftBody, format]); // eslint-disable-line react-hooks/exhaustive-deps
  const lfToggleScene = (key) => setLfCollapsedScenes(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
  const lfToggleEpisode = (key) => setLfCollapsedEpisodes(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });

  // Run 2: the per-client character registry — feeds the Character cell picker
  // and the registry modal opened from the Character column header. Skit and
  // Mockumentary only (the formats with a character column, DEF-083).
  const isCharacterFormat  = format === 'Skit' || format === 'Mockumentary' || format === 'Longform'; // Longform joined 2026-09-15 (D-089)
  const registryClientId   = selected?.client?.id || null;
  const [registryChars, setRegistryChars]         = useState(null);
  const [registryModalOpen, setRegistryModalOpen] = useState(false);
  const loadRegistry = useCallback(async () => {
    if (!registryClientId) return;
    try {
      const r = await api.get(`/api/client-characters?clientId=${registryClientId}`);
      setRegistryChars(r.data.characters || []);
    } catch { setRegistryChars([]); }
  }, [registryClientId]);
  useEffect(() => {
    setRegistryChars(null);
    setRegistryModalOpen(false);
    if (registryClientId && isCharacterFormat) loadRegistry();
  }, [registryClientId, isCharacterFormat, loadRegistry]);
  // Live names only — the picker never offers an archived character.
  const registryNames = useMemo(
    () => (isCharacterFormat && registryChars) ? registryChars.filter(r => !r.isArchived).map(r => r.name) : null,
    [isCharacterFormat, registryChars]);
  // Mirrors the server gates: list curation follows the script-edit roles,
  // talent mapping follows canEditTal (Producer / Power User / Admin).
  const canEditRegistry = hasRole('Admin', 'Manager', 'Power User', 'Qual Reviewer', 'Writer');
  const canMapTalent    = hasRole('Admin', 'Power User', 'Producer');
  const addCharacterToRegistry = async (name) => {
    if (!registryClientId || !name) return;
    try { await api.post('/api/client-characters', { clientId: registryClientId, name }); }
    catch { /* 409 already-present etc. — the refetch below shows the truth */ }
    await loadRegistry();
  };
  // Live spoken-word count (null spokenKey -> None/Riff/unknown -> counter hidden).
  const spokenKey = SPOKEN_COL[format] || null;
  const spokenWordCount = useSpokenWordCount(draftBody, spokenKey);

  // WPM input: accept the keystroke into `wpmInput`, but only promote a valid,
  // clamped number into `wpm` (which drives the division). Non-numeric / empty
  // input is silently ignored — `wpm` holds its last good value and reverts the
  // display on blur. Persist (debounced) becomes the user's new default.
  const handleWpmChange = e => {
    const raw = e.target.value;
    setWpmInput(raw);
    if (raw.trim() === '') return;
    const n = parseInt(raw, 10);
    if (!Number.isFinite(n)) return;
    const clamped = clampWpm(n);
    setWpm(clamped);
    if (wpmPersistTimer.current) clearTimeout(wpmPersistTimer.current);
    wpmPersistTimer.current = setTimeout(() => {
      try { localStorage.setItem(WPM_STORAGE_KEY, String(clamped)); } catch { /* ignore */ }
    }, 400);
  };
  const handleWpmBlur = () => {
    // Snap the display back to the last good clamped value (drops empty/invalid/
    // out-of-range text the user may have left in the box).
    setWpmInput(String(wpm));
    if (wpmPersistTimer.current) { clearTimeout(wpmPersistTimer.current); wpmPersistTimer.current = null; }
    try { localStorage.setItem(WPM_STORAGE_KEY, String(wpm)); } catch { /* ignore */ }
  };
  const colKeyToIdx = useMemo(() => {
    const m = {};
    cols.forEach((c, i) => { m[c.key] = i; });
    return m;
  }, [cols]);
  const startCellDrag = (rowIdx, colKey, shift, ctrl) => {
    const colIdx = colKeyToIdx[colKey];
    if (colIdx == null) return;
    // Cell and row selection are mutually exclusive — starting a cell selection
    // clears any gutter row selection so a copy reads the cells, not stale rows.
    setSelectionMode('cells');
    if (selectedRows.size) { setSelectedRows(new Set()); setSelectionAnchor(null); }
    if (ctrl) {
      // Ctrl/Cmd+click → toggle the cell in the non-contiguous set. If an
      // active rectangular range was being shown, freeze its cells into the
      // set first so the user doesn't lose them when they extend with Ctrl.
      setCellMultiSelection(prev => {
        const next = new Set(prev);
        if (cellSelection &&
            (cellSelection.startRow !== cellSelection.endRow ||
             cellSelection.startCol !== cellSelection.endCol)) {
          const r1 = Math.min(cellSelection.startRow, cellSelection.endRow);
          const r2 = Math.max(cellSelection.startRow, cellSelection.endRow);
          const c1 = Math.min(cellSelection.startCol, cellSelection.endCol);
          const c2 = Math.max(cellSelection.startCol, cellSelection.endCol);
          for (let r = r1; r <= r2; r++) {
            for (let c = c1; c <= c2; c++) next.add(`${r}:${cols[c].key}`);
          }
        }
        const id = `${rowIdx}:${colKey}`;
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
      });
      // Collapse the rect range to the clicked cell so it doesn't keep the
      // big rectangle highlighted while the user is building a non-contiguous set.
      setCellSelection({ startRow: rowIdx, startCol: colIdx, endRow: rowIdx, endCol: colIdx });
      return;
    }
    draggingRef.current = true;
    cellDragLastRef.current = `${rowIdx}:${colKey}`;
    // Plain or shift click — drop any non-contiguous selection.
    if (cellMultiSelection.size) setCellMultiSelection(new Set());
    setCellSelection(prev => {
      if (shift && prev) return { ...prev, endRow: rowIdx, endCol: colIdx };
      return { startRow: rowIdx, startCol: colIdx, endRow: rowIdx, endCol: colIdx };
    });
  };
  const updateCellDrag = (rowIdx, colKey) => {
    if (!draggingRef.current) return;
    const colIdx = colKeyToIdx[colKey];
    if (colIdx == null) return;
    setCellSelection(prev => prev ? { ...prev, endRow: rowIdx, endCol: colIdx } : prev);
  };
  const clearCellSelection = () => {
    setCellSelection(null);
    setCellMultiSelection(new Set());
  };
  // Collapse an active multi-cell range/multi-selection down to a single cell when
  // the user navigates by keyboard (arrows / Tab / Enter). Google-Sheets behavior:
  // a plain arrow after a range moves to ONE cell rather than keeping the old range.
  // We drop a real rectangle (and any ctrl+click multi-set) so the destination cell
  // — tracked by focusedCell — becomes the active single cell, which is what copy,
  // delete, color, etc. then act on. A lone 1×1 (or null) is left untouched so we
  // don't trigger needless re-renders mid-navigation.
  const collapseCellSelection = () => {
    setCellSelection(prev =>
      (prev && (prev.startRow !== prev.endRow || prev.startCol !== prev.endCol)) ? null : prev);
    setCellMultiSelection(prev => (prev.size ? new Set() : prev));
  };

  // ── Cross-script app clipboard: selection reader, copy, paste ────────────
  const flashAppClipToast = (msg) => {
    setAppClipToast(msg);
    if (appClipToastTimer.current) clearTimeout(appClipToastTimer.current);
    appClipToastTimer.current = setTimeout(() => setAppClipToast(null), 5000);
  };

  // Normalize the current selection into a descriptor used by copy and by paste
  // (as the paste anchor). Two kinds:
  //   'cells' — any cell selection (drag range, ctrl+click multi, or one focused
  //             cell). Copied/pasted as a rectangular OVERLAY block. anchorRow /
  //             anchorColIdx are the top-left cell.
  //   'rows'  — whole rows selected via the gutter. Copied/pasted as INSERTED rows.
  const cellsSel = (r1, r2, c1, c2) => {
    const rowIdxs = []; for (let r = r1; r <= r2; r++) rowIdxs.push(r);
    const colIdxs = []; for (let c = c1; c <= c2; c++) colIdxs.push(c);
    const colKeys = colIdxs.map(c => cols[c]?.key).filter(Boolean);
    return { kind: 'cells', rowIdxs, colIdxs, colKeys, anchorRow: r1, anchorColIdx: c1 };
  };
  const rowsSelection = () => {
    if (selectedRows.size === 0) return null;
    const sorted = [...selectedRows].sort((a, b) => a - b);
    return { kind: 'rows', rowIdxs: sorted, colKeys: cols.map(c => c.key), anchorRow: sorted[0], anchorColIdx: 0 };
  };
  const cellsSelection = () => {
    if (cellMultiSelection.size > 0) {
      // Non-contiguous ctrl+click set → bounding rectangle (like Sheets).
      let r1 = Infinity, r2 = -Infinity, c1 = Infinity, c2 = -Infinity;
      for (const id of cellMultiSelection) {
        const sep = id.indexOf(':');
        const r = Number(id.slice(0, sep));
        const ci = colKeyToIdx[id.slice(sep + 1)];
        if (!Number.isFinite(r) || ci == null) continue;
        r1 = Math.min(r1, r); r2 = Math.max(r2, r);
        c1 = Math.min(c1, ci); c2 = Math.max(c2, ci);
      }
      if (r1 !== Infinity) return cellsSel(r1, r2, c1, c2);
    }
    // A real multi-cell rectangle (drag / shift-range) is authoritative. A
    // COLLAPSED 1×1 cellSelection, however, carries no more information than the
    // focused cell — and keyboard navigation (arrows / Tab / Enter) moves
    // focusedCell WITHOUT updating cellSelection, so a stale 1×1 left over from an
    // earlier click would otherwise win and copy the WRONG (previously-clicked)
    // cell. Defer to focusedCell in that case, mirroring getActiveCellGrid's
    // precedence (the reader used by delete / color / move). Fall back to the 1×1
    // only when there's no usable focused cell.
    const cs = cellSelection;
    const csIsRange = cs && (cs.startRow !== cs.endRow || cs.startCol !== cs.endCol);
    if (csIsRange) {
      const r1 = Math.min(cs.startRow, cs.endRow), r2 = Math.max(cs.startRow, cs.endRow);
      const c1 = Math.min(cs.startCol, cs.endCol), c2 = Math.max(cs.startCol, cs.endCol);
      return cellsSel(r1, r2, c1, c2);
    }
    if (focusedCell && Number.isFinite(focusedCell.rowIdx)) {
      const ci = colKeyToIdx[focusedCell.colKey];
      if (ci != null) return cellsSel(focusedCell.rowIdx, focusedCell.rowIdx, ci, ci);
    }
    if (cs) {
      const r1 = Math.min(cs.startRow, cs.endRow), c1 = Math.min(cs.startCol, cs.endCol);
      return cellsSel(r1, r1, c1, c1);
    }
    return null;
  };
  // SINGLE decision point. `selectionMode` (set by the user's last action — cell
  // drag/click vs gutter click) is authoritative, so a row copy can never be
  // mistaken for a cell copy or vice-versa. Falls back to whichever exists only
  // when no mode is set yet.
  const getActiveSelection = () => {
    if (selectionMode === 'rows')  return rowsSelection();
    if (selectionMode === 'cells') return cellsSelection();
    return cellsSelection() || rowsSelection();
  };

  const copySelectionToAppClipboard = () => {
    if (!format || !draftBody) return;
    const sel = getActiveSelection();
    if (!sel) { flashCopyFeedback('Select cells or rows to copy first.', 2000); return; }
    const scenes = draftBody.scenes || [];
    const base = {
      sourceScriptId:   selected?.id,
      sourceScriptName: selected?.scriptName || 'Untitled',
      sourceFormat:     format,
      copiedAt:         Date.now(),
    };
    let payload, n;
    if (sel.kind === 'rows') {
      const rows = sel.rowIdxs.filter(i => scenes[i]).map(i => {
        const scene = scenes[i]; const out = {};
        for (const c of cols) out[c.key] = scene[c.key] ?? '';
        if (scene._rowColor)   out._rowColor   = scene._rowColor;
        if (scene._cellColors) out._cellColors = { ...scene._cellColors };
        if (scene._links)      out._links      = JSON.parse(JSON.stringify(scene._links));
        return out;
      });
      if (!rows.length) return;
      payload = { ...base, kind: 'rows', rows };
      n = rows.length;
    } else {
      // Rectangular cell block → 2D grid of { v, color?, link? }, preserving layout.
      const grid = sel.rowIdxs.map(r => {
        const scene = scenes[r] || {};
        return sel.colKeys.map(k => {
          const cell = { v: scene[k] ?? '' };
          const color = scene._cellColors?.[k];
          const link  = scene._links?.[k];
          if (color) cell.color = color;
          if (link)  cell.link  = link;
          return cell;
        });
      });
      if (!grid.length || !sel.colKeys.length) {
        flashAppClipToast(`Copy issue — selection captured ${grid.length} rows × ${sel.colKeys.length} cols (nothing to copy).`);
        return;
      }
      payload = { ...base, kind: 'cells', colKeys: sel.colKeys, grid };
      n = grid.length;
    }
    writeAppClip(payload);
    // Best-effort system clipboard (TSV) so the same selection can also paste into
    // Google Sheets / Excel. sessionStorage is the source of truth; a denied
    // clipboard permission just means no external paste.
    try {
      const tsv = payload.kind === 'cells'
        ? payload.grid.map(row => row.map(c => tsvEscapeCell(c.v)).join('\t')).join('\n')
        : buildSubsetTSV(payload.rows, cols.map(c => c.key));
      if (tsv && navigator.clipboard?.writeText) navigator.clipboard.writeText(tsv).catch(() => {});
    } catch { /* best effort */ }
    if (payload.kind === 'cells') {
      const w = payload.colKeys.length;
      flashAppClipToast(`Copied ${n}×${w} cell block — click a target cell in any script, then paste.`);
    } else {
      flashAppClipToast(`Copied ${n} row${n !== 1 ? 's' : ''} — paste into any script.`);
    }
  };

  // Write whole rows (by index) to the app clipboard as a 'rows' payload. Called
  // by the legacy row-copy paths (gutter Ctrl+C and the right-click menu) so they
  // populate the SAME cross-script clipboard the paste reads — otherwise a copy
  // there leaves a stale app clipboard and paste uses old rows from another script.
  const writeRowsToAppClip = (indices) => {
    if (!format || !indices?.length) return;
    const scenes = draftBody?.scenes || [];
    const rows = indices.filter(i => scenes[i]).map(i => {
      const scene = scenes[i]; const out = {};
      for (const c of cols) out[c.key] = scene[c.key] ?? '';
      if (scene._rowColor)   out._rowColor   = scene._rowColor;
      if (scene._cellColors) out._cellColors = { ...scene._cellColors };
      if (scene._links)      out._links      = JSON.parse(JSON.stringify(scene._links));
      return out;
    });
    if (!rows.length) return;
    writeAppClip({
      kind: 'rows',
      sourceScriptId:   selected?.id,
      sourceScriptName: selected?.scriptName || 'Untitled',
      sourceFormat:     format,
      copiedAt:         Date.now(),
      rows,
    });
  };

  // Explicit save that tags the change as a paste so the API writes a paste
  // audit entry. Mirrors saveBody's permission guard; takes the freshly computed
  // body to avoid the async-setState race.
  const savePaste = async (bodyToSave, meta) => {
    if (!selected || !bodyToSave) return;
    const isShootLeadEditing = location.state?.isShootLeadForScript && (hasRole('Producer'));
    if (!hasRole('Admin', 'Manager', 'Power User', 'Qual Reviewer') && !isShootLeadEditing && selected?.writer?.id !== user?.id) return;
    setSaving(true);
    try {
      await api.patch(`/api/scripts/${selected.id}`, {
        scriptBody: bodyToSave,
        qualNotes:  draftNotesRef.current,
        saveType:   'paste',
        pasteMeta:  meta,
      });
      setLastSaved(new Date());
      setSaveError(null);
      if (activityOpen) {
        const logRes = await api.get(`/api/audit/script/${selected.id}`);
        setActivityLog(logRes.data);
      }
    } catch (err) {
      console.error('Paste save failed:', err);
      setSaveError({ tooLarge: err?.response?.status === 413 });
    } finally {
      setSaving(false);
    }
  };

  const pasteFromAppClipboard = async () => {
    if (isReadOnly || !format || !draftBody) return;
    const clip = appClip;
    if (!clip || (clip.kind === 'cells' ? !clip.grid?.length : !clip.rows?.length)) {
      flashCopyFeedback('Clipboard is empty.', 2000);
      return;
    }

    const cur = [...(draftBody.scenes || [])];
    const beforeCount = cur.length;
    let next, selectStart, pastedRowCount;

    if (clip.kind === 'cells') {
      // Spreadsheet overlay: drop the grid into cells starting at the anchor,
      // overwriting existing values and growing the script with new rows when the
      // block runs past the bottom. Columns past the rightmost are ignored.
      const grid = clip.grid;
      const sel = getActiveSelection();
      // Anchor at the active target cell if one is selected in THIS script;
      // otherwise top-left. Never append at the bottom — that turned a cell paste
      // into "a bunch of rows". An out-of-bounds (stale, cross-script) anchor also
      // falls back to top-left.
      const inBounds = sel && Number.isFinite(sel.anchorRow) && sel.anchorRow >= 0 && sel.anchorRow < cur.length;
      const anchorRow = inBounds ? sel.anchorRow : 0;
      const anchorCol = inBounds ? sel.anchorColIdx : 0;
      next = cur.map(s => ({ ...s }));
      for (let r = 0; r < grid.length; r++) {
        const targetRow = anchorRow + r;
        while (next.length <= targetRow) next.push(emptyRow(format));
        const scene = { ...next[targetRow] };
        const gridRow = grid[r];
        for (let c = 0; c < gridRow.length; c++) {
          const destCol = cols[anchorCol + c];
          if (!destCol) continue;                            // past the last column → ignore overflow
          const cell = gridRow[c];
          scene[destCol.key] = cell.v ?? '';
          if (cell.color) scene._cellColors = { ...(scene._cellColors || {}), [destCol.key]: cell.color };
          if (cell.link)  scene._links      = { ...(scene._links      || {}), [destCol.key]: cell.link };
        }
        next[targetRow] = scene;
      }
      selectStart = anchorRow;
      pastedRowCount = grid.length;
    } else {
      // Whole rows → insert. Positional cross-format mapping (with a warning).
      if (clip.sourceFormat !== format) {
        const ok = await new Promise(resolve =>
          setCrossFormatPastePrompt({ sourceFormat: clip.sourceFormat, destFormat: format, resolve }));
        setCrossFormatPastePrompt(null);
        if (!ok) return;
      }
      const { scenes: incoming } = mapClipRowsToDestination(
        { rows: clip.rows, sourceFormat: clip.sourceFormat, selectionType: 'full_rows' },
        format, { columnMode: 'full' });
      if (!incoming.length) return;
      const sel = getActiveSelection();
      const at = sel ? sel.anchorRow : cur.length;           // insert above active row, else append
      next = [...cur.slice(0, at), ...incoming, ...cur.slice(at)];
      selectStart = at;
      pastedRowCount = incoming.length;
    }

    pushUndo(draftBody);
    if (next.length === 0) next = [emptyRow(format)];
    const newBody = { ...draftBody, scenes: next };
    setDraftBody(newBody);
    clearCellSelection();
    setSelectAllActive(false);
    const newSelection = new Set();
    for (let k = 0; k < pastedRowCount; k++) newSelection.add(selectStart + k);
    setSelectedRows(newSelection);
    setSelectionAnchor(selectStart);
    setSelectionMode('rows');   // result is highlighted as rows; keep the mode consistent
    const whatPasted = clip.kind === 'cells'
      ? `${pastedRowCount}×${(clip.colKeys || []).length} cell block`
      : `${pastedRowCount} row${pastedRowCount !== 1 ? 's' : ''}`;
    flashCopyFeedback(
      `Pasted ${whatPasted}${clip.sourceScriptName ? ` from ${clip.sourceScriptName}` : ''}.`,
      2800
    );

    // Audit the paste (pass the freshly computed body — setDraftBody is async).
    savePaste(newBody, {
      beforeCount,
      afterCount:       next.length,
      pastedCount:      pastedRowCount,
      sourceScriptName: clip.sourceScriptName,
      sourceFormat:     clip.sourceFormat,
    });
  };

  // Keep the capture-phase keydown listener reading current state without
  // re-subscribing on every render.
  appClipKbdRef.current = {
    getActiveSelection,
    copy:       copySelectionToAppClipboard,
    paste:      pasteFromAppClipboard,
    hasClip:    appClipCount > 0,   // true for both cell-grid and whole-row payloads
    // A pending legacy row "cut" (Ctrl+X → paste-to-move) must keep Ctrl+V, so
    // the app clipboard yields V while a cut is in flight.
    cutPending: cutRows.size > 0,
  };

  // Cross-script clipboard keyboard ownership (capture phase, runs before the
  // legacy bubble-phase row-clipboard handler). Claims Ctrl/Cmd+C only when a
  // selection exists, and Ctrl/Cmd+V only when the app clipboard is populated
  // and no legacy cut is pending — and only when NOT editing text and NOT in #
  // select-all (those keep their existing native / Google-Sheets behavior). On a
  // claim it stopImmediatePropagation()s so the legacy handler doesn't double-fire;
  // otherwise it leaves the event completely untouched.
  useEffect(() => {
    if (!selected) return;
    const onKeyDownCapture = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      const tag = document.activeElement?.tagName;
      if (tag === 'TEXTAREA' || tag === 'INPUT') return; // editing a cell → native
      if (selectAllActive) return;                       // # select-all → Google Sheets clipboard
      const isC = e.key === 'c' || e.key === 'C';
      const isV = e.key === 'v' || e.key === 'V';
      if (!isC && !isV) return;
      const h = appClipKbdRef.current;
      if (isC) {
        if (h.getActiveSelection && h.getActiveSelection()) {
          e.preventDefault();
          e.stopImmediatePropagation();
          h.copy();
        }
        return;
      }
      // isV
      if (h.hasClip && !h.cutPending) {
        e.preventDefault();
        e.stopImmediatePropagation();
        h.paste();
      }
    };
    document.addEventListener('keydown', onKeyDownCapture, true);
    return () => document.removeEventListener('keydown', onKeyDownCapture, true);
  }, [selected, selectAllActive]);

  // Phase C1 (spec 4.1 / 4.2): move-drag for single cell OR range. The anchor
  // is the cell whose move-handle the user grabbed; the source range is the
  // current cellSelection (or just the anchor cell if no multi-cell range).
  // The whole range moves by the (target - anchor) offset on drop.
  const startMoveDrag = (anchorRow, anchorCol) => {
    let r1 = anchorRow, r2 = anchorRow, c1 = anchorCol, c2 = anchorCol;
    if (cellSelection && (cellSelection.startRow !== cellSelection.endRow || cellSelection.startCol !== cellSelection.endCol)) {
      r1 = Math.min(cellSelection.startRow, cellSelection.endRow);
      r2 = Math.max(cellSelection.startRow, cellSelection.endRow);
      c1 = Math.min(cellSelection.startCol, cellSelection.endCol);
      c2 = Math.max(cellSelection.startCol, cellSelection.endCol);
    }
    const md = { anchorRow, anchorCol, r1, r2, c1, c2, targetRow: anchorRow, targetCol: anchorCol };
    moveDragRef.current = md;
    setMoveDrag(md);
  };
  const updateMoveTarget = (targetRow, colKey) => {
    const cur = moveDragRef.current;
    if (!cur) return;
    const targetCol = colKeyToIdx[colKey];
    if (targetCol == null) return;
    const next = { ...cur, targetRow, targetCol };
    moveDragRef.current = next;
    setMoveDrag(next);
  };
  const commitMoveDrag = () => {
    const md = moveDragRef.current;
    if (!md) return;
    setMoveDrag(null);
    const offsetR = md.targetRow - md.anchorRow;
    const offsetC = md.targetCol - md.anchorCol;
    if (offsetR === 0 && offsetC === 0) return;
    const body = draftBodyRef.current;
    if (!body) return;
    pushUndo(body);

    // Build per-row update bundle. Source clears come first; target sets
    // override in the overlap so cells inside both source AND target end up
    // with their target value rather than empty.
    const updates = new Map(); // rowIdx -> { colKey: value }
    const setUpdate = (r, key, v) => {
      if (!updates.has(r)) updates.set(r, {});
      updates.get(r)[key] = v;
    };

    // 1) Capture source values and queue source clears.
    const captured = [];
    for (let r = md.r1; r <= md.r2; r++) {
      for (let c = md.c1; c <= md.c2; c++) {
        const key = cols[c]?.key;
        if (!key) continue;
        const v = body.scenes[r]?.[key] ?? '';
        captured.push({ dr: r - md.r1, dc: c - md.c1, value: v });
        setUpdate(r, key, '');
      }
    }
    // 2) Apply captured values at the target position.
    for (const { dr, dc, value } of captured) {
      const tr = md.r1 + offsetR + dr;
      const tc = md.c1 + offsetC + dc;
      if (tr < 0 || tr >= body.scenes.length) continue;
      const key = cols[tc]?.key;
      if (!key) continue;
      setUpdate(tr, key, value);
    }

    const scenes = body.scenes.map((row, idx) => {
      const u = updates.get(idx);
      if (!u) return row;
      return { ...row, ...u };
    });
    setDraftBody({ ...body, scenes });

    // Move the selection + focus to the new location so the user sees where
    // their range landed and can immediately keep working on it.
    const isRange = md.r1 !== md.r2 || md.c1 !== md.c2;
    if (isRange) {
      setCellSelection({
        startRow: md.r1 + offsetR, startCol: md.c1 + offsetC,
        endRow:   md.r2 + offsetR, endCol:   md.c2 + offsetC,
      });
    } else {
      setCellSelection(null);
    }
    const newAnchorCol = cols[md.anchorCol + offsetC]?.key;
    if (newAnchorCol) setFocusedCell({ rowIdx: md.anchorRow + offsetR, colKey: newAnchorCol });
  };

  // Phase C2 (spec 5.2): autofill drag from the bottom-right handle. Both the
  // ref and the state are updated synchronously — the ref so the very next
  // mouseenter (which can fire before the useEffect sync) sees a live drag,
  // and the state so the preview tint renders.
  const startAutofillDrag = () => {
    let r1, r2, c1, c2;
    if (cellSelection && (cellSelection.startRow !== cellSelection.endRow || cellSelection.startCol !== cellSelection.endCol)) {
      r1 = Math.min(cellSelection.startRow, cellSelection.endRow);
      r2 = Math.max(cellSelection.startRow, cellSelection.endRow);
      c1 = Math.min(cellSelection.startCol, cellSelection.endCol);
      c2 = Math.max(cellSelection.startCol, cellSelection.endCol);
    } else if (focusedCell) {
      const colIdx = colKeyToIdx[focusedCell.colKey];
      if (colIdx == null) return;
      r1 = r2 = focusedCell.rowIdx;
      c1 = c2 = colIdx;
    } else return;
    const af = { sourceR1: r1, sourceR2: r2, sourceC1: c1, sourceC2: c2, targetEndRow: r2 };
    autofillDragRef.current = af;
    setAutofillDrag(af);
  };
  const updateAutofillTarget = (rowIdx) => {
    const cur = autofillDragRef.current;
    if (!cur) return;
    const next = { ...cur, targetEndRow: Math.max(cur.sourceR2, rowIdx) };
    autofillDragRef.current = next;
    setAutofillDrag(next);
  };
  const commitAutofillDrag = () => {
    const af = autofillDragRef.current;
    if (!af) return;
    setAutofillDrag(null);
    if (af.targetEndRow <= af.sourceR2) return;
    const body = draftBodyRef.current;
    if (!body) return;
    pushUndo(body);
    const fillCount = af.targetEndRow - af.sourceR2;
    const scenes = [...body.scenes];
    for (let c = af.sourceC1; c <= af.sourceC2; c++) {
      const key = cols[c]?.key;
      if (!key) continue;
      const sourceValues = [];
      for (let r = af.sourceR1; r <= af.sourceR2; r++) {
        sourceValues.push(body.scenes[r]?.[key] ?? '');
      }
      const fillValues = detectAndExtendColumn(sourceValues, fillCount);
      for (let i = 0; i < fillCount; i++) {
        const targetR = af.sourceR2 + 1 + i;
        if (targetR >= scenes.length) break;
        scenes[targetR] = { ...scenes[targetR], [key]: fillValues[i] };
      }
    }
    setDraftBody({ ...body, scenes });
    // Extend the selection to cover source + fill range so the user sees what landed.
    setCellSelection({
      startRow: af.sourceR1, startCol: af.sourceC1,
      endRow:   Math.min(af.targetEndRow, scenes.length - 1), endCol: af.sourceC2,
    });
  };

  // Phase B3: resolve the currently "active" cell grid. If a multi-cell range
  // is active, that's the grid. Otherwise the focused single cell. Returns
  // null when there's nothing to act on. Reads cellSelection/focusedCell from
  // the parent's render closure — each render redefines this with latest state,
  // and callers reach it via handlersLatestRef so they always see the latest.
  const getActiveCellGrid = () => {
    if (cellSelection && (cellSelection.startRow !== cellSelection.endRow || cellSelection.startCol !== cellSelection.endCol)) {
      const r1 = Math.min(cellSelection.startRow, cellSelection.endRow);
      const r2 = Math.max(cellSelection.startRow, cellSelection.endRow);
      const c1 = Math.min(cellSelection.startCol, cellSelection.endCol);
      const c2 = Math.max(cellSelection.startCol, cellSelection.endCol);
      const grid = [];
      for (let r = r1; r <= r2; r++) {
        const row = [];
        for (let c = c1; c <= c2; c++) {
          if (cols[c]) row.push({ rowIdx: r, colKey: cols[c].key, colIdx: c });
        }
        grid.push(row);
      }
      return grid;
    }
    if (focusedCell) {
      const colIdx = colKeyToIdx[focusedCell.colKey];
      if (colIdx == null) return null;
      return [[{ rowIdx: focusedCell.rowIdx, colKey: focusedCell.colKey, colIdx }]];
    }
    return null;
  };

  // Spec 2.1: clear every cell in the active grid. One undo per action.
  const clearActiveCells = () => {
    const grid = getActiveCellGrid();
    if (!grid || grid.length === 0) return;
    const body = draftBodyRef.current;
    if (!body) return;
    pushUndo(body);
    const targets = new Map(); // rowIdx -> Set<colKey>
    grid.flat().forEach(c => {
      if (!targets.has(c.rowIdx)) targets.set(c.rowIdx, new Set());
      targets.get(c.rowIdx).add(c.colKey);
    });
    const scenes = body.scenes.map((row, idx) => {
      const keys = targets.get(idx);
      if (!keys) return row;
      const next = { ...row };
      keys.forEach(k => { next[k] = ''; });
      return next;
    });
    setDraftBody({ ...body, scenes });
  };

  // Spec 2.3: cut = copy active grid to clipboard as TSV, then clear source.
  // TSV is the format spreadsheets use, so the user can paste into Sheets/Excel.
  const cutActiveCells = async () => {
    const grid = getActiveCellGrid();
    if (!grid || grid.length === 0) return;
    const body = draftBodyRef.current;
    if (!body) return;
    const tsv = grid.map(row =>
      row.map(c => {
        const v = body.scenes[c.rowIdx]?.[c.colKey];
        return String(v || '').replace(/\t/g, ' ').replace(/\r?\n/g, ' ');
      }).join('\t')
    ).join('\n');
    try { await navigator.clipboard.writeText(tsv); }
    catch { /* clipboard denied — still clear so the cut intent is honored */ }
    clearActiveCells();
  };

  // Spec 2.3: paste TSV from clipboard into cells starting at focused cell.
  // Distributes values row-by-row, column-by-column. Extras (beyond grid edge)
  // are dropped — we don't auto-add rows or columns.
  const pasteIntoActiveCells = async () => {
    if (!focusedCell) return;
    let text;
    try { text = await navigator.clipboard.readText(); }
    catch { return; }
    if (!text) return;
    const startCol = colKeyToIdx[focusedCell.colKey];
    if (startCol == null) return;
    const startRow = focusedCell.rowIdx;
    const lines = text.split(/\r?\n/);
    if (lines.length > 1 && lines[lines.length - 1] === '') lines.pop();
    const grid = lines.map(line => line.split('\t'));
    const body = draftBodyRef.current;
    if (!body) return;
    pushUndo(body);
    const scenes = [...body.scenes];
    for (let r = 0; r < grid.length; r++) {
      const targetRow = startRow + r;
      if (targetRow >= scenes.length) break;
      const next = { ...scenes[targetRow] };
      for (let c = 0; c < grid[r].length; c++) {
        const targetCol = cols[startCol + c];
        if (!targetCol) break;
        next[targetCol.key] = grid[r][c];
      }
      scenes[targetRow] = next;
    }
    setDraftBody({ ...body, scenes });
  };

  // Spec 2.4: fill down — single-cell only (range form belongs to autofill 5.2).
  // Copies the value from the cell directly above into the focused cell.
  const fillDownActiveCells = () => {
    if (!focusedCell || focusedCell.rowIdx <= 0) return;
    const body = draftBodyRef.current;
    if (!body) return;
    const above = body.scenes[focusedCell.rowIdx - 1]?.[focusedCell.colKey];
    if (above == null) return;
    pushUndo(body);
    const scenes = [...body.scenes];
    scenes[focusedCell.rowIdx] = { ...scenes[focusedCell.rowIdx], [focusedCell.colKey]: above };
    setDraftBody({ ...body, scenes });
  };

  // SceneRow handlers: keep the ref pointing at the latest functions every render,
  // and expose a *stable* rowHandlers object whose methods delegate through the ref.
  // Stability of rowHandlers is what lets React.memo on SceneRow actually skip work
  // on unchanged rows.
  handlersLatestRef.current = {
    updateScene, applyMarkdown, addRow, moveRow,
    handleGutterMouseDown, updateRowColor, updateCellColor,
    removeLink, saveLink, pushUndo, saveBody,
    enterEdit, exitEdit, revertEdit,
    startCellDrag, updateCellDrag, clearCellSelection, collapseCellSelection,
    clearActiveCells, cutActiveCells, pasteIntoActiveCells, fillDownActiveCells,
    startMoveDrag, updateMoveTarget, commitMoveDrag,
    startAutofillDrag, updateAutofillTarget, commitAutofillDrag,
    cellDragPointerMove, stopEdgeAutoScroll,
    addCharacterToRegistry,
  };
  const rowHandlers = useMemo(() => ({
    updateScene:           (...a) => handlersLatestRef.current.updateScene(...a),
    applyMarkdown:         (...a) => handlersLatestRef.current.applyMarkdown(...a),
    addRow:                (...a) => handlersLatestRef.current.addRow(...a),
    moveRow:               (...a) => handlersLatestRef.current.moveRow(...a),
    handleGutterMouseDown: (...a) => handlersLatestRef.current.handleGutterMouseDown(...a),
    updateRowColor:        (...a) => handlersLatestRef.current.updateRowColor(...a),
    updateCellColor:       (...a) => handlersLatestRef.current.updateCellColor(...a),
    removeLink:            (...a) => handlersLatestRef.current.removeLink(...a),
    saveLink:              (...a) => handlersLatestRef.current.saveLink(...a),
    pushUndo:              (...a) => handlersLatestRef.current.pushUndo(...a),
    saveBody:              (...a) => handlersLatestRef.current.saveBody(...a),
    // Set a focus target the layout effect will honor right after React commits.
    // Used when addRow is called from Enter / Tab at the bottom so cursor moves to the
    // new row in the same paint as the row appearing.
    queueFocus: (rowIdx, colKey) => { pendingFocusRef.current = { rowIdx, colKey }; },
    getTabOrigin:   ()  => tabOriginColRef.current,
    setTabOrigin:   (k) => { tabOriginColRef.current = k; },
    clearTabOrigin: ()  => { tabOriginColRef.current = null; },
    enterEdit:      (...a) => handlersLatestRef.current.enterEdit(...a),
    exitEdit:       (...a) => handlersLatestRef.current.exitEdit(...a),
    revertEdit:     (...a) => handlersLatestRef.current.revertEdit(...a),
    startCellDrag:       (...a) => handlersLatestRef.current.startCellDrag(...a),
    updateCellDrag:      (...a) => handlersLatestRef.current.updateCellDrag(...a),
    clearCellSelection:  ()     => handlersLatestRef.current.clearCellSelection(),
    collapseCellSelection: ()   => handlersLatestRef.current.collapseCellSelection(),
    clearActiveCells:    ()     => handlersLatestRef.current.clearActiveCells(),
    startMoveDrag:       (...a) => handlersLatestRef.current.startMoveDrag(...a),
    updateMoveTarget:    (...a) => handlersLatestRef.current.updateMoveTarget(...a),
    startAutofillDrag:   ()     => handlersLatestRef.current.startAutofillDrag(),
    updateAutofillTarget:(...a) => handlersLatestRef.current.updateAutofillTarget(...a),
    addCharacterToRegistry: (...a) => handlersLatestRef.current.addCharacterToRegistry(...a),
  }), []); // stable for the component's lifetime

  const handleExportSceneCSV = () => {
    if (!selected || !draftBody) return;
    exportSceneCSV({ scriptName: selected.scriptName, scriptBody: draftBody, hasBeenApproved: selected.hasBeenApproved });
  };

  async function loadShareLinks(scriptId) {
    setShareLinksLoad(true);
    try {
      const res = await api.get(`/api/share-links/scripts/by-script/${scriptId}`);
      setShareLinks(res.data);
    } catch { setShareLinks([]); } finally { setShareLinksLoad(false); }
  }

  async function generateShareLink() {
    if (!selected) return;
    setShareGenerating(true);
    try {
      const res = await api.post('/api/share-links/scripts', { scriptId: selected.id });
      setShareNewUrl(res.data.url);
      await loadShareLinks(selected.id);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to generate share link.');
    } finally { setShareGenerating(false); }
  }

  async function deactivateShareLink(linkId) {
    try {
      await api.delete(`/api/share-links/scripts/${linkId}`);
      setShareLinks(prev => prev.filter(l => l.id !== linkId));
      if (shareNewUrl) {
        // Check if the deactivated link is the one we just generated
        const deactivated = shareLinks.find(l => l.id === linkId);
        if (deactivated && shareNewUrl.includes(deactivated.token)) setShareNewUrl(null);
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to deactivate link.');
    }
  }

  const handleImportCSV = (e) => {
    const file = e.target.files[0];
    if (!file || !format) return;
    e.target.value = '';
    const reader = new FileReader();
    reader.onload = (ev) => {
      const scenes = csvToScenes(ev.target.result, format);
      if (scenes.length === 0) {
        alert('No valid rows found. Make sure the CSV headers match the downloaded template.');
        return;
      }
      const proceed = !draftBody?.scenes?.some(r => Object.values(r).some(v => v))
        || window.confirm(`Replace all ${draftBody.scenes.length} existing rows with ${scenes.length} imported rows?`);
      if (proceed) setDraftBody({ ...draftBody, scenes });
    };
    reader.readAsText(file);
  };

  // ── Spreadsheet clipboard sync handlers ──────────────────────────
  const hasRealScriptContent = (scenes) =>
    Array.isArray(scenes) && scenes.some(r =>
      Object.entries(r).some(([k, v]) => !k.startsWith('_') && typeof v === 'string' && v.trim())
    );

  const copyScriptToClipboard = async () => {
    if (!format || !draftBody?.scenes?.length || !hasRealScriptContent(draftBody.scenes)) {
      flashCopyFeedback('Nothing to copy — script is empty.', 2000);
      return;
    }
    const tsv = buildScriptTSV(draftBody.scenes, format);
    const html = buildScriptHTML(draftBody.scenes, format);
    try {
      // Prefer ClipboardItem so we can write both text/plain AND text/html in one go.
      // Heliconflow→Heliconflow paste prefers text/html (round-trips colors + links);
      // Sheets paste also reads text/html so colors and hyperlinks come through there too.
      if (navigator.clipboard?.write && typeof window.ClipboardItem !== 'undefined') {
        const item = new window.ClipboardItem({
          'text/html':  new Blob([html], { type: 'text/html'  }),
          'text/plain': new Blob([tsv],  { type: 'text/plain' }),
        });
        await navigator.clipboard.write([item]);
      } else if (navigator.clipboard?.writeText) {
        // Older browsers: TSV only (colors/links lost on round-trip).
        await navigator.clipboard.writeText(tsv);
      } else {
        const ta = document.createElement('textarea');
        ta.value = tsv;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        ta.setAttribute('readonly', '');
        document.body.appendChild(ta);
        ta.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(ta);
        if (!ok) throw new Error('execCommand copy returned false');
      }
      flashCopyFeedback('Script copied — paste into any spreadsheet.', 2000);
    } catch {
      flashCopyFeedback('Could not copy to clipboard. Try Ctrl+C after clicking the # cell.', 2800);
    }
  };

  // Direct paste — no modal, no header matching. Tries text/html first (full fidelity for
  // colors + hyperlinks), then falls back to text/plain TSV positional parse. Ctrl+Z restores.
  const pasteFromClipboard = async () => {
    if (!format || isReadOnly || !draftBody) return;
    let newScenes = null;
    // 1) HTML path — works for Heliconflow→Heliconflow and Sheets→Heliconflow alike.
    try {
      if (navigator.clipboard?.read) {
        const items = await navigator.clipboard.read();
        for (const item of items) {
          if (item.types?.includes('text/html')) {
            const blob = await item.getType('text/html');
            const html = await blob.text();
            if (html && html.trim()) {
              const parsed = parseScenesFromHTML(html, format);
              if (parsed && parsed.length > 0) { newScenes = parsed; break; }
            }
          }
        }
      }
    } catch { /* fall through to TSV */ }
    // 2) Plain text TSV fallback.
    if (!newScenes) {
      try {
        if (navigator.clipboard?.readText) {
          const text = await navigator.clipboard.readText();
          if (text && text.trim()) newScenes = parseScenesPositional(text, format, '\t');
        }
      } catch { /* leave newScenes null */ }
    }
    if (!newScenes || newScenes.length === 0) {
      flashCopyFeedback("Couldn't read clipboard — copy a range from your spreadsheet, then try again.", 2800);
      return;
    }
    pushUndo(draftBody);
    setDraftBody({ ...draftBody, scenes: newScenes });
    setSelectAllActive(false);
    flashCopyFeedback(
      `${newScenes.length} row${newScenes.length !== 1 ? 's' : ''} pasted from clipboard.`,
      2500
    );
  };

  // Bulk-apply a qual status to every selected script. The server enforces the
  // per-script transition rules (valid from-state, role, no self-approve) and
  // all side effects, so we loop the single-script endpoint and report a summary
  // rather than assuming uniform success — a mixed selection will partially fail
  // (e.g. "Approved" only takes on scripts currently in Needs Review).
  const handleBulkStatus = async (status) => {
    const ids = [...selectedScripts];
    if (ids.length === 0) return;
    // Pulling scripts back out of Approved in bulk deletes their video cards and
    // scheduled shoot days (server-side, same as the single-script path, which
    // has its own modal). Before 2026-09-08 the server refused Approved → Needs
    // Review outright, so this bulk button could never destroy anything — now it
    // can, and it needs the same explicit confirmation.
    if (status === 'Needs Review') {
      const approved = scripts.filter(s => selectedScripts.has(s.id) && s.qualStatus === 'Approved');
      if (approved.length > 0 && !window.confirm(
        `${approved.length} of the ${ids.length} selected script${ids.length !== 1 ? 's are' : ' is'} Approved. `
        + `Moving ${approved.length !== 1 ? 'them' : 'it'} back to Needs Review permanently deletes `
        + `${approved.length !== 1 ? 'their' : 'its'} video cards and any scheduled shoot days (a Longform series loses its episode cards; re-approving recreates them from the script). Continue?`
      )) return;
    }
    setBulkStatusBusy(true);
    try {
      const results = await Promise.allSettled(
        ids.map(id => api.patch(`/api/scripts/${id}/status`, { status }))
      );
      const ok = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.length - ok;
      await loadScripts();
      loadCounts();
      setSelectedScripts(new Set());
      if (failed === 0) {
        flashCopyFeedback(`${ok} script${ok !== 1 ? 's' : ''} marked "${status}".`, 2500);
      } else {
        // Surface the most common reason so it's actionable (usually an illegal
        // transition from the script's current status).
        const firstErr = results.find(r => r.status === 'rejected');
        const reason = firstErr?.reason?.response?.data?.error || 'not an allowed change for their current status';
        alert(`Marked ${ok} as "${status}". ${failed} skipped — ${reason}.`);
      }
    } finally {
      setBulkStatusBusy(false);
    }
  };

  const handleMassDeleteScripts = async () => {
    const ids = [...selectedScripts];
    try {
      await Promise.all(ids.map(id => api.delete(`/api/scripts/${id}`)));
      setScripts(prev => prev.filter(s => !selectedScripts.has(s.id)));
      setSelectedScripts(new Set());
      setScriptMassDelete(false);
      loadCounts();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete one or more scripts.');
      setScriptMassDelete(false);
      loadScripts();
    }
  };

  // Bulk archive / unarchive (B5). Same selection mechanic + Admin/PU gate as
  // mass delete. Archive is unconditional — no status/production guard (B3).
  const handleBulkArchive = async (archive) => {
    const ids = [...selectedScripts];
    if (ids.length === 0) return;
    setBulkStatusBusy(true);
    try {
      const path = archive ? 'bulk-archive' : 'bulk-unarchive';
      const res = await api.post(`/api/scripts/${path}`, { ids });
      const n = archive ? res.data.archived : res.data.unarchived;
      setSelectedScripts(new Set());
      await Promise.all([loadScripts(), loadCounts()]);
      flashCopyFeedback(`${n} script${n !== 1 ? 's' : ''} ${archive ? 'archived' : 'unarchived'}.`, 2500);
    } catch (err) {
      alert(err.response?.data?.error || `Failed to ${archive ? 'archive' : 'unarchive'} scripts.`);
    } finally {
      setBulkStatusBusy(false);
    }
  };

  // Restore (un-delete) from the Deleted view. Mirrors handleBulkArchive: same
  // selection mechanic, same Admin/PU gate. Restoring only clears the deleted
  // flag — a script that was archived when deleted returns to the archive shelf,
  // so the restored row may not appear on the production line afterwards.
  const handleBulkRestore = async () => {
    const ids = [...selectedScripts];
    if (ids.length === 0) return;
    setBulkStatusBusy(true);
    try {
      const res = await api.post('/api/scripts/bulk-restore', { ids });
      const n = res.data.restored;
      setSelectedScripts(new Set());
      await Promise.all([loadScripts(), loadCounts()]);
      flashCopyFeedback(`${n} script${n !== 1 ? 's' : ''} restored.`, 2500);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to restore scripts.');
    } finally {
      setBulkStatusBusy(false);
    }
  };

  // ── Reassign selected scripts to another project ────────────────────────────
  // Opening the modal asks the SERVER which projects can receive the selection:
  // eligibility is the receiving project's allowed script formats plus the
  // "hasn't started filming" rule, and duplicating either here would let the
  // modal offer a project the write path refuses. An ineligible project is
  // simply absent from the list (Owner 2026-09-08) rather than offered and
  // rejected, so there is no format warning to show.
  const openReassign = async () => {
    const ids = [...selectedScripts];
    if (ids.length === 0) return;
    setReassignBusy(true);
    setReassignResult(null);
    try {
      const res = await api.post('/api/scripts/reassign-targets', { ids });
      setReassignData(res.data);
    } catch (err) {
      alert(err.response?.data?.error || 'Could not work out which projects can receive these scripts.');
    } finally {
      setReassignBusy(false);
    }
  };

  // The move itself. The server renames each script onto the receiving project's
  // initials and reports the before/after, which the modal then shows — the
  // rename is the part users need to see, since every downstream reference
  // (Frame.io folder, file name) is by script name.
  const handleReassign = async (clientId) => {
    const ids = (reassignData?.movable || []).map(m => m.id);
    if (ids.length === 0) return;
    setReassignBusy(true);
    try {
      const res = await api.post('/api/scripts/reassign', { ids, clientId });
      setReassignResult(res.data);
      setSelectedScripts(new Set());
      await Promise.all([loadScripts(), loadCounts()]);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to reassign scripts.');
    } finally {
      setReassignBusy(false);
    }
  };

  const handleCreate = async (clientId, title, format, rowCount, csvScenes, writerId) => {
    const openInNewTab = !!newScriptDefaults?.openInNewTab;
    // Open the tab synchronously within the click gesture so the browser doesn't
    // treat it as a blocked popup after the await below; we set its URL once created.
    const win = openInNewTab ? window.open('about:blank', '_blank') : null;
    try {
      const body = { clientId, title, format: format || undefined };
      if (writerId) body.writerId = writerId;
      const res = await api.post('/api/scripts', body);
      const id = res.data.id;
      // Longform (D-089): the series is paper-first — no empty rows are seeded;
      // rows arrive from the roster helper / paste importer (a CSV still imports).
      if (format && (format !== 'Longform' || csvScenes)) {
        const scenes = csvScenes || Array.from({ length: rowCount }, () => emptyRow(format));
        if (scenes.length > 0) {
          await api.patch(`/api/scripts/${id}`, { scriptBody: { format, scenes } });
        }
      }
      await loadScripts();
      setShowNew(false);
      setNewScriptDefaults(null);
      if (openInNewTab) {
        // Keep the current script open in this tab; open the new one alongside it.
        const url = `${window.location.origin}/scripts?script=${id}`;
        if (win) win.location = url; else window.open(url, '_blank');
      } else {
        selectScript(id);
      }
    } catch (err) {
      if (win) win.close();
      alert(err.response?.data?.error || 'Failed to create script.');
    }
  };

  // Copy the current script into a standalone "Copy-<name>" script and open it in
  // a new browser tab (so the original and the copy can be edited concurrently).
  const handleDuplicate = async () => {
    if (!selected) return;
    const win = window.open('about:blank', '_blank'); // open within the click gesture
    try {
      const res = await api.post(`/api/scripts/${selected.id}/duplicate`);
      const id = res.data.id;
      await loadScripts();
      const url = `${window.location.origin}/scripts?script=${id}`;
      if (win) win.location = url; else window.open(url, '_blank');
    } catch (err) {
      if (win) win.close();
      alert(err.response?.data?.error || 'Failed to copy script.');
    }
  };

  const handleDeleteScript = async () => {
    if (!selected) return;
    try {
      await api.delete(`/api/scripts/${selected.id}`);
      setDeleteConfirm(false);
      goBackToList();
    } catch (err) {
      setDeleteConfirm(false);
      alert(err.response?.data?.error || 'Failed to delete script.');
    }
  };

  const availableTransitions = getAvailableTransitions(selected, user);
  // Pick the right editor column percentages/drag fn for the current format
  // When the Production Notes column is actually rendered (approved + a format
  // that gets it — not AI), use the "with Production Notes" width arrays so the
  // saved widths and drag handles line up 1:1 with the on-screen columns.
  const useProdCols = showProdNotesCol && PROD_NOTES_INSERT_IDX[format] != null;
  const editorColPcts = useProdCols
    ? (format === 'TH' ? thEditorPctsP : format === 'Mockumentary' ? mockEditorPctsP : format === 'Recipe' ? recipeEditorPctsP : format === 'Longform' ? lfEditorPctsP : skitEditorPctsP)
    : (format === 'TH' ? thEditorPcts  : format === 'AI' ? aiEditorPcts  : format === 'Mockumentary' ? mockEditorPcts  : format === 'Recipe' ? recipeEditorPcts  : format === 'Longform' ? lfEditorPcts : skitEditorPcts);
  const startEditorColDrag = useProdCols
    ? (format === 'TH' ? startThEditorDragP : format === 'Mockumentary' ? startMockEditorDragP : format === 'Recipe' ? startRecipeEditorDragP : format === 'Longform' ? startLfEditorDragP : startSkitEditorDragP)
    : (format === 'TH' ? startThEditorDrag  : format === 'AI' ? startAiEditorDrag  : format === 'Mockumentary' ? startMockEditorDrag  : format === 'Recipe' ? startRecipeEditorDrag  : format === 'Longform' ? startLfEditorDrag : startSkitEditorDrag);
  const resetEditorCols = useProdCols
    ? (format === 'TH' ? resetThEditorColsP : format === 'Mockumentary' ? resetMockEditorColsP : format === 'Recipe' ? resetRecipeEditorColsP : format === 'Longform' ? resetLfEditorColsP : resetSkitEditorColsP)
    : (format === 'TH' ? resetThEditorCols  : format === 'AI' ? resetAiEditorCols  : format === 'Mockumentary' ? resetMockEditorCols  : format === 'Recipe' ? resetRecipeEditorCols  : format === 'Longform' ? resetLfEditorCols : resetSkitEditorCols);
  const isReviewerOrAdmin      = user?.roles?.some(r => r === 'Qual Reviewer' || r === 'Admin' || r === 'Power User');
  const canSeeQualNotesPanel   = user?.roles?.some(r => ['Admin','Power User','Qual Reviewer','Writer'].includes(r));
  // Per-scene qualNotes column: visible to reviewers always, and to writers only when the script needs revision.
  const showQualNotesCol       = canSeeQualNotesPanel && (isReviewerOrAdmin || selected?.qualStatus === 'Needs Revision');
  // The stored defaults reserve a slice for the Qual Notes column. When that column
  // isn't rendered (any non-Reviewer/Admin role on a non-revision script), the
  // content-column percentages sum to ~80%, leaving ~20% unaccounted — which
  // table-layout:fixed then redistributes onto the fixed-pixel # column, ballooning
  // it to 20-30% of table width. Rescale to 100% for the rendered subset so the
  // table lays out cleanly regardless of which columns are visible.
  // `editorColPcts` already matches the rendered content columns (the prod-notes
  // variant includes the Production Notes column in place), with the qualNotes
  // width as its trailing slot. Slice content vs qualNotes by the rendered column
  // count and rescale to 100% so table-layout:fixed doesn't dump leftover width
  // onto the fixed-pixel # column when qualNotes is hidden.
  const { renderedColPcts, qualNotesPct } = (() => {
    const raw        = editorColPcts;
    const contentLen = cols.length;            // rendered content cols (incl. prodNotes when shown)
    const contentRaw = raw.slice(0, contentLen);
    const qnRaw      = raw[contentLen];         // qualNotes slot
    const full = showQualNotesCol ? [...contentRaw, qnRaw] : contentRaw;
    const sum  = full.reduce((a, b) => a + (b || 0), 0);
    const scaled = sum > 0 ? full.map(p => ((p || 0) * 100) / sum) : full;
    return showQualNotesCol
      ? { renderedColPcts: scaled.slice(0, contentLen), qualNotesPct: scaled[contentLen] }
      : { renderedColPcts: scaled, qualNotesPct: null };
  })();
  // Writable by reviewers always; also by the script's own writer when status is Needs Revision (so they can address feedback).
  const canEditQualNotes       = selected?.qualStatus !== 'Posted' &&
                                 (isReviewerOrAdmin || (selected?.writer?.id === user?.id && selected?.qualStatus === 'Needs Revision'));
  const isShootLeadForScript   = location.state?.isShootLeadForScript;
  const fromVideoCard = location.state?.fromVideoCard;
  // Writers may edit any script's body regardless of owner (team policy), so
  // 'Writer' is included alongside the reviewer/admin roles. Read-only only if
  // the user has none of these roles, isn't the script's writer, and isn't a
  // Producer navigated from a Video Card. Mirrors the backend gate in scripts.js.
  // A9: a Posted script is a shipped artifact — read-only for EVERY role, Admins
  // included. This overrides all edit permissions. The escape hatch is the
  // un-post path (pull a card back from Posted → auto-reverts to Approved), not
  // an admin override. `is_archived` has NO effect on editability.
  const isPosted = selected?.qualStatus === 'Posted';
  const canEditBody = !isPosted && (
                      hasRole('Admin', 'Manager', 'Power User', 'Qual Reviewer', 'Writer') ||
                      selected?.writer?.id === user?.id ||
                      (isShootLeadForScript && (hasRole('Producer'))));
  const isReadOnly  = !!selected && !canEditBody;
  // Title can be edited by anyone who can edit the script body, but is locked
  // while the script is Approved/Posted or once production has started. The
  // server decides (titleLocked / titleLockReason from PATCH+GET) so the two
  // can't drift — a narrower role list here (added in cd81e40) silently hid the
  // editable input from Managers / Qual Reviewers / non-owner Writers.
  // Owner 2026-09-08: a past approval no longer locks the title forever —
  // pull the script back to Needs Review and it's editable again.
  // Fallback (a `selected` that didn't come from the detail fetch): assume
  // locked for Approved/Posted rather than offering an input the server refuses.
  const titleLocked = selected?.titleLocked
    ?? (selected?.qualStatus === 'Approved' || selected?.qualStatus === 'Posted');
  const canEditTitle = canEditBody && !titleLocked;
  // C4: Duplicate follows the ORIGINAL writer, so a pure Writer (Writer but not
  // Reviewer/Admin/Power User) duplicating someone else's script would create one
  // they can't see — hide the button (the endpoint also 403s). External client
  // users don't create scripts.
  const isPureWriter = hasRole('Writer') && !hasRole('Admin', 'Power User', 'Qual Reviewer');
  const canDuplicate = !!selected && !user?.isExternal &&
                       !(isPureWriter && selected?.writer?.id !== user?.id);

  // Template create/use is Writer, Qual Reviewer, Power User or Admin (mirrors
  // requireTemplateRole on the server). Deleting one is Admin/PU only. The
  // server enforces both independently — this only hides the controls.
  const canUseTemplates    = !user?.isExternal && hasRole('Writer', 'Qual Reviewer', 'Power User', 'Admin');
  const canDeleteTemplates = !user?.isExternal && hasRole('Admin', 'Power User');

  // The page's existing search + client filters apply to templates too. Writer
  // filter is deliberately not applied: templates are a shared library, and the
  // "writer" of a template is just whoever saved it.
  const templateRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return templates.filter(t => {
      if (clientFilter && t.client?.id !== clientFilter) return false;
      if (!q) return true;
      return (t.templateName || '').toLowerCase().includes(q)
          || (t.scriptName   || '').toLowerCase().includes(q)
          || (t.client?.name || '').toLowerCase().includes(q)
          || (t.writer?.name || '').toLowerCase().includes(q);
    });
  }, [templates, search, clientFilter]);

  // ── Templates: load, save, use, delete ──────────────────────────────────────
  const loadTemplates = useCallback(async () => {
    setTemplatesLoad(true);
    try {
      // The character filter is NOT passed through any more. D-015 passed it
      // because the Character dropdown was visible in this view and a visible
      // control that silently does nothing is worse than one that works. That
      // premise is gone: the filter now lives in the Characters column header,
      // the Templates view has its own list shape and does not render that
      // column, so there is no control here to be inert. Narrowing this list by
      // a filter with no visible cause would be the worse of the two.
      const res = await api.get('/api/scripts?templates=true');
      setTemplates(res.data || []);
    } catch (err) {
      console.error('Failed to load templates:', err);
    } finally {
      setTemplatesLoad(false);
    }
  }, []);

  useEffect(() => { if (templatesView) loadTemplates(); }, [templatesView, loadTemplates]);

  const handleSaveAsTemplate = async (scriptId, templateName) => {
    try {
      const res = await api.post(`/api/scripts/${scriptId}/save-as-template`, { templateName });
      setSaveTplPrompt(null);
      flashCopyFeedback(`Saved as template: ${res.data.templateName}`, 2500);
      if (templatesView) loadTemplates();
    } catch (err) {
      setSaveTplPrompt(null);
      setErrorModal({ message: err.response?.data?.error || 'Could not save this script as a template.' });
    }
  };

  const handleNewFromTemplate = async (template, clientId) => {
    try {
      const res = await api.post(`/api/scripts/from-template/${template.id}`, { clientId });
      setUseTplPrompt(null);
      // Leave the Templates view and open the new script in the editor.
      setTemplatesView(false);
      await loadScripts();
      selectScript(res.data.id);
    } catch (err) {
      setUseTplPrompt(null);
      setErrorModal({ message: err.response?.data?.error || 'Could not create a script from this template.' });
    }
  };

  const handleDeleteTemplate = async (template) => {
    try {
      await api.delete(`/api/scripts/template/${template.id}`);
      setDelTplConfirm(null);
      flashCopyFeedback('Template deleted.', 2000);
      loadTemplates();
    } catch (err) {
      setDelTplConfirm(null);
      setErrorModal({ message: err.response?.data?.error || 'Could not delete this template.' });
    }
  };

  const saveTitleIfChanged = async () => {
    const trimmed = draftTitle.trim();
    if (!trimmed || trimmed === selected?.scriptName) return;
    try {
      const res = await api.patch(`/api/scripts/${selected.id}`, { scriptName: trimmed });
      setSelected(prev => ({ ...prev, scriptName: res.data.scriptName }));
      setScripts(prev => prev.map(s => s.id === selected.id ? { ...s, scriptName: res.data.scriptName } : s));
    } catch (err) {
      console.error('Failed to rename script:', err);
      setDraftTitle(selected.scriptName);
      // A silent revert reads as a broken input; the server's reason (usually a
      // locked title) is the only thing that explains it.
      setErrorModal({
        title:   'Rename Failed',
        message: err.response?.data?.error || 'Could not rename this script.',
      });
    }
  };

  // ── Editor view ──────────────────────────────────────────────────

  if (selected) {
    return (
      <div className="main" style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        <div className="topbar" style={{ paddingBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <button
              className="btn-ghost"
              onClick={backToRef.current
                ? () => { saveBody(); navigate(backToRef.current.path, { state: backToRef.current.state }); }
                : fromVideoCard ? () => navigate(-1) : goBackToList}
              style={{ flexShrink: 0 }}
            >
              {backToRef.current ? (backToRef.current.label || '← Back') : fromVideoCard ? '← Video Card' : '← Scripts'}
            </button>
            <div style={{ minWidth: 0 }}>
              {canEditTitle ? (
                <input
                  className="page-title"
                  style={{ fontSize: 17, background: 'transparent', border: 'none', outline: 'none', padding: 0, width: '100%', cursor: 'text', fontFamily: 'inherit', color: 'inherit', fontWeight: 'inherit' }}
                  value={draftTitle}
                  onChange={e => setDraftTitle(e.target.value)}
                  onBlur={saveTitleIfChanged}
                  onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
                />
              ) : (
                <div
                  className="page-title"
                  style={{ fontSize: 17 }}
                  title={titleLocked
                    ? (selected?.titleLockReason
                       || 'Title is locked — move this script back to Needs Review to rename it.')
                    : undefined}
                >{selected.scriptName}</div>
              )}
              <div className="page-sub" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
                <span>{selected.client?.name}</span>
                {format && <span className={formatBadgeClass(format)} title={formatTitle(format)} data-testid="script-format-badge">{formatLabel(format)}</span>}
                <span style={{ color: 'var(--tx-faint)' }}>v{selected.version}</span>
              </div>
            </div>
          </div>
          <div className="topbar-actions">
            {/* Save status — previously there was no indicator at all, so a
                failing save (e.g. a 413 on an oversized body) was invisible. */}
            {!isReadOnly && (
              saveError?.largeDeletion ? (
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, padding: '5px 10px', borderRadius: 6, background: 'rgba(212,85,85,0.14)', border: '1px solid rgba(212,85,85,0.45)', color: 'var(--s-incomplete-t)', fontSize: 11, fontWeight: 600 }}
                  title={saveError.largeDeletion.message || 'This save would remove most of the script. Your copy may be out of date.'}
                >
                  <span>⚠ Large deletion blocked — your copy may be out of date</span>
                  <button
                    className="btn-ghost"
                    style={{ padding: '2px 8px', fontSize: 11 }}
                    onClick={() => selectScript(selected.id)}
                  >
                    Reload latest
                  </button>
                  <button
                    className="btn-ghost"
                    style={{ padding: '2px 8px', fontSize: 11 }}
                    onClick={saveAnyway}
                  >
                    Save anyway
                  </button>
                </div>
              ) : saveError?.retrying ? (
                <span
                  style={{ flexShrink: 0, fontSize: 11, fontWeight: 600, color: '#b26b00' }}
                  title="The last save didn't go through — retrying automatically. Your changes are still here, not yet on the server."
                >
                  ↻ Reconnecting — retrying save…
                </span>
              ) : saveError ? (
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, padding: '5px 10px', borderRadius: 6, background: 'rgba(212,85,85,0.14)', border: '1px solid rgba(212,85,85,0.45)', color: 'var(--s-incomplete-t)', fontSize: 11, fontWeight: 600 }}
                  title={saveError.tooLarge ? 'The script is too large to save. Tell an admin — the save size limit needs raising.' : 'The last save did not go through. Your latest edits are not saved on the server.'}
                >
                  <span>⚠ Not saved{saveError.tooLarge ? ' — script too large' : ''}</span>
                  <button
                    className="btn-ghost"
                    style={{ padding: '2px 8px', fontSize: 11 }}
                    onClick={() => saveBody(true)}
                  >
                    Retry
                  </button>
                </div>
              ) : saving ? (
                <span style={{ flexShrink: 0, fontSize: 11, color: 'var(--tx-muted)' }}>Saving…</span>
              ) : isDirty ? (
                <span
                  style={{ flexShrink: 0, fontSize: 11, fontWeight: 600, color: '#b26b00' }}
                  title="You have unsaved changes. They'll save automatically in a moment."
                >
                  ● Unsaved…
                </span>
              ) : lastSaved ? (
                <span style={{ flexShrink: 0, fontSize: 11, color: 'var(--tx-muted)' }}>
                  Saved {lastSaved.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                </span>
              ) : null
            )}
            {/* P2: same-cell collision notice. Non-blocking — your edit was saved; this
                just flags that someone else had also changed those cell(s). Full resolve
                UI is P4. */}
            {!isReadOnly && cellConflicts.length > 0 && (
              <div
                style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, padding: '5px 10px', borderRadius: 6, background: 'rgba(178,107,0,0.14)', border: '1px solid rgba(178,107,0,0.45)', color: '#b26b00', fontSize: 11, fontWeight: 600 }}
                title={'Another editor also changed ' + (cellConflicts.length === 1 ? 'this cell' : 'these cells') + ' while you were editing. Your version was saved.'}
              >
                <span>⚠ {cellConflicts.length} cell{cellConflicts.length === 1 ? '' : 's'} also edited elsewhere — yours was saved</span>
                <button
                  className="btn-ghost"
                  style={{ padding: '2px 8px', fontSize: 11 }}
                  onClick={() => setCellConflicts([])}
                >
                  Dismiss
                </button>
              </div>
            )}
            {/* Copy Link / Delete / Find & Replace / Duplicate / Save as Template
                moved into the Actions ▾ menu, and Share / Print PDF / Export Word /
                Export CSV into Export ▾ — both sit further right, before
                + New Script. The toolbar had outgrown its width. */}
            {selected.sourceUrl && (
              <a
                className="btn-ghost"
                href={selected.sourceUrl}
                target="_blank"
                rel="noreferrer"
                title="Open the original source of this script in a new tab"
                style={{ flexShrink: 0, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 5 }}
              >
                <span style={{ fontSize: 13 }}>👁</span> View Original ↗
              </a>
            )}
            {/* Cross-script copy — appears whenever cells or rows are selected. */}
            {format && getActiveSelection() && (
              <button
                className="btn-ghost"
                style={{ flexShrink: 0 }}
                onClick={copySelectionToAppClipboard}
                title="Copy the current selection — paste into this or any other script (Ctrl/Cmd+C)"
              >
                ⎘ Copy Selection
              </button>
            )}
            {/* Cross-script paste — persists across navigation, so it shows on any
                script while the app clipboard holds content. */}
            {format && !isReadOnly && appClipCount > 0 && (
              <button
                className="btn-ghost"
                style={{ flexShrink: 0, color: 'var(--violet)', borderColor: 'var(--violet-glow)' }}
                onClick={pasteFromAppClipboard}
                title="Paste the copied rows here (Ctrl/Cmd+V)"
              >
                ↑ Paste Copied ({appClipCount})
              </button>
            )}
            {format && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 1, border: '1px solid var(--border)', borderRadius: 7, padding: '0 3px', height: 32, flexShrink: 0 }}>
                {[['**','B','bold'],['*','I','italic'],['__','U','underline']].map(([marker, label, title]) => (
                  <button
                    key={marker}
                    title={`${title} (Ctrl+${label})`}
                    onMouseDown={e => { e.preventDefault(); if (focusedCell) applyMarkdown(marker, focusedCell.rowIdx, focusedCell.colKey); }}
                    style={{ background: 'none', border: 'none', cursor: focusedCell ? 'pointer' : 'default', color: focusedCell ? 'var(--tx-secondary)' : 'var(--tx-faint)', fontSize: 12, fontWeight: label === 'B' ? 700 : 400, fontStyle: label === 'I' ? 'italic' : 'normal', textDecoration: label === 'U' ? 'underline' : 'none', padding: '0 5px', lineHeight: 1, fontFamily: 'var(--font)', minWidth: 22, minHeight: 22 }}
                  >{label}</button>
                ))}
              </div>
            )}
            {format && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 2, border: '1px solid var(--border)', borderRadius: 7, padding: '0 4px', height: 32, flexShrink: 0 }}>
                <button
                  onClick={() => {
                    const next = Math.max(8, editorFontSize - 1);
                    setEditorFontSize(next);
                    if (selected?.id) localStorage.setItem(`fontSize_script_${selected.id}`, next);
                  }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--tx-secondary)', fontSize: 14, padding: '0 4px', lineHeight: 1, fontFamily: 'var(--font)' }}
                  title="Decrease font size"
                >−</button>
                <span style={{ fontSize: 11, color: 'var(--tx-secondary)', minWidth: 22, textAlign: 'center', userSelect: 'none' }}>{editorFontSize}</span>
                <button
                  onClick={() => {
                    const next = Math.min(24, editorFontSize + 1);
                    setEditorFontSize(next);
                    if (selected?.id) localStorage.setItem(`fontSize_script_${selected.id}`, next);
                  }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--tx-secondary)', fontSize: 14, padding: '0 4px', lineHeight: 1, fontFamily: 'var(--font)' }}
                  title="Increase font size"
                >+</button>
              </div>
            )}
            {format && (
              <button
                className="btn-ghost"
                style={{ flexShrink: 0 }}
                onClick={resetEditorCols}
                title="Reset column widths to defaults (use if the # column appears unusually wide)"
              >
                Reset Columns
              </button>
            )}
            {isReadOnly ? (
              <span style={{ fontSize: 11, color: 'var(--tx-faint)', alignSelf: 'center' }}>Read only</span>
            ) : (
              <>
                {availableTransitions.filter(s => s !== 'Backburner').map(s => (
                  <button key={s} className={transitionBtnClass(s)} onClick={() => handleTransition(s)} disabled={transitioning}>
                    {transitionBtnLabel(s, selected)}
                  </button>
                ))}
                {availableTransitions.includes('Backburner') && (
                  <button className="action-btn action-backburner" onClick={() => handleTransition('Backburner')} disabled={transitioning}>
                    Backburner
                  </button>
                )}
              </>
            )}
            {!isReadOnly && (
              <button
                className="btn-ghost"
                onClick={() => saveBody(true)}
                style={{ flexShrink: 0 }}
              >
                Save
              </button>
            )}
            {/* Actions ▾ — Copy Link / Find & Replace / Duplicate / Save as Template /
                Delete, which used to be five separate buttons spread across the bar.
                Each item keeps the exact condition its button had, expressed by
                passing a null handler when it shouldn't appear. Delete is last,
                below a divider, so the destructive item isn't next to the others. */}
            <ToolbarMenu
              label="Actions ▾"
              title="Copy a link, find & replace, duplicate, save as a template, or delete this script"
              items={[
                {
                  label: scriptLinkCopied ? 'Copied!' : '⎘ Copy Link',
                  key: 'copy-link',
                  keepOpen: true, // so 'Copied!' is visible rather than closing over it
                  onClick: () => {
                    navigator.clipboard.writeText(`${window.location.origin}/scripts?script=${selected.id}`);
                    setScriptLinkCopied(true);
                    setTimeout(() => setScriptLinkCopied(false), 2000);
                  },
                },
                {
                  label: 'Find & Replace',
                  title: 'Find & Replace (Ctrl+H)',
                  onClick: (!isReadOnly && format) ? (() => setFindReplaceOpen(true)) : null,
                },
                {
                  label: '⎘ Duplicate',
                  title: 'Duplicate into a new I/P version (opens in a new tab). Works on any script, including Posted.',
                  onClick: canDuplicate ? handleDuplicate : null,
                },
                {
                  label: '⎘ Save as Template',
                  title: "Save this script as a reusable template. The template keeps this script's format and content; this script is not changed.",
                  onClick: canUseTemplates
                    ? (() => setSaveTplPrompt({ scriptId: selected.id, defaultName: selected.scriptName || '' }))
                    : null,
                },
                {
                  label: 'Delete',
                  danger: true,
                  dividerBefore: true,
                  onClick: (hasRole('Admin', 'Qual Reviewer', 'Power User') || selected.writer?.id === user?.id)
                    ? (() => setDeleteConfirm(true))
                    : null,
                },
              ]}
            />
            {/* Export ▾ — Share / Print PDF / Export Word / Export CSV, which
                used to be four separate buttons crowding the left of the bar.
                Word and CSV need a scene table, so they only appear when the
                script has a format (matching the old per-button conditions). */}
            <ToolbarMenu
              label="Export ▾"
              title="Share this script, print it, or export it to Word or CSV"
              items={[
                { label: 'Share',       onClick: () => setShareOpen(true) },
                { label: 'Print PDF',   onClick: handlePrint },
                { label: 'Export Word', onClick: format ? handleExportDocx : null },
                { label: 'Export CSV',  onClick: format ? handleExportSceneCSV : null },
              ]}
            />
            <button
              className="btn-ghost"
              style={{ flexShrink: 0 }}
              title="Create another script for this project — opens in a new tab so you can write both at once"
              onClick={() => { setNewScriptDefaults({ clientId: selected.client?.id || '', openInNewTab: true }); setShowNew(true); }}
            >
              + New Script
            </button>
            <button
              className="btn-ghost"
              style={{ flexShrink: 0 }}
              onClick={() => setClioOpen(o => !o)}
            >
              {clioOpen ? 'Close Clio' : 'Clio ✦'}
            </button>
          </div>
        </div>

        {/* Editor + Clio drawer */}
        <div style={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>
          {/* Script editor column */}
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

          {/* Status flow bar */}
          <div className="status-flow">
            {STATUS_FLOW_STEPS.map((step, i) => {
              const isCurrent = selected.qualStatus === step;
              return (
                <span key={step} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {i > 0 && <span className="sf-arrow">→</span>}
                  <span className={`sf-pill ${PILL_CLASS[step]}${isCurrent ? ' current' : ' dim'}`}>{step}</span>
                </span>
              );
            })}
            {(selected.qualStatus === 'Needs Revision' || selected.qualStatus === 'Backburner') && (
              <>
                <span style={{ color: 'var(--tx-faint)', fontSize: 11, margin: '0 4px' }}>·</span>
                <span className={`sf-pill ${PILL_CLASS[selected.qualStatus]} current`}>{selected.qualStatus}</span>
              </>
            )}
            {/* Live spoken-word count + read-aloud runtime. Ambient readout,
                muted to match the bar. marginLeft:auto groups it (and the
                Skip button that follows) at the right without reflowing the
                pipeline pills. Hidden for formats with no spoken column
                (None / Riff). Not gated by isReadOnly — a reviewer needs the
                runtime too, and WPM is a client-side personal preference. */}
            {spokenKey && (
              <span
                style={{
                  marginLeft: 'auto',
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  fontSize: 11, color: 'var(--tx-muted)',
                  whiteSpace: 'nowrap', fontFamily: 'var(--font)',
                }}
                title="Spoken words and estimated read-aloud runtime"
              >
                <span>{formatWordCount(spokenWordCount)} words</span>
                <span style={{ color: 'var(--tx-faint)' }}>·</span>
                <span>{formatRuntime(spokenWordCount, wpm)}</span>
                <span style={{ color: 'var(--tx-faint)' }}>@</span>
                <input
                  type="text"
                  inputMode="numeric"
                  value={wpmInput}
                  onChange={handleWpmChange}
                  onBlur={handleWpmBlur}
                  onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                  aria-label="Words per minute"
                  title={`Words per minute (${WPM_MIN}–${WPM_MAX})`}
                  style={{
                    width: 34, textAlign: 'center',
                    background: 'var(--bg-input)', border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-sm)', color: 'var(--tx-secondary)',
                    fontSize: 11, fontFamily: 'var(--font)', padding: '1px 3px',
                  }}
                />
                <span>wpm</span>
              </span>
            )}
            <button
              type="button"
              onClick={() => {
                const scroller = document.querySelector('.script-scroll-body');
                if (scroller) scroller.scrollTo({ top: scroller.scrollHeight, behavior: 'smooth' });
              }}
              style={{
                marginLeft: 'auto',
                background: 'none',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--tx-secondary)',
                fontSize: 11,
                padding: '3px 10px',
                cursor: 'pointer',
                fontFamily: 'var(--font)',
                whiteSpace: 'nowrap',
              }}
              title="Jump to the bottom of the script"
            >
              ↓ Skip to bottom
            </button>
          </div>

          {/* Editor body — plain scroll container, layout handled by inner div */}
          <div className="script-scroll-body" style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          <div style={{ padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="script-print-header">{selected.scriptName}</div>

            {/* In Transcript — CTF clients only */}
            {selected.client?.serviceType === 'CTF' && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--tx-secondary)', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={!!selected.inTranscript}
                  onChange={async e => {
                    try {
                      const res = await api.patch(`/api/scripts/${selected.id}`, { inTranscript: e.target.checked });
                      setSelected(res.data);
                    } catch (err) {
                      console.error('Failed to update inTranscript:', err);
                    }
                  }}
                  style={{ accentColor: 'var(--violet)' }}
                />
                In Transcript
              </label>
            )}

            {/* Reviewer Notes — always visible to qual-note-eligible users, editable by reviewers */}
            {canSeeQualNotesPanel && (
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--tx-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 5 }}>
                  Reviewer Notes
                </label>
                <textarea
                  value={draftNotes}
                  spellCheck={true}
                  onChange={isReviewerOrAdmin && !isPosted ? e => setDraftNotes(e.target.value) : undefined}
                  onBlur={isReviewerOrAdmin && !isPosted ? () => saveBody() : undefined}
                  readOnly={!isReviewerOrAdmin || isPosted}
                  placeholder={isReviewerOrAdmin ? 'Overall notes for the writer…' : ''}
                  style={{
                    width: '100%', boxSizing: 'border-box',
                    minHeight: 56,
                    background: 'var(--bg-input)',
                    border: `1px solid ${draftNotes && selected.qualStatus === 'Needs Revision' ? 'rgba(212,85,85,0.45)' : 'var(--border)'}`,
                    borderRadius: 'var(--radius-sm)',
                    padding: '8px 10px',
                    fontSize: 13,
                    color: draftNotes ? 'var(--tx-secondary)' : 'var(--tx-faint)',
                    lineHeight: 1.55,
                    resize: 'vertical',
                    fontFamily: 'var(--font)',
                    cursor: isReviewerOrAdmin ? 'text' : 'default',
                  }}
                />
              </div>
            )}

            {/* Scene table */}
            {cols.length > 0 && draftBody?.scenes != null ? (
              <div className="editor-card">
                {/* Hidden catcher — focused while select-all is active so paste/copy
                    events fire on it (not on a cell textarea, not on nothing). */}
                {selectAllActive && (
                  <textarea
                    ref={pasteCatcherRef}
                    aria-hidden="true"
                    tabIndex={-1}
                    readOnly
                    value=""
                    onChange={() => {}}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        e.preventDefault();
                        setSelectAllActive(false);
                      }
                    }}
                    onPaste={(e) => {
                      e.preventDefault();
                      if (isReadOnly || !format || !draftBody) return;
                      let newScenes = null;
                      const html = e.clipboardData?.getData('text/html');
                      if (html && html.trim()) {
                        const parsed = parseScenesFromHTML(html, format);
                        if (parsed && parsed.length > 0) newScenes = parsed;
                      }
                      if (!newScenes) {
                        const text = e.clipboardData?.getData('text/plain') || '';
                        if (text.trim()) newScenes = parseScenesPositional(text, format, '\t');
                      }
                      if (!newScenes || newScenes.length === 0) {
                        flashCopyFeedback('Clipboard is empty.', 2000);
                        return;
                      }
                      pushUndo(draftBody);
                      setDraftBody({ ...draftBody, scenes: newScenes });
                      setSelectAllActive(false);
                      flashCopyFeedback(
                        `${newScenes.length} row${newScenes.length !== 1 ? 's' : ''} pasted from clipboard.`,
                        2500
                      );
                    }}
                    onCopy={(e) => {
                      e.preventDefault();
                      if (!format || !draftBody?.scenes?.length || !hasRealScriptContent(draftBody.scenes)) {
                        flashCopyFeedback('Nothing to copy — script is empty.', 2000);
                        return;
                      }
                      const tsv  = buildScriptTSV(draftBody.scenes, format);
                      const html = buildScriptHTML(draftBody.scenes, format);
                      e.clipboardData?.setData('text/plain', tsv);
                      e.clipboardData?.setData('text/html',  html);
                      flashCopyFeedback('Script copied — paste into any spreadsheet.', 2000);
                    }}
                    style={{
                      position: 'fixed',
                      top: 0,
                      left: 0,
                      width: 1,
                      height: 1,
                      opacity: 0,
                      pointerEvents: 'none',
                      zIndex: -1,
                      border: 'none',
                      padding: 0,
                      resize: 'none',
                    }}
                  />
                )}
                {/* Contextual toolbar — shown while the # select-all state is active */}
                {selectAllActive && (
                  <div
                    data-clipboard-toolbar
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '10px 16px',
                      background: 'var(--violet-soft)',
                      borderBottom: '1px solid var(--violet-glow)',
                      fontSize: 12,
                    }}
                  >
                    <span style={{ color: 'var(--violet)', fontWeight: 600 }}>
                      All {draftBody.scenes.length} row{draftBody.scenes.length !== 1 ? 's' : ''} selected
                    </span>
                    <button
                      className="btn-ghost"
                      style={{ padding: '5px 12px', fontSize: 11 }}
                      onClick={copyScriptToClipboard}
                      title="Copy script as TSV to the system clipboard (Ctrl+C)"
                    >
                      ⎘ Copy to clipboard
                    </button>
                    {!isReadOnly && (
                      <button
                        className="btn-ghost"
                        style={{ padding: '5px 12px', fontSize: 11 }}
                        onClick={pasteFromClipboard}
                        title="Paste TSV from Google Sheets or Excel (Ctrl+V)"
                      >
                        ↑ Paste from clipboard
                      </button>
                    )}
                    <span style={{ marginLeft: 'auto', color: 'var(--tx-faint)', fontSize: 10 }}>
                      Esc or click outside to deselect
                    </span>
                  </div>
                )}
                {(format === 'Longform' || selected?.scriptBody?.format === 'Longform') && (
                  <LongformSeriesPanel
                    script={selected}
                    draftBody={draftBody}
                    canEdit={!isReadOnly}
                    registryNames={registryNames}
                    onReload={() => selectScript(selected.id)}
                    user={user}
                    refreshKey={lastSaved}
                    onInsertEpisodeBreak={() => insertEpisodeBreakAt(draftBody?.scenes?.length || 0)}
                    onStartWriting={startWritingScene1}
                    onAddEpisode={addEpisodeToScript}
                    onSetEpisodeTitle={setEpisodeTitleInScript}
                    episodesVersion={lfEpisodesVersion}
                    onSceneAdded={handleLongformSceneAdded}
                    onEpisodesChanged={eps => setLfEpisodes(eps)}
                  />
                )}
                {/* D-117: a blank Longform series shows the panel's entry choices in place of the empty grid. */}
                <table className="scene-table" style={{ tableLayout: 'fixed', width: '100%', ...((format === 'Longform' || selected?.scriptBody?.format === 'Longform') && !isReadOnly && (draftBody?.scenes?.length || 0) === 0 ? { display: 'none' } : {}) }}>
                  <thead className="scene-head">
                    <tr>
                      <th
                        className="scene-th"
                        style={{
                          width: 68,
                          cursor: 'pointer',
                          userSelect: 'none',
                          background: selectAllActive
                            ? 'var(--violet-soft)'
                            : (hashCellHover ? 'var(--bg-hover)' : undefined),
                          color: selectAllActive ? 'var(--violet)' : undefined,
                          transition: 'background 0.1s, color 0.1s',
                        }}
                        onMouseEnter={() => setHashCellHover(true)}
                        onMouseLeave={() => setHashCellHover(false)}
                        onClick={() => {
                          // Drop focus out of any cell so the next Ctrl+V doesn't get
                          // swallowed by a textarea's native paste handler.
                          const a = document.activeElement;
                          if (a && (a.tagName === 'TEXTAREA' || a.tagName === 'INPUT')) a.blur();
                          setSelectAllActive(v => !v);
                        }}
                        title="Select all rows for copy/paste with Google Sheets"
                      >#</th>
                      {cols.map((c, colIdx) => {
                        const isLast = colIdx === cols.length - 1 && !showQualNotesCol;
                        return (
                          <th
                            key={c.key}
                            className="scene-th"
                            style={{ width: `${renderedColPcts[colIdx]}%`, position: 'relative', userSelect: 'none' }}
                          >
                            {c.key === 'character' && isCharacterFormat && registryClientId ? (
                              // Run 2 §4.2: clicking the Character column header opens
                              // the client's character-registry modal.
                              <span
                                role="button"
                                tabIndex={0}
                                data-testid="character-header"
                                title="Manage this project's character list"
                                onClick={() => setRegistryModalOpen(true)}
                                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setRegistryModalOpen(true); } }}
                                style={{ cursor: 'pointer', textDecoration: 'underline dotted', textUnderlineOffset: 3 }}
                              >
                                {c.label} ▾
                              </span>
                            ) : c.label}
                            {!isLast && (
                              <div
                                style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 6, cursor: 'col-resize', zIndex: 2 }}
                                onMouseDown={e => startEditorColDrag(colIdx, e)}
                              />
                            )}
                          </th>
                        );
                      })}
                      {showQualNotesCol && (
                        <th className="scene-th" style={{ width: `${qualNotesPct}%`, position: 'relative', userSelect: 'none' }}>
                          Qual Notes
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {draftBody.scenes.map((row, idx) => {
                      const totalRows = draftBody.scenes.length;
                      const gp = lfGroupPlan ? lfGroupPlan[idx] : null;
                      const bandSpan = cols.length + 1 + (showQualNotesCol ? 1 : 0);
                      const bands = gp && (gp.episodeBand || gp.sceneBand) ? (
                        <>
                          {gp.episodeBand && (
                            <tr className={`lf-band lf-band-ep${lfCollapsedEpisodes.has(gp.episodeBand.key) ? ' collapsed' : ''}`} data-testid="lf-band-episode" data-band-key={gp.episodeBand.key} data-episode={gp.episodeBand.episodeNumber ?? undefined} data-card={gp.episodeBand.cardId ? 'true' : 'false'}>
                              <td colSpan={bandSpan}>
                                <div className="lf-band-row">
                                  <button type="button" className="lf-band-toggle" aria-expanded={!lfCollapsedEpisodes.has(gp.episodeBand.key)} onClick={() => lfToggleEpisode(gp.episodeBand.key)} title={lfCollapsedEpisodes.has(gp.episodeBand.key) ? 'Expand this episode' : 'Collapse this episode'}>
                                    <span className="lf-band-chevron">{lfCollapsedEpisodes.has(gp.episodeBand.key) ? '▸' : '▾'}</span>
                                    {lfBandEditing?.k === gp.episodeBand.episodeNumber
                                      ? <input autoFocus className="lf-band-title-input" data-testid="lf-band-title-input" value={lfBandEditing.value} onClick={e => e.stopPropagation()} onChange={e => setLfBandEditing({ k: gp.episodeBand.episodeNumber, value: e.target.value })}
                                          onBlur={() => { const v = String(lfBandEditing?.value || '').trim(); setLfBandEditing(null); if (v !== (gp.episodeBand.title || '')) setEpisodeTitleInScript(gp.episodeBand.episodeNumber, v); }}
                                          onKeyDown={e => { e.stopPropagation(); if (e.key === 'Enter') e.currentTarget.blur(); if (e.key === 'Escape') setLfBandEditing(null); }} />
                                      : <span className="lf-band-label">{gp.episodeBand.label}</span>}
                                  </button>
                                  {!isReadOnly && <button type="button" className="lf-band-action" data-testid="lf-band-rename" title={gp.episodeBand.untitled ? 'Name this episode (writes its EPISODE header row)' : 'Rename this episode (edits its EPISODE header row)'} onClick={() => setLfBandEditing({ k: gp.episodeBand.episodeNumber, value: gp.episodeBand.title || '' })}>✎</button>}
                                  {gp.episodeBand.cardId && <button type="button" className="lf-band-action" data-testid="lf-band-open-card" title="Open this episode's production card" onClick={() => navigate(`/production/${gp.episodeBand.cardId}`)}>↗ card</button>}
                                  {!isReadOnly && gp.episodeBand.episodeNumber > 1 && <button type="button" className="lf-band-action lf-band-remove" data-testid="lf-band-remove" title="Remove this episode break — the rows below join the episode above" onClick={() => { if (window.confirm(`Remove the break that starts Ep ${gp.episodeBand.episodeNumber}? Its rows join Ep ${gp.episodeBand.episodeNumber - 1}.`)) removeEpisodeBoundary(gp.episodeBand.episodeNumber); }}>✕ break</button>}
                                </div>
                              </td>
                            </tr>
                          )}
                          {gp.sceneBand && (
                            <tr className={`lf-band lf-band-scene${lfCollapsedScenes.has(gp.sceneBand.key) ? ' collapsed' : ''}`} data-testid="lf-band-scene" data-scene={gp.sceneBand.sceneNumber} data-episode={gp.sceneBand.episodeNumber ?? undefined}>
                              <td colSpan={bandSpan}>
                                <button type="button" className="lf-band-toggle" aria-expanded={!lfCollapsedScenes.has(gp.sceneBand.key)} onClick={() => lfToggleScene(gp.sceneBand.key)} title={lfCollapsedScenes.has(gp.sceneBand.key) ? 'Expand this scene' : 'Collapse this scene'}>
                                  <span className="lf-band-chevron">{lfCollapsedScenes.has(gp.sceneBand.key) ? '▸' : '▾'}</span>
                                  <span className="lf-band-label">{gp.sceneBand.sceneNumber ? `Scene ${gp.sceneBand.sceneNumber}` : 'Unnumbered'}</span>
                                  {gp.sceneBand.description && <span className="lf-band-desc">{gp.sceneBand.description}</span>}
                                  <span className="lf-band-count">{gp.sceneBand.rows} row{gp.sceneBand.rows === 1 ? '' : 's'}</span>
                                </button>
                              </td>
                            </tr>
                          )}
                        </>
                      ) : null;
                      if (gp?.hidden) return <React.Fragment key={idx}>{bands}</React.Fragment>;
                      return (
                        <React.Fragment key={idx}>
                        {bands}
                        <SceneRow
                          idx={idx}
                          row={row}
                          cols={cols}
                          format={format}
                          totalRows={totalRows}
                          isReadOnly={isReadOnly}
                          editorFontSize={editorFontSize}
                          showQualNotesCol={showQualNotesCol}
                          canEditQualNotes={canEditQualNotes}
                          registryNames={registryNames}
                          canEditRegistry={canEditRegistry}
                          isSelected={selectedRows.has(idx) || selectAllActive}
                          isCut={cutRows.has(idx)}
                          isHovered={hoveredRow === idx}
                          dropMarker={
                            dropTargetIdx === idx
                              ? 'above'
                              : (dropTargetIdx === idx + 1 && idx === totalRows - 1 ? 'end' : null)
                          }
                          autocomplete={autocomplete && autocomplete.rowIdx === idx ? autocomplete : null}
                          focusedColKey={focusedCell?.rowIdx === idx ? focusedCell.colKey : null}
                          editingColKey={editingCell?.rowIdx === idx ? editingCell.colKey : null}
                          editingMode={editingCell?.rowIdx === idx ? editingCell.mode : null}
                          selectedColRange={(() => {
                            // Spec 1.5: per-row signature for the active cell range.
                            // String form so React.memo's shallow compare can skip rows
                            // whose membership didn't change during a drag.
                            if (!cellSelection) return '';
                            const r1 = Math.min(cellSelection.startRow, cellSelection.endRow);
                            const r2 = Math.max(cellSelection.startRow, cellSelection.endRow);
                            if (idx < r1 || idx > r2) return '';
                            const c1 = Math.min(cellSelection.startCol, cellSelection.endCol);
                            const c2 = Math.max(cellSelection.startCol, cellSelection.endCol);
                            if (r1 === r2 && c1 === c2) return ''; // 1×1, no highlight
                            return `${c1}-${c2}`;
                          })()}
                          multiSelectedColIdxs={(() => {
                            // Non-contiguous multi-selection: comma-joined colIdxs in this row.
                            if (cellMultiSelection.size === 0) return '';
                            const prefix = `${idx}:`;
                            const idxs = [];
                            for (const id of cellMultiSelection) {
                              if (id.startsWith(prefix)) {
                                const ci = colKeyToIdx[id.slice(prefix.length)];
                                if (ci != null) idxs.push(ci);
                              }
                            }
                            if (!idxs.length) return '';
                            idxs.sort((a, b) => a - b);
                            return idxs.join(',');
                          })()}
                          moveTargetColRange={(() => {
                            // Spec 4.1/4.2: per-row target colIdx range during a move-drag.
                            // Empty string when this row isn't a target row.
                            if (!moveDrag) return '';
                            const offsetR = moveDrag.targetRow - moveDrag.anchorRow;
                            const offsetC = moveDrag.targetCol - moveDrag.anchorCol;
                            const tr1 = moveDrag.r1 + offsetR;
                            const tr2 = moveDrag.r2 + offsetR;
                            if (idx < tr1 || idx > tr2) return '';
                            return `${moveDrag.c1 + offsetC}-${moveDrag.c2 + offsetC}`;
                          })()}
                          autofillHandleColIdx={(() => {
                            // Spec 5.2: anchor cell for the autofill handle — bottom-right
                            // of the multi-cell range, or the focused cell if no range.
                            if (cellSelection && (cellSelection.startRow !== cellSelection.endRow || cellSelection.startCol !== cellSelection.endCol)) {
                              const anchorR = Math.max(cellSelection.startRow, cellSelection.endRow);
                              if (anchorR !== idx) return null;
                              return Math.max(cellSelection.startCol, cellSelection.endCol);
                            }
                            if (focusedCell?.rowIdx !== idx) return null;
                            return colKeyToIdx[focusedCell.colKey] ?? null;
                          })()}
                          autofillPreviewRange={(() => {
                            // Spec 5.2: per-row colIdx range that's previewing fill content.
                            // Only rows BELOW the source range (sourceR2 < idx <= targetEndRow)
                            // get a preview tint, since source cells already show their values.
                            if (!autofillDrag) return '';
                            if (idx <= autofillDrag.sourceR2 || idx > autofillDrag.targetEndRow) return '';
                            return `${autofillDrag.sourceC1}-${autofillDrag.sourceC2}`;
                          })()}
                          colorPickerOpen={colorPickerRow === idx}
                          colorPickerCellKey={colorPickerCell?.rowIdx === idx ? colorPickerCell.colKey : null}
                          linkPopoverColKey={linkPopover?.rowIdx === idx ? linkPopover.colKey : null}
                          linkDraft={linkPopover?.rowIdx === idx ? linkDraft : null}
                          setHoveredRow={setHoveredRow}
                          setColorPickerRow={setColorPickerRow}
                          setColorPickerCell={setColorPickerCell}
                          setLinkPopover={setLinkPopover}
                          setLinkDraft={setLinkDraft}
                          setAutocomplete={setAutocomplete}
                          setSelectedRows={setSelectedRows}
                          setSelectionAnchor={setSelectionAnchor}
                          setSelectAllActive={setSelectAllActive}
                          setFocusedCell={setFocusedCell}
                          handlers={rowHandlers}
                          draftBodyRef={draftBodyRef}
                          selectedRowsSizeRef={selectedRowsSizeRef}
                          selectAllActiveRef={selectAllActiveRef}
                          lastFocusBodyRef={lastFocusBodyRef}
                        />
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              !format && (
                <div style={{ color: 'var(--tx-faint)', fontSize: 12 }}>
                  No script format — body unavailable for this script type
                </div>
              )
            )}

            {/* Activity Log */}
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16 }}>
              <button
                className="btn-ghost"
                style={{ fontSize: 11, padding: '4px 10px', marginBottom: activityOpen ? 14 : 0 }}
                onClick={async () => {
                  if (!activityOpen) {
                    setActivityLoading(true);
                    try {
                      const res = await api.get(`/api/audit/script/${selected.id}`);
                      setActivityLog(res.data);
                    } catch {
                      setActivityLog([]);
                    } finally {
                      setActivityLoading(false);
                    }
                  }
                  setActivityOpen(o => !o);
                }}
              >
                {activityOpen ? '▲' : '▼'} Activity Log
              </button>

              {activityOpen && (
                activityLoading ? (
                  <div style={{ fontSize: 11, color: 'var(--tx-muted)' }}>Loading…</div>
                ) : activityLog.length === 0 ? (
                  <div style={{ fontSize: 11, color: 'var(--tx-muted)' }}>No activity recorded.</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                    {activityLog.map((entry, i) => {
                      const date = new Date(entry.changedAt);
                      const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                      const timeStr = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
                      const who = entry.changedByMember?.name || 'Unknown';

                      let label = '';
                      if (entry.fieldChanged === 'qual_status') {
                        if (!entry.oldValue) label = 'Script created';
                        else if (entry.newValue === 'Needs Review')   label = entry.oldValue === 'Approved' ? 'Pulled back to Needs Review' : 'Submitted for review';
                        else if (entry.newValue === 'Approved')       label = 'Approved';
                        else if (entry.newValue === 'Needs Revision') label = 'Sent back for revision';
                        else if (entry.newValue === 'Backburner')     label = 'Moved to Backburner';
                        else if (entry.newValue === 'I/P')            label = entry.oldValue === 'Backburner' ? 'Restored from Backburner' : 'Returned to I/P';
                        else label = `Status → ${entry.newValue}`;
                      } else if (entry.fieldChanged === 'deleted') {
                        label = 'Script deleted';
                      } else if (entry.fieldChanged === 'script_body') {
                        label = 'Script content edited';
                      } else if (entry.fieldChanged === 'script_saved') {
                        label = 'Saved manually';
                      } else if (entry.fieldChanged === 'script_name') {
                        label = 'Title changed';
                      } else if (entry.fieldChanged === 'production_records_deleted') {
                        label = 'Video cards removed (left Approved)';
                      } else if (entry.fieldChanged === 'client_id') {
                        label = `Reassigned ${entry.oldValue} → ${entry.newValue}`;
                      } else if (entry.fieldChanged === 'posting_slots_cleared') {
                        label = 'Posting-schedule slot cleared (reassigned)';
                      } else {
                        label = entry.fieldChanged?.replace(/_/g, ' ');
                      }

                      return (
                        <div key={entry.id} style={{ display: 'flex', alignItems: 'baseline', gap: 10, padding: '7px 0', borderBottom: i < activityLog.length - 1 ? '1px solid var(--border)' : 'none' }}>
                          <div style={{ fontSize: 10, color: 'var(--tx-muted)', whiteSpace: 'nowrap', minWidth: 130 }}>
                            {dateStr} · {timeStr}
                          </div>
                          <div style={{ fontSize: 12, color: 'var(--tx-primary)', flex: 1 }}>
                            <span style={{ fontWeight: 600 }}>{who}</span>
                            {' — '}
                            <span style={{ color: 'var(--tx-primary)' }}>{label}</span>
                          </div>
                          {entry.oldValue && entry.newValue && (
                            <div style={{ fontSize: 10, color: 'var(--tx-muted)', whiteSpace: 'nowrap' }}>
                              {entry.oldValue} → {entry.newValue}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )
              )}
            </div>

            {/* Version History */}
            {!isReadOnly && (
              <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16, marginTop: 4 }}>
                <button
                  className="btn-ghost"
                  style={{ fontSize: 11, padding: '4px 10px', marginBottom: versionsOpen ? 14 : 0 }}
                  onClick={async () => {
                    if (!versionsOpen) {
                      setVersionsLoading(true);
                      try {
                        const res = await api.get(`/api/scripts/${selected.id}/versions`);
                        setVersions(res.data);
                      } catch { setVersions([]); }
                      finally { setVersionsLoading(false); }
                    }
                    setVersionsOpen(o => !o);
                  }}
                >
                  {versionsOpen ? '▲' : '▼'} Version History
                </button>

                {versionsOpen && (
                  versionsLoading ? (
                    <div style={{ fontSize: 11, color: 'var(--tx-muted)' }}>Loading…</div>
                  ) : versions.length === 0 ? (
                    <div style={{ fontSize: 11, color: 'var(--tx-muted)' }}>No saved versions yet.</div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                      {versions.map((v, i) => {
                        const date    = new Date(v.savedAt);
                        const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                        const timeStr = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
                        const who     = v.savedByMember?.name || 'Unknown';
                        const isConfirming = restoreConfirm === v.id;
                        return (
                          <div key={v.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: i < versions.length - 1 ? '1px solid var(--border)' : 'none' }}>
                            <div style={{ fontSize: 10, color: 'var(--tx-muted)', whiteSpace: 'nowrap', minWidth: 130 }}>
                              {dateStr} · {timeStr}
                            </div>
                            <div style={{ fontSize: 12, color: 'var(--tx-primary)', flex: 1 }}>
                              <span style={{ fontWeight: 600 }}>{who}</span>
                              {' — '}
                              <span style={{ color: 'var(--tx-secondary)' }}>{v.label || 'Snapshot'}</span>
                            </div>
                            {isConfirming ? (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                                <span style={{ fontSize: 11, color: 'var(--tx-muted)' }}>Restore?</span>
                                <button
                                  className="btn-ghost"
                                  style={{ fontSize: 11, padding: '2px 8px', color: 'var(--violet)' }}
                                  onClick={async () => {
                                    try {
                                      const res = await api.post(`/api/scripts/${selected.id}/restore/${v.id}`);
                                      setSelected(prev => ({ ...prev, scriptBody: res.data.scriptBody }));
                                      setDraftBody(res.data.scriptBody);
                                      setRestoreConfirm(null);
                                      const vRes = await api.get(`/api/scripts/${selected.id}/versions`);
                                      setVersions(vRes.data);
                                    } catch { alert('Restore failed. Please try again.'); }
                                  }}
                                >Yes</button>
                                <button className="btn-ghost" style={{ fontSize: 11, padding: '2px 8px' }} onClick={() => setRestoreConfirm(null)}>Cancel</button>
                              </div>
                            ) : (
                              <button
                                className="btn-ghost"
                                style={{ fontSize: 11, padding: '2px 8px', flexShrink: 0 }}
                                onClick={() => setRestoreConfirm(v.id)}
                              >Restore</button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )
                )}
              </div>
            )}

          </div>
          </div>

        {/* Scene footer — docked at bottom of editor, outside the scroll body */}
        {!isReadOnly && cols.length > 0 && draftBody?.scenes != null && (
          <div className="scene-footer" style={{ flexShrink: 0, background: 'var(--bg-card)', borderTop: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 28px' }}>
              <div className="scene-add" style={{ border: 'none', padding: 0 }} onClick={addRow}>
                <span style={{ fontSize: 16 }}>+</span>
                <span>Add scene row</span>
              </div>
              {draftBody?.format === 'Longform' && (
                <div className="scene-add" data-testid="lf-next-scene" style={{ border: 'none', padding: 0 }} title="Start the next scene of the current episode: appends its first row (scene # = this episode\u2019s next) at the end and puts the cursor in Character — type the description in the row" onClick={() => appendSceneRow(longformNextSceneNumber(draftBody?.scenes), '')}>
                  <span style={{ fontSize: 16 }}>+</span>
                  <span>Next scene</span>
                </div>
              )}
              <label style={{ fontSize: 11, color: 'var(--violet)', cursor: 'pointer', userSelect: 'none', marginLeft: 8 }}>
                <input type="file" accept=".csv" style={{ display: 'none' }} onChange={handleImportCSV} />
                ↑ Import CSV
              </label>
              <button
                onClick={pasteFromClipboard}
                style={{ background: 'none', border: 'none', fontSize: 11, color: 'var(--violet)', cursor: 'pointer', padding: 0, fontFamily: 'var(--font)' }}
                title="Paste from Google Sheets or Excel (replaces all rows)"
              >
                ↑ Paste from spreadsheet
              </button>
              <button
                onClick={() => downloadTemplate(format)}
                style={{ background: 'none', border: 'none', fontSize: 11, color: 'var(--tx-secondary)', cursor: 'pointer', padding: 0, fontFamily: 'var(--font)' }}
              >
                ↓ Download template
              </button>
              {selectedRows.size > 0 && (
                <div data-keep-row-selection="true" style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
                  <button
                    onClick={() => moveSelectedBlock(-1)}
                    disabled={Math.min(...selectedRows) === 0}
                    style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: '4px 10px', fontSize: 11, cursor: 'pointer', fontFamily: 'var(--font)', color: 'var(--tx-secondary)' }}
                  >
                    ↑ Move Up
                  </button>
                  <button
                    onClick={() => moveSelectedBlock(1)}
                    disabled={Math.max(...selectedRows) === (draftBody?.scenes?.length ?? 0) - 1}
                    style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: '4px 10px', fontSize: 11, cursor: 'pointer', fontFamily: 'var(--font)', color: 'var(--tx-secondary)' }}
                  >
                    ↓ Move Down
                  </button>
                  <button
                    onClick={() => setMultiDeleteConfirm(true)}
                    style={{ background: 'rgba(212,85,85,0.12)', color: 'var(--s-incomplete-t)', border: '1px solid rgba(212,85,85,0.35)', borderRadius: 'var(--radius-sm)', padding: '4px 12px', fontSize: 11, cursor: 'pointer', fontFamily: 'var(--font)' }}
                  >
                    Delete {selectedRows.size} row{selectedRows.size !== 1 ? 's' : ''} ×
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
          </div>

          {/* Clio drawer */}
          <div style={{
            width: clioOpen ? 360 : 0,
            overflow: 'hidden',
            transition: 'width 0.2s ease',
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            borderLeft: clioOpen ? '1px solid var(--border)' : 'none',
            background: 'var(--bg-card)',
          }}>
            <div style={{ width: 360, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--tx-primary)' }}>Clio</span>
                <button className="vc-close" onClick={() => setClioOpen(false)}>✕</button>
              </div>
              <ClioChat
                mode="drawer"
                sessionKey={selected?.id ? `helicon_chat_script_${selected.id}` : 'helicon_chat_drawer'}
                key={selected?.id || 'no_script'}
              />
            </div>
          </div>
        </div>

        {/* Find & Replace modal (spec 6.1) */}
        {findReplaceOpen && format && draftBody && (
          <FindReplaceModal
            draftBody={draftBody}
            cols={cols}
            onApply={next => { pushUndo(draftBody); setDraftBody(next); }}
            onFocusMatch={focusFindMatch}
            onClose={() => setFindReplaceOpen(false)}
          />
        )}

        {/* Delete confirm modal */}
        {/* Run 2: the client character-registry modal (Character column header) */}
        {registryModalOpen && selected?.client?.id && (
          <CharacterRegistryModal
            clientId={selected.client.id}
            clientName={selected.client.name}
            onClose={() => setRegistryModalOpen(false)}
            canEdit={canEditRegistry}
            canMapTalent={canMapTalent}
            onChanged={loadRegistry}
          />
        )}

        {deleteConfirm && (
          <div className="modal-overlay" onClick={() => setDeleteConfirm(false)}>
            <div className="vc-modal" style={{ width: 400 }} onClick={e => e.stopPropagation()}>
              <div className="vc-header">
                <div><div className="vc-title">Delete Script?</div></div>
                <button className="vc-close" onClick={() => setDeleteConfirm(false)}>✕</button>
              </div>
              <div style={{ padding: '20px 28px' }}>
                <p style={{ fontSize: 13, color: 'var(--tx-secondary)', lineHeight: 1.6, margin: 0 }}>
                  Delete <strong style={{ color: 'var(--tx-primary)' }}>{selected.scriptName}</strong>?
                </p>
                <p style={{ fontSize: 12, color: 'var(--tx-faint)', lineHeight: 1.6, margin: '10px 0 0' }}>
                  If the script has been posted, it will be archived (hidden). Otherwise it will be permanently deleted along with any associated video cards. This cannot be undone.
                </p>
              </div>
              <div className="vc-footer">
                <button className="btn-ghost" onClick={() => setDeleteConfirm(false)}>Cancel</button>
                <button
                  onClick={handleDeleteScript}
                  style={{ background: 'rgba(212,85,85,0.15)', color: 'var(--s-incomplete-t)', border: '1px solid rgba(212,85,85,0.35)', borderRadius: 'var(--radius-sm)', padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font)' }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Multi-row delete confirm modal */}
        {multiDeleteConfirm && (
          <div className="modal-overlay" data-keep-row-selection="true" onClick={() => setMultiDeleteConfirm(false)}>
            <div className="vc-modal" style={{ width: 400 }} onClick={e => e.stopPropagation()}>
              <div className="vc-header">
                <div><div className="vc-title">Delete {selectedRows.size} Row{selectedRows.size !== 1 ? 's' : ''}?</div></div>
                <button className="vc-close" onClick={() => setMultiDeleteConfirm(false)}>✕</button>
              </div>
              <div style={{ padding: '20px 28px' }}>
                <p style={{ fontSize: 13, color: 'var(--tx-secondary)', lineHeight: 1.6, margin: 0 }}>
                  This will permanently remove {selectedRows.size} selected row{selectedRows.size !== 1 ? 's' : ''} from the script. This cannot be undone.
                </p>
              </div>
              <div className="vc-footer">
                <button className="btn-ghost" onClick={() => setMultiDeleteConfirm(false)}>Cancel</button>
                <button
                  onClick={deleteSelectedRows}
                  style={{ background: 'rgba(212,85,85,0.15)', color: 'var(--s-incomplete-t)', border: '1px solid rgba(212,85,85,0.35)', borderRadius: 'var(--radius-sm)', padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font)' }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Warning modal — moving script out of Approved deletes all production records */}
        {approvedTransitionWarn && (
          <div className="modal-overlay" onClick={() => { setApprovedTransitionWarn(false); setPendingTransition(null); }}>
            <div className="vc-modal" style={{ width: 460 }} onClick={e => e.stopPropagation()}>
              <div className="vc-header">
                <div><div className="vc-title">Move Script Out of Approved?</div></div>
                <button className="vc-close" onClick={() => { setApprovedTransitionWarn(false); setPendingTransition(null); }}>✕</button>
              </div>
              <div style={{ padding: '20px 28px' }}>
                <p style={{ fontSize: 13, color: 'var(--tx-secondary)', lineHeight: 1.6, margin: 0 }}>
                  {format === 'Longform' ? (
                    <>Moving <strong style={{ color: 'var(--tx-primary)' }}>{selected.scriptName}</strong> out of Approved will permanently delete its <strong style={{ color: 'var(--tx-primary)' }}>{lfEpisodes.length} episode card{lfEpisodes.length === 1 ? '' : 's'}</strong> — their stage, shoot plans, talent bookings, cut lists and posting slots. The episodes themselves, every line and the cast stay in the script; approving it again recreates the cards from the script.</>
                  ) : (
                    <>Moving <strong style={{ color: 'var(--tx-primary)' }}>{selected.scriptName}</strong> out of Approved will permanently delete all associated video cards (Live and AI production), any scheduled shoot days on those cards, and any scheduler slots.</>
                  )}
                </p>
                {/* The pull-back is the one move people make ON PURPOSE, so spell
                    out what they get back for it (an editable script + title). */}
                {pendingTransition === 'Needs Review' && (
                  <p style={{ fontSize: 12, color: 'var(--tx-secondary)', lineHeight: 1.6, margin: '10px 0 0' }}>
                    The script returns to <strong style={{ color: 'var(--tx-primary)' }}>Needs Review</strong> for writing, and its title becomes editable again. It will need to be approved again to come back into production.
                  </p>
                )}
                <p style={{ fontSize: 12, color: 'var(--tx-faint)', lineHeight: 1.6, margin: '10px 0 0' }}>
                  This cannot be undone. Are you sure you want to continue?
                </p>
              </div>
              <div className="vc-footer">
                <button className="btn-ghost" onClick={() => { setApprovedTransitionWarn(false); setPendingTransition(null); }}>Cancel</button>
                <button
                  onClick={() => {
                    const t = pendingTransition;
                    setApprovedTransitionWarn(false);
                    setPendingTransition(null);
                    doTransition(t);
                  }}
                  style={{ background: 'rgba(212,85,85,0.15)', color: 'var(--s-incomplete-t)', border: '1px solid rgba(212,85,85,0.35)', borderRadius: 'var(--radius-sm)', padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font)' }}
                >
                  Yes, Move Out of Approved
                </button>
              </div>
            </div>
          </div>
        )}

        {/* App-clipboard: cross-format warning */}
        {crossFormatPastePrompt && (
          <div className="modal-overlay" onClick={() => crossFormatPastePrompt.resolve(false)}>
            <div className="vc-modal" style={{ width: 490 }} onClick={e => e.stopPropagation()}>
              <div className="vc-header">
                <div><div className="vc-title">Different script format</div></div>
                <button className="vc-close" onClick={() => crossFormatPastePrompt.resolve(false)}>✕</button>
              </div>
              <div style={{ padding: '20px 28px' }}>
                <div style={{ background: 'rgba(245,200,66,0.12)', border: '1px solid rgba(245,200,66,0.4)', borderRadius: 7, padding: '12px 14px', fontSize: 13, color: 'var(--tx-secondary)', lineHeight: 1.6 }}>
                  The copied rows are from a <strong style={{ color: 'var(--tx-primary)' }}>{crossFormatPastePrompt.sourceFormat}</strong> script. This script uses <strong style={{ color: 'var(--tx-primary)' }}>{crossFormatPastePrompt.destFormat}</strong> format. Columns are pasted by position; extra source columns are dropped and unmatched destination columns are left blank. Continue?
                </div>
              </div>
              <div className="vc-footer">
                <button className="btn-ghost" onClick={() => crossFormatPastePrompt.resolve(false)}>Cancel</button>
                <button className="btn-primary" onClick={() => crossFormatPastePrompt.resolve(true)}>Paste Anyway</button>
              </div>
            </div>
          </div>
        )}

        {/* Generic error modal — replaces alert() for status transition failures */}
        {errorModal && (
          <div className="modal-overlay" onClick={() => setErrorModal(null)}>
            <div className="vc-modal" style={{ width: 420 }} onClick={e => e.stopPropagation()}>
              <div className="vc-header">
                <div><div className="vc-title">{errorModal.title}</div></div>
                <button className="vc-close" onClick={() => setErrorModal(null)}>✕</button>
              </div>
              <div style={{ padding: '20px 28px' }}>
                <p style={{ fontSize: 13, color: 'var(--tx-secondary)', lineHeight: 1.6, margin: 0 }}>
                  {errorModal.message}
                </p>
                {errorModal.message?.toLowerCase().includes('approved') && errorModal.message?.toLowerCase().includes('production') && (
                  <p style={{ fontSize: 12, color: 'var(--tx-faint)', lineHeight: 1.6, margin: '10px 0 0' }}>
                    Scripts must have an <strong>Approved</strong> qual status before they can be added to Live or AI production. Update the script status to Approved first, then try again.
                  </p>
                )}
              </div>
              <div className="vc-footer">
                <button className="btn-ghost" onClick={() => setErrorModal(null)}>OK</button>
              </div>
            </div>
          </div>
        )}

        {/* Share link management panel */}
        {shareOpen && (
          <div className="modal-overlay" onClick={() => setShareOpen(false)}>
            <div
              style={{
                background: 'var(--bg-card)', borderRadius: 'var(--radius-card)',
                border: '1px solid var(--border)', boxShadow: 'var(--shadow-lg)',
                width: 440, maxHeight: '80vh', display: 'flex', flexDirection: 'column',
              }}
              onClick={e => e.stopPropagation()}
            >
              <div className="cal-assign-head">
                <span className="cal-assign-label">Share — {selected.scriptName}</span>
                <button className="vc-close" onClick={() => setShareOpen(false)}>×</button>
              </div>

              <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
                <button
                  className="btn-primary"
                  style={{ width: '100%', padding: '10px', fontSize: 13 }}
                  onClick={generateShareLink}
                  disabled={shareGenerating}
                >
                  {shareGenerating ? 'Generating…' : '+ Generate Share Link'}
                </button>
                {shareNewUrl && (
                  <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
                    <input
                      className="form-input"
                      readOnly
                      value={shareNewUrl}
                      style={{ flex: 1, fontSize: 11, fontFamily: 'var(--mono)' }}
                    />
                    <button
                      className="btn-ghost"
                      style={{ flexShrink: 0, fontSize: 11 }}
                      onClick={() => {
                        navigator.clipboard.writeText(shareNewUrl);
                        setShareCopied('new');
                        setTimeout(() => setShareCopied(null), 2000);
                      }}
                    >
                      {shareCopied === 'new' ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                )}
              </div>

              <div style={{ flex: 1, overflowY: 'auto', padding: '0 20px 16px' }}>
                {shareLinksLoad ? (
                  <div style={{ padding: 20, textAlign: 'center', color: 'var(--tx-faint)', fontSize: 12 }}>Loading…</div>
                ) : shareLinks.length === 0 ? (
                  <div style={{ padding: '20px 0', color: 'var(--tx-faint)', fontSize: 12 }}>No active share links.</div>
                ) : shareLinks.map(link => (
                  <div key={link.id} style={{ padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 6, alignItems: 'center' }}>
                      <div style={{ flex: 1, fontSize: 11, color: 'var(--tx-secondary)', fontFamily: 'var(--mono)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {link.url}
                      </div>
                      <button
                        className="btn-ghost"
                        style={{ flexShrink: 0, fontSize: 11, padding: '3px 10px' }}
                        onClick={() => {
                          navigator.clipboard.writeText(link.url);
                          setShareCopied(link.id);
                          setTimeout(() => setShareCopied(null), 2000);
                        }}
                      >
                        {shareCopied === link.id ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 11, color: 'var(--tx-muted)' }}>
                      <span>
                        {link.viewCount} view{link.viewCount !== 1 ? 's' : ''} ·{' '}
                        {link.lastViewedAt
                          ? `Last viewed ${new Date(link.lastViewedAt).toLocaleDateString()}`
                          : 'Never viewed'}
                      </span>
                      <button
                        type="button"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: 'var(--s-incomplete-t)', padding: '2px 6px', fontFamily: 'var(--font)' }}
                        onClick={() => deactivateShareLink(link.id)}
                      >
                        Deactivate
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Phase 5: right-click gutter context menu */}
        {contextMenu && (() => {
          const { x, y, targetRows, clickedCell } = contextMenu;
          const n = targetRows.length;
          const totalRows = draftBody?.scenes?.length || 0;
          const hasClip = !!rowClipboardRef.current;
          const close = () => setContextMenu(null);
          const run = (fn) => { fn(); close(); };
          const label = (verb) => `${verb} ${n} row${n !== 1 ? 's' : ''}`;
          // Cell-color preview: how many cells would the "Color cell(s)..." entry hit?
          const cellTargets = clickedCell
            ? cellColorTargets(clickedCell.rowIdx, clickedCell.colKey)
            : [];
          const cellLabel = cellTargets.length > 1
            ? `Color ${cellTargets.length} cells…`
            : 'Color cell…';
          // Initial position = the click point; the useLayoutEffect above measures
          // the real menu and clamps it fully on-screen before paint.
          const left = x, top = y;
          const Item = ({ label: lbl, onClick, disabled }) => (
            <button
              type="button"
              disabled={disabled}
              onMouseDown={e => { e.preventDefault(); if (!disabled) onClick(); }}
              style={{
                display: 'block', width: '100%', textAlign: 'left',
                background: 'none', border: 'none',
                padding: '8px 14px', fontSize: 12, fontFamily: 'var(--font)',
                color: disabled ? 'var(--tx-faint)' : 'var(--tx-primary)',
                cursor: disabled ? 'default' : 'pointer',
              }}
              onMouseEnter={e => { if (!disabled) e.currentTarget.style.background = 'var(--bg-hover)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
            >{lbl}</button>
          );
          return (
            <div
              ref={ctxMenuRef}
              data-gutter-context-menu="true"
              style={{
                position: 'fixed', left, top, zIndex: 10000,
                minWidth: 200,
                maxHeight: 'calc(100vh - 16px)',
                overflowY: 'auto',
                background: 'var(--bg-card)',
                border: '1px solid var(--border-mid)',
                borderRadius: 10,
                boxShadow: '0 8px 32px rgba(0,0,0,0.55)',
                padding: '4px 0',
                userSelect: 'none',
              }}
            >
              <Item label={n === 1 ? 'Insert row above' : `Insert ${n} rows above`} onClick={() => run(() => insertRowAt(targetRows[0], n))} />
              <Item label={n === 1 ? 'Insert row below' : `Insert ${n} rows below`} onClick={() => run(() => insertRowAt(targetRows[targetRows.length - 1] + 1, n))} />
              {format === 'Longform' && (() => {
                const allDir = targetRows.every(i => draftBody?.scenes?.[i]?.kind === 'direction');
                return (
                  <>
                    <Item label={allDir ? label('Mark as dialogue') : label('Mark as direction')} onClick={() => run(() => setRowsKind(targetRows, allDir ? null : 'direction'))} />
                    <Item label="Insert episode break below" onClick={() => run(() => insertEpisodeBreakAt(targetRows[targetRows.length - 1] + 1))} />
                  </>
                );
              })()}
              <Item label={label('Duplicate')} onClick={() => run(() => duplicateRowsByIndices(targetRows))} />
              <Item label={label('Cut')} onClick={() => run(() => cutRowsByIndices(targetRows))} />
              <Item label={label('Copy')} onClick={() => run(() => {
                // Respect an active cell selection (copy the cell block); otherwise
                // copy the right-clicked whole rows. Both update the app clipboard.
                const sel = getActiveSelection();
                if (sel && sel.kind === 'cells') copySelectionToAppClipboard();
                else copyRowsByIndices(targetRows);
              })} />
              <Item label="Paste above" onClick={() => run(() => pasteAboveRow(targetRows[0]))} disabled={!hasClip} />
              <div style={{ height: 1, background: 'var(--border)', margin: '4px 0' }} />
              <div style={{ padding: '6px 14px 2px', fontSize: 11, color: 'var(--tx-faint)' }}>
                {n === 1 ? 'Color row' : `Color ${n} rows`}
              </div>
              <div style={{ padding: '0 10px 6px' }}>
                <ColorSwatch onSelect={color => run(() => applyRowColorToIndices(targetRows, color))} />
              </div>
              {clickedCell && (
                <>
                  <div style={{ padding: '6px 14px 2px', fontSize: 11, color: 'var(--tx-faint)' }}>
                    {cellLabel.replace('…', '')}
                  </div>
                  <div style={{ padding: '0 10px 6px' }}>
                    <ColorSwatch onSelect={color => run(() => applyCellColorToTargets(cellTargets, color))} />
                  </div>
                </>
              )}
              <div style={{ height: 1, background: 'var(--border)', margin: '4px 0' }} />
              <Item
                label={label('Delete')}
                onClick={() => run(() => {
                  // If deleting every row, leave one blank row behind (deleteRowsByIndices handles this).
                  deleteRowsByIndices(targetRows);
                })}
                disabled={totalRows - n <= 0 && totalRows > 0 && n >= totalRows}
              />
            </div>
          );
        })()}

        {/* Phase 4: brief toast after copy / cut */}
        {copyFeedback && (
          <div
            style={{
              position: 'fixed',
              bottom: 24,
              left: '50%',
              transform: 'translateX(-50%)',
              background: 'var(--bg-card)',
              color: 'var(--tx-primary)',
              border: '1px solid var(--violet)',
              padding: '8px 16px',
              borderRadius: 7,
              fontSize: 12,
              fontFamily: 'var(--font)',
              fontWeight: 500,
              boxShadow: '0 4px 16px rgba(0,0,0,0.45)',
              zIndex: 9998,
              pointerEvents: 'none',
              userSelect: 'none',
            }}
          >
            {copyFeedback}
          </div>
        )}

        {/* App-clipboard copy toast — dismissible, with a Clear action */}
        {appClipToast && (
          <div
            style={{
              position: 'fixed',
              bottom: 64,
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              alignItems: 'center',
              gap: 14,
              background: 'var(--bg-card)',
              color: 'var(--tx-primary)',
              border: '1px solid var(--violet)',
              padding: '8px 16px',
              borderRadius: 7,
              fontSize: 12,
              fontFamily: 'var(--font)',
              fontWeight: 500,
              boxShadow: '0 4px 16px rgba(0,0,0,0.45)',
              zIndex: 9998,
              pointerEvents: 'auto',
            }}
          >
            <span>{appClipToast}</span>
            <button
              onClick={() => {
                clearAppClip();
                setAppClipToast(null);
                if (appClipToastTimer.current) clearTimeout(appClipToastTimer.current);
              }}
              style={{ background: 'none', border: 'none', color: 'var(--violet)', cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'var(--font)', padding: 0 }}
            >
              Clear
            </button>
          </div>
        )}

        {/* Phase 3: floating preview chip during drag-to-reposition */}
        {dragPreview && (
          <div
            style={{
              position: 'fixed',
              left: dragPreview.x + 12,
              top: dragPreview.y + 12,
              pointerEvents: 'none',
              zIndex: 9999,
              background: 'var(--violet)',
              color: '#fff',
              padding: '6px 12px',
              borderRadius: 7,
              fontSize: 12,
              fontWeight: 600,
              fontFamily: 'var(--font)',
              boxShadow: '0 4px 16px rgba(0,0,0,0.45)',
              opacity: 0.92,
              userSelect: 'none',
            }}
          >
            ↕ Moving {dragPreview.count} row{dragPreview.count !== 1 ? 's' : ''}
          </div>
        )}

        {/* New Script modal — rendered inside the editor too, so launching New
            Script while editing shows the modal here instead of only on the list. */}
        {showNew && (
          <NewScriptModal
            clients={clients}
            members={members}
            defaultClientId={newScriptDefaults?.clientId || ''}
            onClose={() => { setShowNew(false); setNewScriptDefaults(null); }}
            onCreate={handleCreate}
          />
        )}

      </div>
    );
  }

  // ── List view ────────────────────────────────────────────────────

  const canMassDelete        = hasRole('Admin', 'Power User');
  // Roles that can move a script's qual status (mirrors the server TRANSITIONS
  // role lists) — they get the selection checkboxes for bulk status changes too.
  const canBulkStatus        = hasRole('Writer', 'Qual Reviewer', 'Admin', 'Power User');
  // Reassign-to-another-project (mirrors the server's REASSIGN_ROLES). A
  // Producer owns which project a shoot belongs to, so they get this even though
  // they get none of the other bulk actions — which is exactly why it has to
  // widen canSelectRows too, or a pure Producer would have no tick-boxes to
  // select with and the button could never appear.
  const canReassign          = hasRole('Producer', 'Admin', 'Power User');
  const canSelectRows        = canMassDelete || canBulkStatus || canReassign;
  const canSeeQualNotesList  = user?.roles?.some(r => ['Admin','Power User','Qual Reviewer','Writer'].includes(r));
  // Notes stay READ-ONLY in the bin: the inline editor PATCHes /api/scripts/:id,
  // which 404s while the script is deleted, so every blur would fail silently.
  const canEditQualNotesList = user?.roles?.some(r => ['Admin','Power User','Qual Reviewer'].includes(r)) && !deletedView;
  const scriptColTemplate   = canSeeQualNotesList ? scriptColTemplateQn : scriptColTemplateNoQn;
  const startScriptColDrag  = canSeeQualNotesList ? startScriptColDragQn : startScriptColDragNoQn;
  // Characters sits immediately right of Qual Notes — and immediately right of
  // Approved for the roles that never see Qual Notes at all.
  const activeScriptHeaders = canSeeQualNotesList
    ? [...SCRIPT_HEADERS, 'Qual Notes', CHARACTERS_COL]
    : [...SCRIPT_HEADERS, CHARACTERS_COL];
  // Insert a 40px "open in new tab" button column right after the Script Name
  // column (i.e. between Script Name and Client).
  const scriptTemplateWithBtn = (() => {
    const parts = scriptColTemplate.split(' ');
    return [parts[0], '40px', ...parts.slice(1)].join(' ');
  })();
  const listGridCols        = canSelectRows ? `28px ${scriptTemplateWithBtn}` : scriptTemplateWithBtn;

  return (
    <div className="main">
      <div className="topbar">
        <div>
          <div className="page-title">Scripts</div>
          <div className="page-sub">
            {listLoad ? 'Loading…' : (
              displayScripts.length !== scripts.length
                ? `${displayScripts.length} of ${scripts.length} script${scripts.length !== 1 ? 's' : ''}`
                : `${scripts.length} script${scripts.length !== 1 ? 's' : ''}`
            )}
          </div>
        </div>
        <div className="topbar-actions">
          {/* The bin offers exactly one action: put it back. Marking a status or
              archiving a deleted script would write to a row the user can't see
              the consequences of, so those controls are hidden here. */}
          {deletedView && canMassDelete && selectedScripts.size > 0 && (
            <button
              onClick={handleBulkRestore}
              disabled={bulkStatusBusy}
              className="btn-ghost"
              style={{ padding: '9px 16px', fontSize: 13, fontWeight: 600, cursor: bulkStatusBusy ? 'wait' : 'pointer' }}
            >
              ♻ Restore {selectedScripts.size} script{selectedScripts.size !== 1 ? 's' : ''}
            </button>
          )}
          {!deletedView && !templatesView && canBulkStatus && selectedScripts.size > 0 && (
            <>
              <span style={{ fontSize: 12, color: 'var(--tx-muted)', alignSelf: 'center', marginRight: 2 }}>
                {selectedScripts.size} selected — mark:
              </span>
              {['Needs Review', 'Approved', 'Backburner'].map(st => (
                <button
                  key={st}
                  onClick={() => handleBulkStatus(st)}
                  disabled={bulkStatusBusy}
                  className="btn-ghost"
                  style={{ padding: '9px 14px', fontSize: 13, fontWeight: 600, cursor: bulkStatusBusy ? 'wait' : 'pointer' }}
                >
                  {st}
                </button>
              ))}
            </>
          )}
          {/* Reassign to another project. Sits beside the status buttons because
              it acts on the same tick-box selection, but carries its own role
              gate (Producer / Admin / Power User, matching the server) — moving
              work between projects is not a writer's or reviewer's call, and a
              Producer needs it even though they get no other bulk action.
              Hidden in the bin and the template library,
              which hold rows with no production line to move. The archived shelf
              keeps it: archive is orthogonal to qual status, so a shelved script
              can still change project. */}
          {!deletedView && !templatesView && canReassign && selectedScripts.size > 0 && (
            <button
              onClick={openReassign}
              disabled={bulkStatusBusy || reassignBusy}
              className="btn-ghost"
              style={{ padding: '9px 16px', fontSize: 13, fontWeight: 600, cursor: (bulkStatusBusy || reassignBusy) ? 'wait' : 'pointer' }}
            >
              {reassignBusy && !reassignData ? 'Checking…' : `⇄ Reassign ${selectedScripts.size} script${selectedScripts.size !== 1 ? 's' : ''}`}
            </button>
          )}
          {!deletedView && !templatesView && canMassDelete && selectedScripts.size > 0 && (
            <button
              onClick={() => handleBulkArchive(!archivedView)}
              disabled={bulkStatusBusy}
              className="btn-ghost"
              style={{ padding: '9px 16px', fontSize: 13, fontWeight: 600, cursor: bulkStatusBusy ? 'wait' : 'pointer' }}
            >
              {archivedView ? 'Unarchive' : 'Archive'} {selectedScripts.size} script{selectedScripts.size !== 1 ? 's' : ''}
            </button>
          )}
          {!deletedView && !templatesView && canMassDelete && selectedScripts.size > 0 && (
            <button
              onClick={() => setScriptMassDelete(true)}
              disabled={bulkStatusBusy}
              style={{ background: 'rgba(212,85,85,0.15)', color: 'var(--s-incomplete-t)', border: '1px solid rgba(212,85,85,0.35)', borderRadius: 'var(--radius-sm)', padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font)' }}
            >
              Delete {selectedScripts.size} script{selectedScripts.size !== 1 ? 's' : ''}
            </button>
          )}
          <button
            className="btn-ghost"
            onClick={templatesView ? loadTemplates : loadScripts}
            disabled={templatesView ? templatesLoad : listLoad}
          >↺ Refresh</button>
          <button className="btn-primary" onClick={() => { setNewScriptDefaults({ clientId: clientFilter || '', openInNewTab: false }); setShowNew(true); }}>+ New Script</button>
        </div>
      </div>

      <div className="body" style={{ overflow: 'hidden', padding: '20px 36px 28px', gap: 14 }}>

        {/* Status chips + Archived view toggle. Chip counts come from the server
            (active vs archived shelf); in the Archived view the status chips
            filter WITHIN archived. */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', flexShrink: 0 }}>
          {!templatesView && QUAL_STATUSES.map(s => {
            const n = deletedView  ? srvCounts.deleted?.[s]
                    : archivedView ? srvCounts.archived?.[s]
                    :                srvCounts.active?.[s];
            return (
            <div
              key={s}
              className={`chip ${QUAL_CHIP_CLASS[s]}${statusFilter && statusFilter !== s ? ' dimmed' : ''}`}
              onClick={() => setStatusFilter(prev => prev === s ? '' : s)}
            >
              <span>{s}</span>
              {n != null && <span className="chip-count">{n}</span>}
            </div>
            );
          })}
          {!templatesView && statusFilter && (
            <button className="btn-ghost" style={{ padding: '4px 10px', fontSize: 11 }} onClick={() => setStatusFilter('')}>
              Clear ×
            </button>
          )}
          {/* View mode toggle — visually distinct from the status chips (it's a
              mode, not a filter). Entering the Archived view clears the Posted
              chip since Posted+archived are shown together there anyway. */}
          <div style={{ flex: 1 }} />
          <button
            type="button"
            className="btn-ghost"
            onClick={() => {
              setArchivedView(v => {
                const next = !v;
                if (next) { setTemplatesView(false); setDeletedView(false); }
                return next;
              });
              setSelectedScripts(new Set());
            }}
            title="Archived scripts are off the production line but retained and referenceable"
            style={{
              padding: '6px 14px', fontSize: 12, fontWeight: 600,
              borderRadius: 'var(--radius-pill)',
              border: archivedView ? '1px solid var(--violet)' : '1px dashed var(--border-mid)',
              background: archivedView ? 'rgba(139,109,255,0.15)' : 'transparent',
              color: archivedView ? 'var(--violet)' : 'var(--tx-secondary)',
              cursor: 'pointer', fontFamily: 'var(--font)',
            }}
          >
            {archivedView ? '← Back to line' : '🗄 Archived'}
            {srvCounts.archived?.total != null && <span className="chip-count" style={{ marginLeft: 6 }}>{srvCounts.archived.total}</span>}
          </button>
          {/* Templates view — the third shelf, next to Archived. Same mode-not-
              filter treatment. Hidden entirely for roles that can't use them. */}
          {canUseTemplates && (
            <button
              type="button"
              className="btn-ghost"
              onClick={() => {
                setTemplatesView(v => {
                  const next = !v;
                  if (next) { setArchivedView(false); setDeletedView(false); }
                  return next;
                });
                setSelectedScripts(new Set());
              }}
              title="Saved, reusable scripts. A template is never produced, posted, counted or reported — it only seeds new scripts."
              style={{
                padding: '6px 14px', fontSize: 12, fontWeight: 600,
                borderRadius: 'var(--radius-pill)',
                border: templatesView ? '1px solid var(--violet)' : '1px dashed var(--border-mid)',
                background: templatesView ? 'rgba(139,109,255,0.15)' : 'transparent',
                color: templatesView ? 'var(--violet)' : 'var(--tx-secondary)',
                cursor: 'pointer', fontFamily: 'var(--font)', marginLeft: 8,
              }}
            >
              {templatesView ? '← Back to line' : '📋 Templates'}
            </button>
          )}
          {/* Deleted view — the recycle bin, fourth and last mode. Admin/PU only,
              matching the server gate; a Writer who can delete their own script
              still can't restore it, so showing them an empty bin would mislead.
              Rendered last and in red so it reads as recovery, not navigation. */}
          {canMassDelete && (
            <button
              type="button"
              className="btn-ghost"
              onClick={() => {
                setDeletedView(v => {
                  const next = !v;
                  if (next) { setArchivedView(false); setTemplatesView(false); }
                  return next;
                });
                setSelectedScripts(new Set());
              }}
              title="Deleted scripts. Delete is a soft delete — nothing is destroyed, and anything here can be restored."
              style={{
                padding: '6px 14px', fontSize: 12, fontWeight: 600,
                borderRadius: 'var(--radius-pill)',
                border: deletedView ? '1px solid var(--s-incomplete-t)' : '1px dashed var(--border-mid)',
                background: deletedView ? 'rgba(212,85,85,0.15)' : 'transparent',
                color: deletedView ? 'var(--s-incomplete-t)' : 'var(--tx-secondary)',
                cursor: 'pointer', fontFamily: 'var(--font)', marginLeft: 8,
              }}
            >
              {deletedView ? '← Back to line' : '🗑 Deleted'}
              {srvCounts.deleted?.total != null && <span className="chip-count" style={{ marginLeft: 6 }}>{srvCounts.deleted.total}</span>}
            </button>
          )}
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          <input
            className="filter-input"
            style={{ flex: 1, maxWidth: 280 }}
            placeholder="Search scripts, projects, writers…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <select className="filter-input" style={{ width: 160 }} value={clientFilter} onChange={e => setClientFilter(e.target.value)}>
            <option value="">All projects</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select className="filter-input" style={{ width: 155 }} value={writerFilter} onChange={e => setWriterFilter(e.target.value)}>
            <option value="">All writers</option>
            {writerOptions.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
          {/* The Character filter used to be a third dropdown here. It now lives
              in the Characters column's header, beside the tags it filters. */}
        </div>

        {/* Templates card — its own list shape. A template has no qual status,
            no approved date and no production state, so reusing the production
            grid would mean five columns of em-dashes. */}
        {templatesView && (
          <div className="card" style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 110px 1.2fr 1.2fr 110px 190px', padding: '10px 22px', background: 'var(--bg-card2)', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
              {['Template', 'Format', 'Project', 'Created by', 'Created', ''].map((h, i) => (
                <div className="lp-th" key={i}>{h}</div>
              ))}
            </div>
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {templatesLoad ? (
                <div style={{ padding: 40, textAlign: 'center', color: 'var(--tx-faint)' }}>Loading…</div>
              ) : templateRows.length === 0 ? (
                <div style={{ padding: 40, textAlign: 'center', color: 'var(--tx-faint)' }}>
                  No templates yet. Open a script and use “⎘ Save as Template” to create one.
                </div>
              ) : templateRows.map(t => (
                <div key={t.id} className="lp-row" style={{ display: 'grid', gridTemplateColumns: '2fr 110px 1.2fr 1.2fr 110px 190px', alignItems: 'center' }}>
                  <div style={{ minWidth: 0 }}>
                    <div className="script-name">{t.templateName || t.scriptName}</div>
                  </div>
                  <div className="lp-cell">
                    {t.format ? <span className={formatBadgeClass(t.format)}>{formatLabel(t.format)}</span> : '—'}
                  </div>
                  <div className="lp-cell">{t.client?.name || '—'}</div>
                  <div className="lp-cell">{t.writer?.name || '—'}</div>
                  <div style={{ fontSize: 11, color: 'var(--tx-muted)' }}>
                    {t.createdAt ? new Date(t.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}
                  </div>
                  <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                    <button
                      className="btn-ghost"
                      style={{ padding: '4px 10px', fontSize: 11 }}
                      title="Create a new script from this template"
                      onClick={() => setUseTplPrompt({ template: t })}
                    >
                      + New from template
                    </button>
                    {canDeleteTemplates && (
                      <button
                        className="btn-ghost"
                        style={{ padding: '4px 8px', fontSize: 11, color: 'var(--s-incomplete-t)', borderColor: 'rgba(212,85,85,0.35)' }}
                        title="Delete this template"
                        onClick={() => setDelTplConfirm({ template: t })}
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Table card */}
        {!templatesView && (
        <div className="card" style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>

          {/* ONE scroll container for the header and the rows, on both axes.
              The grid's columns are fixed pixel widths that add up to more than
              the card is wide on a normal laptop, and `.card` is
              `overflow: hidden` — so before this, whatever did not fit was
              silently CLIPPED with no way to reach it. That was already true of
              the tail of Qual Notes; adding Characters made it lose a whole
              column. Scrolling horizontally here moves the header and the rows
              together, which two sibling scroll containers could not guarantee,
              and `width: max-content` on the inner wrapper makes the row hover
              background span the full scrollable width rather than stopping at
              the visible edge. The header is `sticky` instead of a flex sibling
              so it stays put while the rows scroll vertically — the same idiom
              the script editor's scene table already uses. */}
          <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          <div style={{ width: 'max-content', minWidth: '100%' }}>

          {/* Header */}
          <div style={{ display: 'grid', gridTemplateColumns: listGridCols, padding: '10px 22px', background: 'var(--bg-card2)', borderBottom: '1px solid var(--border)', position: 'sticky', top: 0, zIndex: 3 }}>
            {canSelectRows && (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={displayScripts.length > 0 && displayScripts.every(s => selectedScripts.has(s.id))}
                  onChange={e => {
                    if (e.target.checked) setSelectedScripts(new Set(displayScripts.map(s => s.id)));
                    else setSelectedScripts(new Set());
                  }}
                  style={{ accentColor: 'var(--violet)', cursor: 'pointer' }}
                />
              </div>
            )}
            {activeScriptHeaders.map((h, i) => {
              // Characters: the only header that is a control rather than a sort
              // trigger. Expand toggle first, then the search box, and no
              // onClick on the cell itself — a click meant for the search box
              // must never also sort the list. (The column has no sort anyway;
              // displayScripts has no branch for it and returns 0.)
              //
              // The toggle sits LEFT of the search rather than at the right
              // edge: it is a fixed-size control and the search box is the one
              // that stretches, so putting the toggle first gives it a position
              // that does not move as the column is resized — and keeps it away
              // from the resize handle, which owns the right edge.
              if (h === CHARACTERS_COL) {
                return (
                  <div
                    key={h}
                    className="lp-th"
                    style={{
                      position: 'relative', display: 'flex', alignItems: 'center', gap: 6, minWidth: 0,
                      // Keeps the search box clear of the resize handle below.
                      paddingRight: 10,
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => setCharsExpanded(v => !v)}
                      title={charsExpanded
                        ? 'Collapse the Characters column — back to one scrolling line per row'
                        : 'Expand the Characters column — every row wraps to show all of its characters'}
                      aria-label={charsExpanded ? 'Collapse the Characters column' : 'Expand the Characters column'}
                      aria-expanded={charsExpanded}
                      style={{
                        flexShrink: 0, background: 'none', cursor: 'pointer',
                        border: '1px solid transparent', borderRadius: 4, padding: '1px 4px',
                        fontSize: 10, lineHeight: 1.2, fontFamily: 'var(--font)',
                        color: charsExpanded ? 'var(--violet)' : 'var(--tx-muted)',
                      }}
                    >
                      {charsExpanded ? '▴' : '▾'}
                    </button>
                    <CharacterHeaderSearch
                      query={characterQuery}
                      setQuery={setCharacterQuery}
                      options={characterOptions}
                    />
                    {/* Same resize handle every other header has. The cell's
                        paddingRight keeps the search box out from under it. */}
                    <div
                      style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 8, cursor: 'col-resize', zIndex: 1 }}
                      onMouseDown={e => startScriptColDrag(i, e)}
                    />
                  </div>
                );
              }
              const active = scriptSort.col === h;
              return (
                <React.Fragment key={h}>
                  <div
                    className="lp-th"
                    style={{ cursor: 'pointer', userSelect: 'none', position: 'relative' }}
                    onClick={() => setScriptSort(prev => ({ col: h, dir: prev.col === h && prev.dir === 'asc' ? 'desc' : 'asc' }))}
                  >
                    {h}
                    <span style={{ marginLeft: 4, fontSize: 9, opacity: active ? 1 : 0.25, color: active ? 'var(--violet)' : 'inherit' }}>
                      {active && scriptSort.dir === 'desc' ? '▼' : '▲'}
                    </span>
                    <div
                      style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 8, cursor: 'col-resize', zIndex: 1 }}
                      onMouseDown={e => startScriptColDrag(i, e)}
                    />
                  </div>
                  {/* Spacer header cell above the open-in-new-tab button (sits between Script Name and Client) */}
                  {i === 0 && <div />}
                </React.Fragment>
              );
            })}
          </div>

          {/* Body — no scroll container of its own any more; the wrapper above
              owns both axes so the header cannot desync from the rows. */}
          <div>
            {listLoad ? (
              <div style={{ padding: 40, textAlign: 'center', color: 'var(--tx-faint)' }}>Loading…</div>
            ) : displayScripts.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: 'var(--tx-faint)' }}>No scripts found</div>
            ) : displayScripts.map(s => (
              <div
                key={s.id}
                style={{ display: 'grid', gridTemplateColumns: listGridCols, background: selectedScripts.has(s.id) ? 'rgba(99,149,255,0.07)' : undefined }}
                className="lp-row"
                // In the bin a row must NOT open the editor: GET /api/scripts/:id
                // 404s on a deleted script, so opening it would just throw. The
                // click toggles selection instead, which is the only thing there
                // is to do here — select, then Restore.
                onClick={() => {
                  if (!deletedView) return selectScript(s.id);
                  setSelectedScripts(prev => {
                    const next = new Set(prev);
                    if (next.has(s.id)) next.delete(s.id); else next.add(s.id);
                    return next;
                  });
                }}
              >
                {canSelectRows && (
                  <div style={{ display: 'flex', alignItems: 'center' }} onClick={e => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedScripts.has(s.id)}
                      onChange={() => setSelectedScripts(prev => {
                        const next = new Set(prev);
                        if (next.has(s.id)) next.delete(s.id); else next.add(s.id);
                        return next;
                      })}
                      style={{ accentColor: 'var(--violet)', cursor: 'pointer' }}
                    />
                  </div>
                )}
                <div style={{ minWidth: 0 }} onContextMenu={e => { if (isEditableTarget(e.target)) return; e.preventDefault(); if (deletedView) return; setScriptCtxMenu({ x: e.clientX, y: e.clientY, script: s }); }}>
                  <div className="script-name">{s.scriptName}</div>
                  {/* "Who deleted this, and when" — read back from the audit log
                      by the server, since there is no deleted_at column. Sits
                      under the name rather than in a new column so the bin keeps
                      the same grid as every other view. */}
                  {deletedView && (
                    <div style={{ fontSize: 10, color: 'var(--tx-muted)', marginTop: 2 }}>
                      {s.deletedAt
                        ? `Deleted ${new Date(s.deletedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}${s.deletedBy?.name ? ` by ${s.deletedBy.name}` : ''}`
                        : 'Deleted — no audit record'}
                    </div>
                  )}
                </div>
                {/* Open this script in a new browser tab (same as the right-click menu).
                    Hidden in the bin: the target route resolves the script through
                    the same detail fetch that 404s while it is deleted. */}
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center' }}>
                  {!deletedView && <button
                    type="button"
                    title="Open this script in a new tab — leaves this list open so you can keep browsing while the script loads in a separate tab."
                    aria-label="Open script in a new tab"
                    className="open-tab-btn"
                    onClick={e => {
                      e.stopPropagation();
                      window.open(`${window.location.origin}/scripts?script=${s.id}`, '_blank', 'noopener');
                    }}
                  >
                    ↗
                  </button>}
                </div>
                <div className="lp-cell">{s.client?.name || '—'}</div>
                <div className="lp-cell">{s.writer?.name || '—'}</div>
                <div>
                  <span className={`pill ${PILL_CLASS[listStatus(s)] || ''}`}>{listStatus(s)}</span>
                </div>
                <div style={{ fontSize: 11, color: 'var(--tx-muted)', paddingTop: 3 }}>
                  {s.dateEntered
                    ? new Date(s.dateEntered).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                    : '—'}
                </div>
                <div style={{ fontSize: 11, color: 'var(--tx-muted)', paddingTop: 3 }}>
                  {s.approvedAt
                    ? new Date(s.approvedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                    : '—'}
                </div>
                {canSeeQualNotesList && (
                  <div
                    style={{ fontSize: 11, overflow: 'hidden' }}
                    onClick={canEditQualNotesList ? e => e.stopPropagation() : undefined}
                  >
                    {canEditQualNotesList ? (
                      <textarea
                        rows={1}
                        placeholder="Add note…"
                        value={noteEdits[s.id] !== undefined ? noteEdits[s.id] : (s.qualNotes || '')}
                        onFocus={e => {
                          setNoteEdits(prev => ({ ...prev, [s.id]: prev[s.id] !== undefined ? prev[s.id] : (s.qualNotes || '') }));
                          e.target.style.borderColor = 'var(--border)';
                          e.target.style.background  = 'var(--bg-card2)';
                        }}
                        onChange={e => setNoteEdits(prev => ({ ...prev, [s.id]: e.target.value }))}
                        onBlur={async e => {
                          e.target.style.borderColor = 'transparent';
                          e.target.style.background  = 'none';
                          const val = e.target.value;
                          setNoteEdits(prev => { const n = { ...prev }; delete n[s.id]; return n; });
                          if (val === (s.qualNotes || '')) return;
                          try {
                            await api.patch(`/api/scripts/${s.id}`, { qualNotes: val });
                            setScripts(prev => prev.map(sc => sc.id === s.id ? { ...sc, qualNotes: val } : sc));
                          } catch {}
                        }}
                        style={{
                          width: '100%', background: 'none', border: '1px solid transparent',
                          borderRadius: 3, padding: '1px 3px', fontSize: 11, fontFamily: 'var(--font)',
                          color: 'var(--tx-secondary)', resize: 'none', outline: 'none',
                          lineHeight: 1.4, cursor: 'text', overflow: 'hidden',
                        }}
                      />
                    ) : (
                      <span style={{ color: 'var(--tx-secondary)', paddingTop: 3, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {s.qualNotes || '—'}
                      </span>
                    )}
                  </div>
                )}
                {/* Characters — READ-ONLY tags, one per distinct character in this
                    script's body. No click-to-edit, no input, no remove control:
                    the character column belongs to the script editor, and this is
                    a view of it. TH/AI/Recipe have no character field at all, so
                    their cell is simply empty — not a dash, which would read as
                    "none found" for a format that can never have any.
                    Collapsed: one line, no wrap, scrolls sideways, so a script
                    with 20 characters does not make its row 4 lines tall.
                    Expanded (whole column): wraps and the row grows to fit. */}
                <div
                  style={{
                    display: 'flex', gap: 4, minWidth: 0, alignItems: 'flex-start',
                    ...(charsExpanded
                      ? { flexWrap: 'wrap' }
                      : {
                          flexWrap: 'nowrap',
                          overflowX: 'auto',
                          // No scrollbar: a 15px one would add height to every row
                          // in the list, and "no row-height change" is the whole
                          // point of the collapsed state. The expand toggle is the
                          // discoverable way to see what is off the end.
                          scrollbarWidth: 'none',
                          msOverflowStyle: 'none',
                        }),
                  }}
                >
                  {displayCharacters(s.characters).map(n => (
                    <span
                      key={n}
                      className="format-badge"
                      style={{ flexShrink: 0, maxWidth: '100%' }}
                      title={n}
                    >
                      {n}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>

          </div>{/* /width:max-content */}
          </div>{/* /scroll container */}
        </div>
        )}

      </div>

      {/* Mass delete confirmation modal */}
      {scriptMassDelete && (
        <div className="modal-overlay" onClick={() => setScriptMassDelete(false)}>
          <div className="vc-modal" style={{ width: 420 }} onClick={e => e.stopPropagation()}>
            <div className="vc-header">
              <div><div className="vc-title">Delete {selectedScripts.size} script{selectedScripts.size !== 1 ? 's' : ''}?</div></div>
              <button className="vc-close" onClick={() => setScriptMassDelete(false)}>✕</button>
            </div>
            <div style={{ padding: '20px 28px' }}>
              <p style={{ fontSize: 13, color: 'var(--tx-secondary)', lineHeight: 1.6, margin: 0 }}>
                Permanently delete <strong style={{ color: 'var(--tx-primary)' }}>{selectedScripts.size} script{selectedScripts.size !== 1 ? 's' : ''}</strong>? This cannot be undone.
              </p>
            </div>
            <div className="vc-footer">
              <div />
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn-ghost" onClick={() => setScriptMassDelete(false)}>Cancel</button>
                <button
                  onClick={handleMassDeleteScripts}
                  style={{ background: 'rgba(212,85,85,0.15)', color: 'var(--s-incomplete-t)', border: '1px solid rgba(212,85,85,0.35)', borderRadius: 'var(--radius-sm)', padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font)' }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Right-click context menu */}
      {scriptCtxMenu && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 299 }} onClick={() => setScriptCtxMenu(null)} />
          <div
            style={{
              position: 'fixed', top: scriptCtxMenu.y, left: scriptCtxMenu.x, zIndex: 300,
              background: 'var(--bg-card)', border: '1px solid var(--border-mid)',
              borderRadius: 8, padding: '4px 0', boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
              minWidth: 160,
            }}
            onClick={() => setScriptCtxMenu(null)}
          >
            <div
              style={{ padding: '8px 14px', fontSize: 13, color: 'var(--tx-primary)', cursor: 'pointer', whiteSpace: 'nowrap' }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(139,109,255,0.12)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              onClick={() => window.open(`${window.location.origin}/scripts?script=${scriptCtxMenu.script.id}`, '_blank')}
            >
              Open in new tab ↗
            </div>
            {canUseTemplates && (
              <div
                style={{ padding: '8px 14px', fontSize: 13, color: 'var(--tx-primary)', cursor: 'pointer', whiteSpace: 'nowrap' }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(139,109,255,0.12)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                onClick={() => setSaveTplPrompt({ scriptId: scriptCtxMenu.script.id, defaultName: scriptCtxMenu.script.scriptName || '' })}
              >
                ⎘ Save as Template
              </div>
            )}
          </div>
        </>
      )}

      {/* Save-as-Template: name prompt */}
      {saveTplPrompt && (
        <SaveTemplateModal
          defaultName={saveTplPrompt.defaultName}
          onClose={() => setSaveTplPrompt(null)}
          onSave={name => handleSaveAsTemplate(saveTplPrompt.scriptId, name)}
        />
      )}

      {/* New-from-template: confirm the target client */}
      {useTplPrompt && (
        <UseTemplateModal
          template={useTplPrompt.template}
          clients={clients}
          onClose={() => setUseTplPrompt(null)}
          onCreate={clientId => handleNewFromTemplate(useTplPrompt.template, clientId)}
        />
      )}

      {/* Delete-template confirmation */}
      {delTplConfirm && (
        <div className="modal-overlay" onClick={() => setDelTplConfirm(null)}>
          <div className="vc-modal" style={{ width: 420 }} onClick={e => e.stopPropagation()}>
            <div className="vc-header">
              <div><div className="vc-title">Delete this template?</div></div>
              <button className="vc-close" onClick={() => setDelTplConfirm(null)}>✕</button>
            </div>
            <div style={{ padding: '20px 28px' }}>
              <p style={{ fontSize: 13, color: 'var(--tx-secondary)', lineHeight: 1.6, margin: 0 }}>
                Delete <strong style={{ color: 'var(--tx-primary)' }}>{delTplConfirm.template.templateName || delTplConfirm.template.scriptName}</strong>?
                Scripts already created from it are not affected.
              </p>
            </div>
            <div className="vc-footer">
              <div />
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn-ghost" onClick={() => setDelTplConfirm(null)}>Cancel</button>
                <button
                  className="action-btn"
                  style={{ background: 'none', border: '1px solid rgba(212,85,85,0.35)', color: 'var(--s-incomplete-t)' }}
                  onClick={() => handleDeleteTemplate(delTplConfirm.template)}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reassign to another project */}
      {reassignData && (
        <ReassignModal
          data={reassignData}
          result={reassignResult}
          busy={reassignBusy}
          onClose={() => { setReassignData(null); setReassignResult(null); }}
          onPick={handleReassign}
        />
      )}

      {/* New Script modal */}
      {showNew && (
        <NewScriptModal
          clients={clients}
          members={members}
          defaultClientId={newScriptDefaults?.clientId || ''}
          onClose={() => { setShowNew(false); setNewScriptDefaults(null); }}
          onCreate={handleCreate}
        />
      )}

    </div>
  );
}

// ── Characters column: header search ──────────────────────────────
// The suggest-as-you-type character search, living in the Characters column
// header. This replaced a toolbar `SearchableSelect` dropdown, and keeps that
// control's behaviour where it mattered:
//   - suggestions come from GET /api/scripts/characters, already subsetted by the
//     current client / writer / view by the caller;
//   - matching is word-prefix, case-insensitive, never mid-word
//     (matchCharacterNames — the shared rule the server's ?characterPrefix=
//     mirrors);
//   - ↓/↑ move the highlight, Enter picks, Escape closes, Tab closes and falls
//     through to normal focus movement, and the highlighted row is scrolled into
//     view — the same keyboard contract as the script-editor cell autocomplete.
//
// What changed, deliberately: it is a text box, not a <select>. The value is free
// text applied as a PREFIX, so typing is a filter on its own and picking a
// suggestion is just a fast way to finish typing one. That is why there is no
// "All characters" row any more — an empty box IS no filter, and it is visibly
// empty, which a closed dropdown reading "All characters" never quite was.
//
// Suggestions are filtered through displayCharacters, so the search cannot offer
// a value the column refuses to render as a tag.
function CharacterHeaderSearch({ query, setQuery, options }) {
  const [open, setOpen] = useState(false);
  const [hi, setHi]     = useState(0);
  const inputRef = useRef(null);
  const listRef  = useRef(null);

  // Uncapped, like the dropdown this replaced: the panel scrolls, and silently
  // dropping the tail would hide a name that does exist in the current scope.
  const shown = useMemo(
    () => matchCharacterNames(query, displayCharacters(options)),
    [query, options]);

  useEffect(() => { setHi(0); }, [query]);

  // Keep the highlighted row in view when arrowing past the visible window.
  useEffect(() => {
    if (!open) return;
    listRef.current?.querySelector(`[data-idx="${hi}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [hi, open]);

  const pick = n => { setQuery(n); setOpen(false); inputRef.current?.blur(); };

  const onKeyDown = e => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setHi(i => Math.min(i + 1, shown.length - 1)); return; }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setHi(i => Math.max(i - 1, 0)); return; }
    // Clamped, not indexed raw: the suggestion list can be replaced under an open
    // popover when the client/writer scope refetches, leaving `hi` past the end.
    if (e.key === 'Enter')     { e.preventDefault(); if (open && shown.length) pick(shown[Math.min(hi, shown.length - 1)]); return; }
    if (e.key === 'Escape')    { e.preventDefault(); setOpen(false); return; }
    if (e.key === 'Tab')       { setOpen(false); /* fall through to normal focus move */ }
  };

  return (
    <div style={{ position: 'relative', flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 2 }}>
      <input
        ref={inputRef}
        className="filter-input"
        placeholder="Search characters…"
        title="Filter the list to scripts with a character whose name starts with this — type to see suggestions"
        value={query}
        onChange={e => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        style={{
          // `width: 0` alongside flex-grow, not just minWidth: 0 — an <input>
          // carries an intrinsic width from its `size` attribute, and without
          // this it refuses to shrink below roughly 20 characters, pushing the
          // expand toggle out of the cell entirely.
          flex: 1, width: 0, minWidth: 0,
          padding: '3px 6px', fontSize: 11, borderRadius: 4,
          // .lp-th uppercases and letter-spaces its contents, which would make
          // the typed query and the placeholder unreadable.
          textTransform: 'none', letterSpacing: 'normal', fontWeight: 400,
        }}
      />
      {query && (
        <button
          type="button"
          onClick={() => { setQuery(''); setOpen(false); }}
          title="Clear the character filter"
          aria-label="Clear the character filter"
          style={{
            flexShrink: 0, background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--tx-muted)', fontSize: 12, lineHeight: 1, padding: '0 2px',
            fontFamily: 'var(--font)',
          }}
        >
          ×
        </button>
      )}
      {open && query.trim() !== '' && (
        <>
          {/* Click-anywhere-to-close, the same popover idiom as the row menus. */}
          <div style={{ position: 'fixed', inset: 0, zIndex: 299 }} onClick={() => setOpen(false)} />
          <div
            style={{
              // Anchored RIGHT, not left: this column is the last one, so a
              // panel wider than the cell growing rightward would run past the
              // card's edge and be clipped by its `overflow: hidden`. Growing
              // leftward keeps it inside the card at any column width.
              position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 300,
              background: 'var(--bg-card)', border: '1px solid var(--border-mid)',
              borderRadius: 8, boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
              minWidth: 220, maxWidth: 320,
              textTransform: 'none', letterSpacing: 'normal', fontWeight: 400,
            }}
          >
            <div ref={listRef} role="listbox" style={{ maxHeight: 260, overflowY: 'auto', padding: '2px 0' }}>
              {shown.length === 0 ? (
                <div style={{ padding: '8px 12px', fontSize: 12, color: 'var(--tx-faint)' }}>
                  No character matches “{query.trim()}”
                </div>
              ) : shown.map((n, i) => (
                <div
                  key={n}
                  data-idx={i}
                  role="option"
                  aria-selected={n === query}
                  onMouseEnter={() => setHi(i)}
                  onMouseDown={e => e.preventDefault()}   // keep focus in the input
                  onClick={() => pick(n)}
                  style={{
                    padding: '6px 12px', fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap',
                    overflow: 'hidden', textOverflow: 'ellipsis',
                    background: i === hi ? 'rgba(139,109,255,0.12)' : 'transparent',
                    color: 'var(--tx-primary)',
                  }}
                >
                  {n}
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── Toolbar ▾ menu ────────────────────────────────────────────────
// One dropdown used by both toolbar menus (Actions ▾ and Export ▾), which were
// otherwise identical markup. `items` is a list of
//   { label, onClick, danger?, keepOpen?, dividerBefore? }
// and any item with no `onClick` is dropped — that's how a caller expresses the
// old per-button conditions (`{format && …}`, `{canDuplicate && …}`, …) without
// this component knowing anything about roles or script state.
//   danger        — red text, for Delete.
//   keepOpen      — don't close on click, so a label that flips to a confirmation
//                   ("Copied!") is actually visible.
//   dividerBefore — hairline above the item, to fence off the destructive one.
function ToolbarMenu({ label, title, items }) {
  const [open, setOpen] = useState(false);
  const shown = items.filter(it => it && typeof it.onClick === 'function');

  if (shown.length === 0) return null;

  return (
    <div style={{ position: 'relative', flexShrink: 0 }}>
      <button
        className="btn-ghost"
        style={{ flexShrink: 0 }}
        title={title}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
      >
        {label}
      </button>
      {open && (
        <>
          {/* Click-anywhere-to-close, same pattern as the list row menu. */}
          <div style={{ position: 'fixed', inset: 0, zIndex: 299 }} onClick={() => setOpen(false)} />
          <div
            role="menu"
            style={{
              position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 300,
              background: 'var(--bg-card)', border: '1px solid var(--border-mid)',
              borderRadius: 8, padding: '4px 0', boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
              minWidth: 170,
            }}
          >
            {shown.map(it => (
              <div
                key={it.key || it.label}
                role="menuitem"
                title={it.title || undefined}
                style={{
                  padding: '8px 14px', fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap',
                  color: it.danger ? 'var(--s-incomplete-t)' : 'var(--tx-primary)',
                  borderTop: it.dividerBefore ? '1px solid var(--border)' : 'none',
                  marginTop: it.dividerBefore ? 4 : 0,
                }}
                onMouseEnter={e => e.currentTarget.style.background = it.danger ? 'rgba(212,85,85,0.12)' : 'rgba(139,109,255,0.12)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                onClick={() => { if (!it.keepOpen) setOpen(false); it.onClick(); }}
              >
                {it.label}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── Save as Template modal ────────────────────────────────────────
// Name defaults to the source script's name; blank falls back to it server-side
// too, so an empty submit can't produce a nameless template.
function SaveTemplateModal({ defaultName, onClose, onSave }) {
  const [name, setName] = useState(defaultName || '');
  const [busy, setBusy] = useState(false);
  const submit = e => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    onSave(name.trim() || defaultName || '');
  };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="vc-modal" style={{ width: 460 }} onClick={e => e.stopPropagation()}>
        <div className="vc-header">
          <div><div className="vc-title">Save as Template</div></div>
          <button className="vc-close" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={submit}>
          <div style={{ padding: '20px 28px' }}>
            <label className="form-label">Template name</label>
            <input
              className="form-input"
              autoFocus
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder={defaultName}
            />
            <p style={{ fontSize: 11.5, color: 'var(--tx-faint)', lineHeight: 1.6, marginTop: 12, marginBottom: 0 }}>
              The template copies this script's format and content. This script is not changed.
              Templates never appear in script lists, counts, reports or production.
            </p>
          </div>
          <div className="vc-footer">
            <div />
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn-primary" disabled={busy}>
                {busy ? 'Saving…' : 'Save Template'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Reassign to another project modal ─────────────────────────────
// Three states in one shell:
//   result  → what the move actually did, old name → new name
//   pick    → the projects that can receive the whole selection
//   nothing → the selection has nowhere to go, and why
//
// The project list comes from the server (POST /reassign-targets) and is NOT
// re-filtered here. Only projects that allow every selected script's format,
// run the right production track, and aren't already holding all of them make
// the list — so there is no "this project can't take Mockumentary" warning to
// show, because such a project is never on screen (Owner 2026-09-08).
function ReassignModal({ data, result, busy, onClose, onPick }) {
  const { movable = [], blocked = [], targets = [] } = data || {};
  // Shown as "TPK- → HN-" on each row when the whole selection comes from one
  // project, which is the normal case and makes the rename concrete before the
  // click. A mixed-source selection has no single "from", so it shows initials only.
  const fromInitials = [...new Set(movable.map(m => m.clientInitials).filter(Boolean))];
  const one = movable.length === 1;

  const sectionNote = { fontSize: 11.5, color: 'var(--tx-faint)', lineHeight: 1.6, margin: '14px 0 0' };

  return (
    <div className="modal-overlay" onClick={busy ? undefined : onClose}>
      <div className="vc-modal" style={{ width: 540 }} onClick={e => e.stopPropagation()}>
        <div className="vc-header">
          <div>
            <div className="vc-label">{result ? 'Reassigned' : 'Reassign scripts'}</div>
            <div className="vc-title">
              {result
                ? `Moved to ${result.toProject?.name}`
                : movable.length === 0
                  ? 'Nothing to move'
                  : `Move ${movable.length} script${one ? '' : 's'} to…`}
            </div>
          </div>
          <button className="vc-close" onClick={onClose} disabled={busy}>✕</button>
        </div>

        <div style={{ padding: '18px 28px', maxHeight: '58vh', overflowY: 'auto' }}>

          {/* ── Result ── */}
          {result ? (
            <>
              {(result.reassigned || []).length === 0 ? (
                <p style={{ fontSize: 13, color: 'var(--tx-secondary)', margin: 0 }}>
                  No script was moved.
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
                  {result.reassigned.map(r => (
                    <div key={r.id} style={{ fontSize: 12, lineHeight: 1.55 }}>
                      <div style={{ color: 'var(--tx-faint)', textDecoration: 'line-through' }}>{r.oldScriptName}</div>
                      <div style={{ color: 'var(--tx-primary)', fontWeight: 600 }}>{r.scriptName}</div>
                    </div>
                  ))}
                </div>
              )}
              {result.segmentsMoved > 0 && (
                <p style={sectionNote}>
                  {result.segmentsMoved} scheduled shoot segment{result.segmentsMoved !== 1 ? 's' : ''} moved
                  across to {result.toProject?.name} on the same day{result.segmentsMoved !== 1 ? 's' : ''}, with the same talent.
                </p>
              )}
              {result.slotsCleared > 0 && (
                <p style={sectionNote}>
                  {result.slotsCleared} posting-schedule slot{result.slotsCleared !== 1 ? 's' : ''} {result.slotsCleared !== 1 ? 'were' : 'was'} cleared —
                  a slot is ordered within one project, so {result.toProject?.name} needs to schedule
                  {result.reassigned?.length !== 1 ? ' these videos' : ' this video'} itself.
                </p>
              )}
              {(result.skipped || []).length > 0 && (
                <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
                  <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--s-incomplete-t)', marginBottom: 6 }}>
                    {result.skipped.length} skipped
                  </div>
                  {result.skipped.map(s => (
                    <div key={s.id} style={{ fontSize: 11.5, color: 'var(--tx-secondary)', lineHeight: 1.55, marginBottom: 4 }}>
                      <span style={{ color: 'var(--tx-primary)' }}>{s.scriptName || s.id}</span> — {s.reason}
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <>
              {/* ── Blocked, before anything is moved ── */}
              {blocked.length > 0 && (
                <div style={{
                  border: '1px solid rgba(212,85,85,0.3)', background: 'rgba(212,85,85,0.08)',
                  borderRadius: 'var(--radius-sm)', padding: '11px 14px', marginBottom: 16,
                }}>
                  <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--s-incomplete-t)', marginBottom: 6 }}>
                    {blocked.length} of the selected script{blocked.length !== 1 ? 's' : ''} cannot be reassigned
                  </div>
                  {blocked.map(b => (
                    <div key={b.id} style={{ fontSize: 11.5, color: 'var(--tx-secondary)', lineHeight: 1.55, marginBottom: 4 }}>
                      <span style={{ color: 'var(--tx-primary)' }}>{b.scriptName || b.id}</span> — {b.reason}
                    </div>
                  ))}
                  <div style={{ fontSize: 11, color: 'var(--tx-faint)', marginTop: 7 }}>
                    Only a script that has not moved into filming can change project.
                  </div>
                </div>
              )}

              {/* ── Pick a project ── */}
              {movable.length === 0 ? (
                <p style={{ fontSize: 13, color: 'var(--tx-secondary)', lineHeight: 1.6, margin: 0 }}>
                  None of the selected scripts can be reassigned.
                </p>
              ) : targets.length === 0 ? (
                <p style={{ fontSize: 13, color: 'var(--tx-secondary)', lineHeight: 1.6, margin: 0 }}>
                  No other project is set up to receive {one
                    ? <>the <strong style={{ color: 'var(--tx-primary)' }}>{movable[0].format || 'formatless'}</strong> format</>
                    : <>all {movable.length} of these script formats</>}.
                  {!one && ' Try reassigning them one format at a time.'}
                  {' '}An Admin can add the format to a project under Admin → Projects.
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {targets.map(t => (
                    <button
                      key={t.id}
                      onClick={() => onPick(t.id)}
                      disabled={busy}
                      className="btn-ghost"
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        gap: 12, width: '100%', textAlign: 'left', padding: '11px 14px',
                        fontSize: 13, fontWeight: 600, cursor: busy ? 'wait' : 'pointer',
                      }}
                    >
                      <span>{t.name}</span>
                      <span className="format-badge" style={{ flexShrink: 0 }}>
                        {fromInitials.length === 1 ? `${fromInitials[0]}- → ${t.initials}-` : `${t.initials}-`}
                      </span>
                    </button>
                  ))}
                </div>
              )}

              {targets.length > 0 && (
                <p style={sectionNote}>
                  {one ? 'The script is' : 'Each script is'} renamed onto the receiving project's initials
                  {fromInitials.length === 1 ? <> (<strong style={{ color: 'var(--tx-secondary)' }}>{fromInitials[0]}-</strong> becomes the new project's) </> : ' '}
                  and {one ? 'its' : 'their'} video card, shoot days and casting move with {one ? 'it' : 'them'}.
                </p>
              )}
            </>
          )}
        </div>

        <div className="vc-footer">
          <div />
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className={result ? 'btn-primary' : 'btn-ghost'} onClick={onClose} disabled={busy}>
              {result ? 'Done' : 'Cancel'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── New from Template modal ───────────────────────────────────────
// Client defaults to the template's own client. Only clients that allow the
// template's format are offered — the server enforces the same rule, so
// offering the rest would just produce a 400.
function UseTemplateModal({ template, clients, onClose, onCreate }) {
  const eligible = (clients || []).filter(c =>
    !template.format || (c.allowedScriptFormats || []).includes(template.format));
  const [clientId, setClientId] = useState(() =>
    eligible.some(c => c.id === template.client?.id) ? template.client.id : (eligible[0]?.id || ''));
  const [busy, setBusy] = useState(false);
  const submit = e => {
    e.preventDefault();
    if (busy || !clientId) return;
    setBusy(true);
    onCreate(clientId);
  };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="vc-modal" style={{ width: 460 }} onClick={e => e.stopPropagation()}>
        <div className="vc-header">
          <div><div className="vc-title">New script from “{template.templateName || template.scriptName}”</div></div>
          <button className="vc-close" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={submit}>
          <div style={{ padding: '20px 28px' }}>
            <label className="form-label">Project</label>
            <select className="form-input" value={clientId} onChange={e => setClientId(e.target.value)}>
              {eligible.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            {eligible.length === 0 && (
              <p style={{ fontSize: 12, color: 'var(--s-incomplete-t)', marginTop: 10, marginBottom: 0 }}>
                No client accepts this template's {template.format} format.
              </p>
            )}
            <p style={{ fontSize: 11.5, color: 'var(--tx-faint)', lineHeight: 1.6, marginTop: 12, marginBottom: 0 }}>
              Creates a new I/P script in {template.format || 'the template’s'} format, with you as the writer.
            </p>
          </div>
          <div className="vc-footer">
            <div />
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn-primary" disabled={busy || !clientId}>
                {busy ? 'Creating…' : 'Create Script'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Find & Replace modal (spec 6.1) ──────────────────────────────
// Searches across all cells of the current draftBody. Replace + Replace All
// commit through onApply, which pushes a single undo entry per action.
function FindReplaceModal({ draftBody, cols, onApply, onFocusMatch, onClose }) {
  const [find,     setFind]     = useState('');
  const [replace,  setReplace]  = useState('');
  const [matchIdx, setMatchIdx] = useState(0);

  // Build the flat list of matches every time find or body changes.
  // Each match: { rowIdx, colKey, start, end }
  const matches = useMemo(() => {
    if (!find || !draftBody?.scenes) return [];
    const needle = find.toLowerCase();
    const out = [];
    draftBody.scenes.forEach((row, rowIdx) => {
      cols.forEach(c => {
        const v = row[c.key];
        if (typeof v !== 'string' || !v) return;
        const hay = v.toLowerCase();
        let from = 0;
        while (from <= hay.length) {
          const i = hay.indexOf(needle, from);
          if (i === -1) break;
          out.push({ rowIdx, colKey: c.key, start: i, end: i + needle.length });
          from = i + Math.max(1, needle.length);
        }
      });
    });
    return out;
  }, [find, draftBody, cols]);

  // Keep matchIdx in range as matches shrink/grow.
  useEffect(() => { if (matchIdx >= matches.length) setMatchIdx(0); }, [matches.length, matchIdx]);

  // Focus + select the current match — only when the user explicitly
  // navigates (Prev/Next/Replace). Without this gate, every keystroke in the
  // Find input would steal focus from the input back to the matched cell.
  // Delegates to the parent's focusFindMatch which brings the cell into edit
  // mode (so the matched text range can actually be selected in a textarea).
  const userNavRef = useRef(false);
  useEffect(() => {
    if (!userNavRef.current) return;
    userNavRef.current = false;
    if (matches.length === 0) return;
    const m = matches[matchIdx];
    if (!m) return;
    onFocusMatch?.(m.rowIdx, m.colKey, m.start, m.end);
  }, [matchIdx, matches, onFocusMatch]);

  function navigate(delta) {
    if (matches.length === 0) return;
    userNavRef.current = true;
    setMatchIdx(i => (i + delta + matches.length) % matches.length);
  }

  function applyReplaceAt(body, m, replacement) {
    const scenes = [...body.scenes];
    const row    = { ...scenes[m.rowIdx] };
    const v      = String(row[m.colKey] || '');
    row[m.colKey] = v.slice(0, m.start) + replacement + v.slice(m.end);
    scenes[m.rowIdx] = row;
    return { ...body, scenes };
  }

  function handleReplace() {
    if (matches.length === 0) return;
    const m = matches[matchIdx];
    const next = applyReplaceAt(draftBody, m, replace);
    // After the body re-renders, focus the new "current" match (matchIdx is
    // unchanged but now points at what used to be matches[matchIdx + 1]).
    userNavRef.current = true;
    onApply(next);
  }

  function handleReplaceAll() {
    if (matches.length === 0) return;
    // Walk matches back-to-front so earlier offsets stay valid.
    let next = draftBody;
    for (let i = matches.length - 1; i >= 0; i--) {
      next = applyReplaceAt(next, matches[i], replace);
    }
    onApply(next);
  }

  function handleKey(e) {
    if (e.key === 'Escape') { e.preventDefault(); onClose(); }
    // Enter on an input → advance to next match without stealing focus from
    // the input. Lets the user keep typing/replacing without re-clicking.
    if (e.key === 'Enter' && !e.shiftKey && document.activeElement?.tagName === 'INPUT') {
      e.preventDefault();
      setMatchIdx(i => (matches.length === 0 ? 0 : (i + 1) % matches.length));
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose} onKeyDown={handleKey}>
      <div
        className="modal"
        onClick={e => e.stopPropagation()}
        style={{ width: 420, maxWidth: '95vw' }}
      >
        <div className="modal-header">
          <div className="modal-title">Find &amp; Replace</div>
          <button className="vc-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <label className="form-label">Find</label>
          <input
            className="form-input"
            autoFocus
            value={find}
            onChange={e => { setFind(e.target.value); setMatchIdx(0); }}
            placeholder="Text to search for…"
          />
          <label className="form-label" style={{ marginTop: 12 }}>Replace with</label>
          <input
            className="form-input"
            value={replace}
            onChange={e => setReplace(e.target.value)}
            placeholder="Replacement text…"
          />
          <div style={{ marginTop: 14, fontSize: 12, color: 'var(--tx-muted)', display: 'flex', alignItems: 'center', gap: 10 }}>
            {find === '' ? (
              <span>Type to begin searching</span>
            ) : matches.length === 0 ? (
              <span style={{ color: 'var(--s-incomplete-t)' }}>No matches</span>
            ) : (
              <>
                <span>{matchIdx + 1} of {matches.length} matches</span>
                <button
                  className="btn-ghost"
                  style={{ fontSize: 11, padding: '3px 9px' }}
                  onClick={() => navigate(-1)}
                >‹ Prev</button>
                <button
                  className="btn-ghost"
                  style={{ fontSize: 11, padding: '3px 9px' }}
                  onClick={() => navigate(+1)}
                >Next ›</button>
              </>
            )}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn-ghost" onClick={onClose}>Close</button>
          <button
            className="btn-ghost"
            disabled={matches.length === 0}
            onClick={handleReplace}
          >Replace</button>
          <button
            className="btn-primary"
            disabled={matches.length === 0}
            data-fr-btn="all"
            onClick={handleReplaceAll}
          >Replace All</button>
        </div>
      </div>
    </div>
  );
}

// ── Color Swatch Picker ──────────────────────────────────────────

function ColorSwatch({ onSelect }) {
  return (
    <div style={{
      background: 'var(--bg-card)',
      border: '1px solid var(--border)',
      borderRadius: 'var(--radius-sm)',
      padding: '6px 8px',
      display: 'flex',
      flexWrap: 'wrap',
      gap: 4,
      width: 106,
      boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
    }}>
      {ROW_COLORS.map((c, i) => (
        <button
          key={i}
          title={c.label}
          onClick={e => { e.stopPropagation(); onSelect(c.value); }}
          style={{
            width: 18, height: 18, borderRadius: 3, padding: 0, cursor: 'pointer',
            border: c.value ? '1px solid rgba(255,255,255,0.1)' : '1px dashed rgba(140,140,160,0.5)',
            background: c.value || 'transparent',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 9, color: 'var(--tx-faint)',
          }}
        >
          {!c.value && '✕'}
        </button>
      ))}
    </div>
  );
}

// ── New Script Modal ─────────────────────────────────────────────

function NewScriptModal({ clients, members, onClose, onCreate, defaultClientId = '' }) {
  const { user } = useAuth();
  const [clientId,   setClientId]   = useState(defaultClientId);
  const [title,      setTitle]      = useState('');
  const [format,     setFormat]     = useState('');
  const [rowCount,   setRowCount]   = useState(10);
  const [writerId,   setWriterId]   = useState('');
  const [csvScenes,  setCsvScenes]  = useState(null); // parsed rows from CSV upload
  const [csvName,    setCsvName]    = useState('');
  const [submitting, setSubmitting] = useState(false);

  const isAdmin = user?.roles?.includes('Admin') || user?.roles?.includes('Power User');
  const writerMembers = (members || []).filter(m =>
    m.isActive !== false && m.roles?.some(r => r.role === 'Writer' || r.role === 'Power User')
  );

  const selectedClient = clients.find(c => c.id === clientId);
  const formats = (selectedClient?.allowedScriptFormats || []).filter(f => f !== 'None');

  const handleCsvFile = (e) => {
    const file = e.target.files[0];
    if (!file || !format) return;
    e.target.value = '';
    const reader = new FileReader();
    reader.onload = (ev) => {
      const scenes = csvToScenes(ev.target.result, format);
      if (scenes.length === 0) {
        alert('No valid rows found. Make sure the CSV headers match the downloaded template.');
        return;
      }
      setCsvScenes(scenes);
      setCsvName(file.name);
    };
    reader.readAsText(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    await onCreate(clientId, title, format || null, format ? Math.max(1, Math.min(400, rowCount)) : 0, csvScenes || undefined, writerId || undefined);
    setSubmitting(false);
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box">
        <div className="modal-title">New Script</div>
        <div className="modal-sub">Script will be created as I/P and assigned to you</div>

        <form onSubmit={handleSubmit}>
          <div className="form-field">
            <label className="form-label">Project</label>
            <select
              className="form-input"
              value={clientId}
              onChange={e => { setClientId(e.target.value); setFormat(''); setCsvScenes(null); setCsvName(''); }}
              required
            >
              <option value="">Select a project…</option>
              {clients
                .filter(c => (c.allowedScriptFormats || []).some(f => f !== 'None'))
                .map(c => <option key={c.id} value={c.id}>{c.name}</option>)
              }
            </select>
          </div>

          <div className="form-field">
            <label className="form-label">Title</label>
            <input
              className="form-input"
              type="text"
              placeholder="e.g. Reach Around"
              value={title}
              onChange={e => setTitle(e.target.value)}
              required
              autoFocus
            />
          </div>

          {formats.length > 0 && (
            <div className="form-field">
              <label className="form-label">Format</label>
              <select className="form-input" value={format} onChange={e => { setFormat(e.target.value); setCsvScenes(null); setCsvName(''); }}>
                <option value="">Select format…</option>
                {formats.map(f => (
                  <option key={f} value={f}>{f === 'TH' ? 'Talking Head' : f}</option>
                ))}
              </select>
            </div>
          )}

          {isAdmin && writerMembers.length > 0 && (
            <div className="form-field">
              <label className="form-label">Assign Writer</label>
              <select className="form-input" value={writerId} onChange={e => setWriterId(e.target.value)}>
                <option value="">Assign to me</option>
                {writerMembers.map(m => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
            </div>
          )}

          {format && (
            <>
              {!csvScenes ? (
                <div className="form-field">
                  <label className="form-label">Starting rows</label>
                  <input
                    className="form-input"
                    type="number"
                    min={1}
                    max={400}
                    value={rowCount}
                    onChange={e => setRowCount(Math.max(1, Math.min(400, parseInt(e.target.value) || 1)))}
                    style={{ width: 100 }}
                  />
                  <div style={{ fontSize: 11, color: 'var(--tx-faint)', marginTop: 4 }}>1 – 400 blank rows</div>
                </div>
              ) : (
                <div className="form-field">
                  <div style={{ fontSize: 12, color: 'var(--tx-secondary)' }}>
                    <span style={{ color: 'var(--violet)' }}>✓</span> {csvScenes.length} rows from <em>{csvName}</em>
                    <button type="button" onClick={() => { setCsvScenes(null); setCsvName(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--tx-faint)', fontSize: 11, marginLeft: 8, fontFamily: 'var(--font)' }}>× clear</button>
                  </div>
                </div>
              )}
              <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
                <label style={{ fontSize: 11, color: 'var(--violet)', cursor: 'pointer', userSelect: 'none' }}>
                  <input type="file" accept=".csv" style={{ display: 'none' }} onChange={handleCsvFile} />
                  ↑ Import from CSV
                </label>
                <button type="button" onClick={() => downloadTemplate(format)} style={{ background: 'none', border: 'none', fontSize: 11, color: 'var(--tx-secondary)', cursor: 'pointer', padding: 0, fontFamily: 'var(--font)' }}>
                  ↓ Download template
                </button>
              </div>
            </>
          )}

          <div className="modal-footer">
            <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary" data-testid="new-script-create" disabled={submitting || !clientId || !title || !format} title={!format && clientId ? 'Pick a format first' : undefined}>
              {submitting ? 'Creating…' : 'Create Script'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
